// @ts-strict-ignore
/* eslint-disable @typescript-eslint/indent */
import { Subject, Subscription } from 'rxjs';
import { CallDirection, CallEvents, CallRB, CallRedirectionCause, CallStatus, CallType, CalledParty } from 'rainbow-web-sdk/models/call.model';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { Bubble, BubbleRB } from 'rainbow-web-sdk/models/bubble.model';
import { CliOption } from 'rainbow-web-sdk/models/cli.model';
import anonymizer from 'rainbow-web-sdk/utils/anonymizer';

import { Service } from 'rainbow-web-sdk/services/service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { TelephonyServiceSipWise } from "rainbow-web-sdk/services/telephony/telephonySipWise.service";
import { ITelephonyEventHandler, TelephonyHybridEventHandler } from "rainbow-web-sdk/services/telephony/telephonyHybridEventHandler";
import { TelephonySipWiseEventHandler } from "rainbow-web-sdk/services/telephony/telephonySipWiseEventHandler";
import type { BubbleConferenceServiceRB } from 'rainbow-web-sdk/services/conference/bubbleConference.service';
import { BUBBLE_CONFERENCE_SVC } from 'rainbow-web-sdk/services/conference/bubbleConference.service';
import type { BubbleServiceRB } from 'rainbow-web-sdk/services/bubbles/bubble.service';
import { BUBBLE_SVC } from 'rainbow-web-sdk/services/bubbles/bubble.service';
import type { WebrtcGatewayService } from 'rainbow-web-sdk/services/webrtcGateway/webrtcGateway.service';
import { WEBRTC_GATEWAY_SVC } from 'rainbow-web-sdk/services/webrtcGateway/webrtcGateway.service';
import type { WebrtcP2PService } from "rainbow-web-sdk/services/webrtc/webrtcP2P.service";
import { WEBRTC_P2P_SVC } from "rainbow-web-sdk/services/webrtc/webrtcP2P.service";
import { CallInfo } from './supervision.service';

import { SupervisionService } from "rainbow-web-sdk/services/telephony/supervision.service";
import { $iq, $msg, $pres } from 'strophe.js';
import { TelephonyCCDAgentService } from './telephonyCcdAgent.service';

export const TELEPHONY_SVC = 'TelephonyService';

export interface IOxeGroup {
    huntingGroupNumber: string; // hunting group number of the oxe user
    huntingGroupName: string; // hunting group name of the oxe user (may be empty)
    logged: boolean; // logged state of the user in the hunting group: true for "logged in" state / false for "logged out" state
}

/**
 * Collection of call distribution group (hunt group) types.
 */
export enum HuntGroupType {

    /**
     * Hunt group.
     */
    HUNTING_GROUP = "hunting_group",

    /**
     * Hunt group with waiting queue.
     */
    CALL_QUEUE = "call_queue",

    /**
     * Manager/Assistant group.
     *
     * @deprecated See 'manager_assistant' subtype
     */
    MANAGER_ASSISTANT = "manager_assistant"

}

/**
 * Collection of call distribution group (hunt group) sub-types.
 */
export enum HuntGroupSubType {

    /**
     * Regular hunt group.
     *
     * @remarks Voice attendant users cannot be part of a 'default' group.
     */
    DEFAULT = "default",

    /**
     * Attendant hunt group.
     *
     * @remarks Only Voice attendant users can be part of a 'hg_attendant' group.
     */
    ATTENDANT = "hg_attendant",

    /**
     * Manager/Assistant group.
     */
    MANAGER_ASSISTANT = "manager_assistant"
}

/**
 * Collection of call queue distribution policies.
 */
export enum HuntGroupPolicy {

    /**
     * Parallel hunting.
     *
     * @remarks only 'parallel' policy for 'hg_attendant' hunting group.
     */
    PARALLEL = "parallel",

    /**
     * Serial hunting.
     *
     * @remarks only 'serial' for 'manager_assistant' group.
     */
    SERIAL = "serial",

    /**
     * Circular hunting (ie. the calls are distributed "round-robin").
     */
    CIRCULAR = "circular"
}

export interface HuntGroupMember {
    connected: string;
    displayName: string;
    memberId: string;
    roles: string[];
    status: 'active' | 'idle';
}

export interface HuntGroup {
    availableMembers: number;
    canBeJoined: boolean;
    connectedMembers: number;
    id: string;
    members: HuntGroupMember[];
    name: string;
    policy: HuntGroupPolicy;
    roomId: string;
    subType: HuntGroupSubType;
    totalAgents: number;
    totalMembers: number;
    type: HuntGroupType;
}

export class TelephonyService extends Service {

    public calls: Record<string, CallRB> = {};
    public pendingMakeCalls: any[] = [];
    public agentStatus: any = {};

    public starting: boolean = false;
    public started: boolean = false;

    public isOxo: boolean = false;
    public isOxe: boolean = false;
    public isThirdParty: boolean = false;

    public forwardObject: any = {};
    public nomadicObject: any = {};

    public voiceMailFeatureEnabled: boolean;
    public voicemailNumber: string;

    /*********************************************************/
    /**                 LIFECYCLE STUFF                      */
    /*********************************************************/
    private listeners: any = [];
    private subscriptions: Subscription[] = [];
    private CALLSERVICE_NS: string = "urn:xmpp:pbxagent:callservice:1";

    private sipWise: boolean = false;

    public rxSubject: Subject<any> = new Subject<any>();
    private mainService: MainService = MainService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private webrtcP2PService?: WebrtcP2PService = undefined;
    public telephonyServiceSipWise?: TelephonyServiceSipWise = undefined;
    public supervisionService?: SupervisionService = undefined;
    public ccdAgentService?: TelephonyCCDAgentService = undefined;

    private pbxId: string;
    public makingCall: boolean = false;
    private mediaPillarConfigured: boolean = false;
    private mediaPillarRemoteExtension: string = "";
    private mediaPillarAlive: boolean = false;
    private nomadicAnswerNotTakedIntoAccount: boolean = false;
    public sipWiseUserGroupsData: any = {};

    private oxeUserGroupData: IOxeGroup;

    public isBasicCallAllowed: boolean;
    private isSecondCallAllowed: boolean;
    private isTransferAllowed: boolean;
    private isConferenceAllowed: boolean;
    private isDeflectCallAllowed: boolean;
    private isForwardEnabled: boolean;
    private isNomadicEnabled: boolean;
    private isAdhocConferenceAllowed: boolean;
    private isMixPstnWebRtcAllowed: boolean;
    private isOXEDeskphoneNonRinging: boolean;
    private isOxeSecretIdentity: boolean = false;//RQRAINB-9582
    private isOxeDoNotDisturb: boolean = false;
    public isOXEDynamicStateReceived: boolean;
    private isCloudPbxRejectCall: boolean; //RQRAINB-12845

    private activeConversation: any;

    private userJidTel: any;
    private portalURL: string;

    public telephonyEventHandler: ITelephonyEventHandler;
    private telAPIPromiseInProgress: [] = [];//CR27077

    // CLI Stuff
    private cliOptions: CliOption[] = [];
    private currentCli: CliOption = null;

    private offlineCallManagementTimer: any = null;

    public autoOffHookConference: any = null;
    public webConferenceInvitDeskphoneRoom: any = null;

    public internalTestCallIndex: number = 1;

    public remoteControlledPhoneCallIds: Set<string> = new Set<string>();

    private autoAnswerCorrelatorIdResourceMap: any = {};
    private autoAnswerTimeout: any = {};

    public static getInstance() {
        return Service.create(TELEPHONY_SVC, TelephonyService);
    }

    public static build() {
        return new TelephonyService();
    }

    private constructor() { super(); }

    public publish(event: RBEvent): void { this.rxSubject.next(event); }
    public subscribe(callback: any): Subscription { return this.rxSubject.subscribe(callback); }

    /**
     * Start service method
     * @public
     * @async
     * @instance
     * @function
     */
    public async start(): Promise<void> {
        this.logger.info("[telephonyService] [TEL] === STARTING ===");

        this.telephonyServiceSipWise = TelephonyServiceSipWise.getInstance();
        this.webrtcP2PService = this.mainService.inject<WebrtcP2PService>(WEBRTC_P2P_SVC);
        this.supervisionService = SupervisionService.getInstance();
        this.ccdAgentService = TelephonyCCDAgentService.getInstance();

        //sipWise++
        this.setSipWise(this.isSipWiseRoot());//sipWise only if RVCP Subscriber
        if (this.isSipWise()) {
            this.logger.info("[telephonyService] starting in sipWise mode");
            await this.telephonyServiceSipWise.start();
        }
        //sipWise--

        /**
         * service.calls
         * Used by SDK (public)
         * Warning when modifying this property
         */
        this.calls = {};
        this.pendingMakeCalls = [];
        this.starting = this.started = false;
        this.agentStatus = { phoneApi: "disconnected", xmppAgent: "stopped", agentVersion: "unknown", featureVVM: false, featureOXOMULTICALL: false };
        this.voicemailNumber = this.contactService.userContact.voicemailNumber;
        this.pbxId = this.contactService.userContact.pbxId;
        this.makingCall = false;
        this.isOXEDynamicStateReceived = false;
        this.isOXEDeskphoneNonRinging = false;
        this.isOxeSecretIdentity = false;//RQRAINB-9582
        this.isOxeDoNotDisturb = false;
        this.telAPIPromiseInProgress = [];//CR27077
        //this.voiceMail = VoiceMail.create();
        this.forwardObject = { forwardType: "Deactivation", forwardTo: undefined, pbxForwardType: "no_forward" };
        this.nomadicObject = {};
        this.nomadicAnswerNotTakedIntoAccount = false;
        this.sipWiseUserGroupsData = {
            //Former mng-assist way Deprecated++
            myMemberId: "",
            managerAssistGrId: 0, //this way as long as (1:1 man/assist) only taken into account
            managerId: 0, //this way as long as (1:1 man/assist) only taken into account
            managerName: "", //this way as long as (1:1 man/assist) only taken into account
            managerStatus: "", //this way as long as (1:1 man/assist) only taken into account
            managerForward: "unknow", //in {unknow,unactivated,activated} this way as long as (1:1 man/assist) only taken into account
            assistantId: 0, //this way as long as (1:1 man/assist) only taken into account
            assistantName: "", //this way as long as (1:1 man/assist) only taken into account
            assistantStatus: "", //this way as long as (1:1 man/assist) only taken into account
            //Former mng-assist way Deprecated--
            //RQRAINB-7479 new mng-assist way : based on hunting group mechanism
            isSomeMngAssistAsHG: false, //true if at least 1 mng-assist group is as HG (type:hunting_group/subType:manager_assistant)
            huntingGroupNb: 0,
            managerAssistantNb: 0,
            managerAssistantMyAssistList: [], //for all mng-assist GRs, list of all my assistant Id list (if not empty => I'm manager)
            managerAssistantMyMngList: [], //for all mng-assist GRs, list of all my manager Id list (if not empty => I'm assistant)
            sipWiseUserGroupsList: [], //the root global group structure from the server API getUserGroups()
            initialized: false // indicates wether the group data have been initialized from server data
        };//RGRAINB-4519

        this.isBasicCallAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_BASIC_CALL);
        this.isSecondCallAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_SECOND_CALL);
        this.isTransferAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_TRANSFER_CALL);
        this.isConferenceAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_CONFERENCE_CALL);
        this.isDeflectCallAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_DEFLECT_CALL);
        this.voiceMailFeatureEnabled = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_VOICE_MAIL);
        this.isForwardEnabled = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_CALL_FORWARD);
        this.isNomadicEnabled = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_NOMADIC);
        this.isAdhocConferenceAllowed = this.contactService.userContact.webrtcAudioEnabled && (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_CONFERENCE_ALLOWED) || this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_ADD_PARTICIPANT))
            && (this.contactService.userContact.roomsEnabled || this.contactService.userContact.roomsAsVoiceEnabled);//RQRAINB-5427// Store the user jid tel
        this.isMixPstnWebRtcAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS) || this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS_FOR_PBX) || config.mixwebrtcpstn || config.mixwebrtcpstnhybride;
        this.isCloudPbxRejectCall = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.CLOUD_PBX_REJECT_CALL); //RQRAINB-12845
        this.userJidTel = this.authService.jidTel;
        this.portalURL = `${config.restServerUrl}/api/rainbow/telephony/v1.0/`;

        // Register on user telephony presence
        this.subscriptions.push(this.xmppService.subscribeToContactEvents((event) => {
            if (event.name === 'ON_CONTACT_PRESENCE_EVENT') { this.onTelPresenceChange(event.data); }
        }));

        this.subscriptions.push(this.xmppService.subscribeToConnectionEvents((event: RBEvent) => {
            try {
                if (event.name === 'ON_XMPP_DISCONNECTED') {
                    //we've lost the XMPP connection
                    //manage offline mode
                    this.starting = this.started = false;

                    this.logger.info("[telephonyService] [TEL] XMPP disconnected: arm timer offlineCallManagementTimer");
                    this.offlineCallManagementTimer = setTimeout(() => {
                        this.telephonyOfflineCallManagement(true); // true means xmpp offline
                        this.offlineCallManagementTimer = null;
                    }, 3000);

                    this.eventService.publish("ON_TELEPHONY_STATUS_CHANGED_EVENT", "stopped");
                }
            }
            catch (_err) {
                //do nothing
            }
        }));

        // Create the telephonyEventHandler
        this.telephonyEventHandler = this.isSipWise()
            ? TelephonySipWiseEventHandler.create(this)
            : TelephonyHybridEventHandler.create(this);

        this.logger.info("[telephonyService] starting on user jidTel: " + this.userJidTel);
        if (this.contactService.userContact) {
            this.logger.info("[telephonyService] [TEL] starting user name: " + this.contactService.userContact.nameForLogs + ", full jid: " + this.contactService.userContact.fullJid);
            this.logger.info("[telephonyService] [TEL] starting phonePbx: " + anonymizer(this.contactService.userContact.phonePbx) + ", phonePro: " + anonymizer(this.contactService.userContact.phonePro)
                + ", voicemailNumber: " + anonymizer(this.contactService.userContact.voicemailNumber) + ", pbxId: " + this.contactService.userContact.pbxId);
        }

        //start with existing tel presence or wait for it if it has not yet been received
        if (this.contactService.userContact) {
            if (this.contactService.userContact.telStatus) {
                //tel status of our user has already been received => start telephony service
                this.logger.info("[telephonyService] start with my existing telephony presence " + this.contactService.userContact.telStatus + " of user contact (" + this.contactService.userContact.jidtel + ") ");
                this.onTelPresenceChange({ jid: this.userJidTel + "/phone", status: this.contactService.userContact.telStatus });
            } else {
                this.logger.info("[telephonyService] Subscribe and wait for my telephony presence (" + this.contactService.userContact.jidtel + ")");
                this.xmppService.send($pres({ to: this.contactService.userContact.jidtel, type: "subscribe" }));
            }
        }

        this.listeners.push(this.eventService.subscribe("ON_ACTIVE_CONVERSATION_CHANGE", this.onActiveConversationChange.bind(this)));

        //on call update, update the capabilities for my contact & my calls
        this.listeners.push(this.eventService.subscribe("ON_CALL_UPDATED_EVENT", this.onCallUpdated.bind(this)));
        this.listeners.push(this.eventService.subscribe("PUT_ACTIVE_TELEPHONY_CALL_ON_HOLD", (__event, data) => {
            this.putActiveTelephonyCallOnHold(data?.conferenceSession);
        }));
        this.listeners.push(this.eventService.subscribe("ON_WEB_CONFERENCE_RELEASED", (__event, data) => {
            this.onWebConferenceReleased(data?.roomDbId);
        }));
        this.listeners.push(this.eventService.subscribe("ON_WEBRTCGATEWAY_CONFIG_CHANGE", this.onMediaPillarConfigChanged.bind(this)));
        this.listeners.push(this.eventService.subscribe("ON_WEBRTCGATEWAY_STATE_CHG", this.onMediaPillarStateChanged.bind(this)));
        this.listeners.push(this.eventService.subscribe("TELEPHONY_MAKE_SIMPLE_CALL_TO_NUMBER", async (__event: any, number: string, room: any) => {
            this.makeCall(null, number, undefined, undefined, undefined, undefined, room);
        }));
        this.listeners.push(this.eventService.subscribe("TELEPHONY_AUTOANSWER_CONFERENCECALL", async (__event: any, number: string, room: any) => {
            this.setAutoOffHookConference(number, room);
        }));
        this.listeners.push(this.eventService.subscribe("ON_WEBCONFERENCE_STARTED_DESKPHONE_INVITATION", async (__event: any, roomJid: string, roomName: string) => {
            // A conferenceAdd event has been received in deskphone mode (event contains <deskphone>true</deskphone>)
            this.setWebConferenceInvitDeskphone(roomJid, roomName);
        }));
        this.listeners.push(this.eventService.subscribe('ON_SIPWISE_HUNTING_GROUP_UPDATE_EVENT', () => { this.onUpdateHuntingGroupsData(); }));
    }

    /**
     * Attach the handlers : we request the event handler to start its (xmpp) handler(s)
     * @private
     * @instance
     * @function
     */
    private attachHandlers(): void {
        this.logger.info("[telephonyService] attachHandlers");

        // Attach Xmpp handler(s) in telephony event handler
        this.telephonyEventHandler.attachHandlers();
    }

    /**
     * Stop service method
     * @public
     * @async
     * @instance
     * @function
     */
    public async stop(): Promise<void> {
        this.logger.info("");
        this.logger.info("[telephonyService] [TEL] === STOPPING ===");
        this.starting = this.started = false;
        this.userJidTel = null;
        this.calls = null;
        this.telAPIPromiseInProgress = [];//CR27077

        if (this.telephonyEventHandler) {
            this.telephonyEventHandler.detachHandlers();
            this.telephonyEventHandler = null;
        }

        // Remove listeners
        let listener;
        while ((listener = this.listeners.pop())) { listener(); }
        if (this.subscriptions) { this.subscriptions.forEach((subscription) => { subscription.unsubscribe(); }); }

        this.logger.info("[telephonyService] [TEL] === STOPPED ===");
        this.eventService.publish("ON_TELEPHONY_STATUS_CHANGED_EVENT", "stopped");
    }

    /**
     * Handling of the telephony presence event
     * @private
     * @param {any} attr reference to the telephony service SipWise
     * @async
     * @returns {Promise<boolean>} Promise resolved with true value because it is a xmpp event handler in order to continue next events handling
     * @instance
     * @function
     */
    private async onTelPresenceChange(attr: any, attemp: number = 0): Promise<boolean> {
        if (this.contactService.isTelJid(attr.jid)) {
            //sipWise++
            const ressourceFromJid = this.contactService.getResourceFromJid(attr.jid);
            if (ressourceFromJid !== "phone" && ressourceFromJid !== "pcg2") { return true; }
            //sipWise--
            const jidIm: string = this.contactService.getImJid(attr.jid);
            if (!jidIm) { return true; }

            const status = attr.status;

            if (this.contactService.isUserContactJid(jidIm)) {
                this.telAPIPromiseInProgress = [];//CR27077

                // Receive unavailable status
                if (status === "unavailable" || status === "offline") {
                    this.logger.info("[telephonyService] [TEL] received my telephony presence -- " + status);
                    this.starting = this.started = false;
                    this.telephonyOfflineCallManagement();
                    this.eventService.publish("ON_TELEPHONY_STATUS_CHANGED_EVENT", "stopped");
                    this.logger.info("[telephonyService] [TEL] === STOPPED ===");
                }

                // Service is not started, try to fetch agent status
                else if (!this.started && !this.starting) {
                    this.logger.info("[telephonyService] [TEL] received my telephony presence -- " + status);
                    this.starting = true;
                    try {
                        await this.getAgentStatus();
                        this.attachHandlers();
                        //sipWise++
                        if (this.isSipWise()) {
                            this.sipWiseUserGroupsDataClear();
                            this.telephonyServiceSipWise.emergencyCallDataClear();//RQRAINB-3835
                            this.telephonyServiceSipWise.routingDataClear();//RQ4863
                            //with sipwise no nomadic mng
                            await this.getTelephonyConfig();
                        } else if (this.isNomadicEnabled) {
                            // We must first retrieve nomadic config to know the current device (main or secondary), and make the snapshot only on the current device
                            await this.getNomadicStatus();
                        }

                        // initialize forward
                        if (this.isForwardEnabled) {
                            this.getForwardStatus();
                        }

                        this.logger.info("[telephonyService] [TEL] === STARTED ===");
                        this.started = true;
                        this.starting = false;
                        this.eventService.publish("ON_TELEPHONY_STATUS_CHANGED_EVENT", "started");

                        // Do snapshot calls
                        // Note: this is done after telephony is set to started , because maincontroller ignores incoming calls if telephony is not started
                        const snapshotSecondary = this.nomadicObject.featureActivated && this.nomadicObject.modeActivated && !this.nomadicObject.makeCallInitiatorIsMain;
                        await this.snapshotCalls(snapshotSecondary);

                        this.logger.info("[telephonyService] end of initialization");

                        //send update of supervision in case new calls are added
                        if (this.supervisionService) {
                            this.supervisionService.sendEvent(SupervisionService.ON_SUPERVISION_CONVERSATIONS_UPDATED, { mySupervisedCalls: this.supervisionService.mySupervisedCalls });
                        }

                    } catch (error) {
                        this.starting = false;
                        //try to reinit again in random number between 10 - 30 sec for the first attempt;
                        //after that put random between 30 - 80 sec
                        const randomNumber = (!attemp) ? Math.floor(Math.random() * 21) + 10 : Math.floor(Math.random() * 51) + 30;

                        this.logger.error(`[telephonyService] receive telephony presence but error in initialization - ${error?.message} -- retry again in ${randomNumber}sec`);

                        setTimeout(() => {
                            this.logger.debug(`[telephonyService] -- try to reinit after ${randomNumber}sec timeout`);
                            //send attempt number so that we can increase the timer
                            this.launchTelephonyInitilization(1);
                        }, randomNumber * 1000);
                    }
                }
            }
        }
        return true;
    }

    /**
     * Laucnh telephony initilization with server
     * @private
     * @instance
     * @function
     */
    public launchTelephonyInitilization(attemp: number = 0): void {
        if (this.contactService.userContact?.telStatus) {
            this.logger.info("[telephonyService] launchTelephonyInitilization telStatus: " + this.contactService.userContact.telStatus + " => start");
            this.onTelPresenceChange({ jid: this.userJidTel + "/phone", status: this.contactService.userContact.telStatus }, attemp);
        } else {
            this.logger.info("[telephonyService] launchTelephonyInitilization no tel presence => do nothing");
        }
    }

    /**
     * Handling of the xmpp reconnection event
     * @public
     * @instance
     * @function
     */
    public reconnect(): void {
        // When we reconnect, we only attach the XMPP handlers
        // We dont touch the telephony status, as the "telephony stop" should be done when we lose network (and not on the reconnect)
        this.logger.info("[telephonyService] [TEL] XMPP reconnection");

        if (this.offlineCallManagementTimer) {
            // Xmpp connection has been reestablished, but the offline Call Management armed by xmpp disconnection
            // has not yet been executed: cancel it,
            // so that we keep calls on xmpp quick disconnection/reconnection (less than 3 seconds)
            this.logger.info("[telephonyService] [TEL] cancel timer offlineCallManagementTimer");
            clearTimeout(this.offlineCallManagementTimer);
            this.offlineCallManagementTimer = null;
        }
        this.attachHandlers();
    }

    /**
     * Handling of the telephony offline event
     * @private
     * @instance
     * @function
     * @param {boolean} xmppOffline True if xmpp is down
     */
    private telephonyOfflineCallManagement(xmppOffline: boolean = false): void {
        this.logger.info("[telephonyService] [TEL] OfflineCallManagement, xmpp offline: " + xmppOffline);
        try {
            Object.keys(this.calls || {}).forEach((key) => {
                const call = this.calls[key];
                if (call) {
                    // Manage incoming calls : send CALL_UPDATED_EVENT with UNKNOWN status only for ringing incoming calls in order to close the incoming
                    // popup and to stop the ringing sound
                    if (call.callStatus === CallStatus.RINGING_INCOMMING || call.callStatus === CallStatus.QUEUED_INCOMMING) {
                        if (call.mediaPillarCall?.webrtcCallRef) {
                            this.logger.info("[telephonyService] OfflineCallManagement: release MP webrtc call for phone call " + call.id + " in state " + call.callStatus);
                            this.webrtcP2PService?.releaseCall(call.mediaPillarCall.webrtcCallRef);
                        }
                    }

                    // Manage outgoing calls : release mediapillar calls in state RINGING_OUtGOING (or QUEUED_OUTGOING)
                    if ((call.callStatus === CallStatus.RINGING_OUTGOING || call.callStatus === CallStatus.QUEUED_OUTGOING)
                        && call.mediaPillarCall && call.mediaPillarCall.webrtcCallRef) {
                        this.logger.info("[telephonyService] OfflineCallManagement: release MP webrtc call for phone call " + call.id + " in state " + call.callStatus);
                        this.webrtcP2PService?.releaseCall(call.mediaPillarCall.webrtcCallRef);
                    }

                    // delete all calls but keep established mediapillar calls
                    const keepCall = !xmppOffline && call.isMediaPillarCall() && call.callStatus === CallStatus.ACTIVE;
                    if (!keepCall) {
                        if (call.callStatus !== CallStatus.ENDED) {
                            this.logger.info("[telephonyService] OfflineCallManagement: clear phone call: " + call);
                            call.setStatus(CallStatus.ENDED);
                            call.sendEvent(CallEvents.ON_CALL_UPDATED_EVENT);
                            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
                        }
                        // don't delete call in this.calls because it can be useful to retrieve this cleared call after reconnection ( to read the previous isMPMediaOwner attribute during snapshot processing)
                        this.logger.info("[telephonyService] [TEL] OfflineCallManagement: phone call deleted: " + call);
                    } else {
                        //keep established mediapillar call
                        //mark the call to know that the pxb connection is down
                        this.logger.info("[telephonyService] [TEL] OfflineCallManagement: keep MP call for phone call: " + call);
                        call.pbxConnectionDown = true;
                    }
                }
            });

            this.logger.info("[telephonyService] Offline Call Management: clear " + this.pendingMakeCalls.length + " pending makecalls");
            this.pendingMakeCalls = [];

        } catch (error) {
            const errorMessage = (error?.message) ? error.message : "unknownError";
            const errorStack = (error?.stack) ? error.stack : "Unknown stack";
            this.logger.error("[telephonyService] telephonyOfflineCallManagement  exception  -- " + errorMessage + " : " + errorStack);
        }
    }

    /**
     * Retrieve and Process the telephony agent status service
     * @private
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @instance
     * @function
     */
    private async getAgentStatus(): Promise<void> {
        //sipWise++
        const TYPE_SIPWISE_RVCP = "RVCP";
        let toPCG = "/phone"; //standard PCGv1
        if (this.isSipWise()) {
            this.logger.info("[telephonyService] getAgentStatus --- sipWise version");
            toPCG = "/pcg2";
        }//sipWise--

        // Forge request iq
        const getAgentIq = $iq({ type: "get", to: this.userJidTel + toPCG })
            .c("pbxagentstatus", { xmlns: "urn:xmpp:pbxagent:monitoring:1" });

        try {
            // Send "getAgentIq" iq
            const data = await this.xmppService.sendIQ(getAgentIq, 60000); //agent status response may come after the standard 10 sec timeout : use 60 sec here

            const phoneApi = $(data).find("phoneapi").text();
            const xmppAgent = $(data).find("xmppagent").text();
            const agentVersion = $(data).find("version").text();
            const features = $(data).find("features").text();
            const type = $(data).find("type").text();
            const featuresCapabilities = $(data).find("featuresCapabilities").text();//RQ7175++

            let featureVVM: boolean = false;
            let featureOXOMULTICALL: boolean = false;
            if (featuresCapabilities) {
                const featuresTab: string[] = featuresCapabilities.split(",");
                featureVVM = featuresTab.includes("VVM");
                featureOXOMULTICALL = featuresTab.includes("OXO_MULTICALL");
            } else if (features) { //compat PCG former version
                featureVVM = (features === "VVM");
            }//RQ7175--

            if (agentVersion) {
                this.agentStatus = { phoneApi: phoneApi, xmppAgent: xmppAgent, agentVersion: agentVersion, featureVVM: featureVVM, featureOXOMULTICALL: featureOXOMULTICALL };
                if (type) {
                    this.isOxe = type.toLowerCase().includes("oxe");
                    this.isOxo = type.toLowerCase().includes("oxo");
                    this.isThirdParty = type.toLowerCase().includes("tp");
                } else {
                    this.isOxe = agentVersion.toLowerCase().includes("oxe");
                    this.isOxo = agentVersion.toLowerCase().includes("oxo");
                    this.isThirdParty = agentVersion.toLowerCase().includes("tp");
                }
            }
            if (this.isSipWise()) {
                if (type !== TYPE_SIPWISE_RVCP) {
                    this.setSipWise(false);
                    const errorMessage = "getAgentStatus -- sipWise failure : wrong PCG type";
                    this.logger.error("[telephonyService] " + errorMessage);
                    throw new Error(errorMessage);
                }
            }
            this.logger.info("[telephonyService] [TEL] getAgentStatus -- success -- version " + agentVersion + " isOxe:" + this.isOxe + " isOxo:" + this.isOxo + " isSipWise:" + this.isSipWise() + " isThirdParty:" + this.isThirdParty);
            this.eventService.publish("ON_AGENT_STATUS_RECEIVED_EVENT");
        } catch (error) {
            const errorMessage = "getAgentStatus -- failure : " + error.message;
            this.logger.error("[telephonyService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Retrieve telephony config ( devices, routingInfo, groups, emergency data ), used by sipwise
     * @private
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @instance
     * @function
     */
    private async getTelephonyConfig(): Promise<void> {
        //sipWise++
        if (this.isSipWise()) {
            this.logger.info("[telephonyService] getTelephonyConfig -- sipWise get Devices then routingInfo then groups and emergency data");
            try {
                await this.telephonyServiceSipWise.getUserDevices();
                await this.telephonyServiceSipWise.getUserRoutingInfo();
                // Fetch User's hunt groups
                this.getUserHuntGroupsConfig();
                //fetch-update emergency call data
                this.telephonyServiceSipWise.getEmergencyCallData();//RQRAINB-3835
                return;
            } catch (error) {
                this.logger.info("[telephonyService] sipwise getTelephonyConfig CATCH");
                const errorMessage = "sipwise getTelephonyConfig -- " + error.message;
                this.logger.error("[telephonyService] " + errorMessage);
                throw error;
            }
        }//sipWise--

        const errorMessage = "getTelephonyConfig not implemented for hybrid system";
        this.logger.error("[telephonyService] " + errorMessage);
        throw new Error(errorMessage);
    }

    /**
     * Retrieve and Process the telephony connections snapshot service (implemented by iq request for hybrid system)
     * @private
     * @param {boolean} secondary True for a secondary call
     * @async
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     * @instance
     * @function
     */
    public async snapshotCalls(secondary: boolean): Promise<void> {

        //sipWise++
        if (this.isSipWise()) {
            this.logger.info("[telephonyService][TEL][API] snapshotCalls -- sipWise snapshot");
            try {
                await this.telephonyServiceSipWise.processSnapshot();
                return;
            } catch (error) {
                const errorMessage = "sipwise snapshotCalls -- " + error.message;
                this.logger.error("[telephonyService][TEL][API] " + errorMessage);
                throw error;
            }
        }//sipWise--

        // Create the iq request
        let iq: any;
        if (!secondary) {
            iq = $iq({ type: "get", to: this.userJidTel + "/phone" })
                .c("callservice", { xmlns: this.CALLSERVICE_NS })
                .c("connections");
        }
        else {
            iq = $iq({ type: "get", to: this.userJidTel + "/phone" })
                .c("callservice", { xmlns: this.CALLSERVICE_NS })
                .c("connections", { deviceType: "SECONDARY" });
        }

        try {
            // Send "get connections" iq
            const data = await this.xmppService.sendIQ(iq, 60000); // connections response may come after the standard 10 sec timeout : use 60 sec here

            // Handle eventual error message
            const errorMessage = this.getErrorMessage(data, "snapshotCalls");
            if (errorMessage) {
                this.logger.info("[telephonyService] snapshotCalls -- failure -- " + errorMessage);
                throw new Error(errorMessage);
            }

            let serverDeviceType: string;
            const connections = $(data).find("connections");
            if (connections.length) {
                serverDeviceType = $(connections).attr("deviceType");
            }
            serverDeviceType = serverDeviceType || "MAIN";

            // Handle existing calls on server
            const existingCalls = $(data).find("connection");

            this.logger.info("[telephonyService] [TEL][API] snapshotCalls (" + (secondary ? "SECONDARY" : "MAIN") + ") -- success -- " + existingCalls.length + " existing call(s) on server, type: " + serverDeviceType);

            if (existingCalls.length === 0) {
                // delete all client calls
                this.deleteCallsNotInList([]);
                return;
            }

            // Build existing calls
            const getCallPromises = [];
            existingCalls.each((__index: number, connection: Element) => {
                getCallPromises.push(this.createCallFromConnectionElem(connection, secondary, serverDeviceType === "SECONDARY"));
            });

            // Send all getContactPromise
            try {
                const discoveredCalls: CallRB[] = await Promise.all(getCallPromises);
                this.logger.info("[telephonyService] snapshotCalls (" + (secondary ? "SECONDARY" : "MAIN") + ") -- calls creation completed -- " + existingCalls.length + " calls created");

                // delete all client calls not present in discovered calls
                this.deleteCallsNotInList(discoveredCalls);
            }
            catch (error) {
                this.logger.error("[telephonyService] [TEL][API] snapshotCalls -- calls creation failure -- " + error.message);
                throw error;
            }

        } catch (error) {
            this.logger.error("[telephonyService] [TEL][API] snapshotCalls -- failure -- " + error.message);
            throw error;
        }
    }

    public deleteCallsNotInList(calls: CallRB[]): void {
        const callIds = (calls || []).map((call) => { return call ? call.id : ""; });
        Object.keys(this.calls || {}).forEach((key) => {
            const call = this.calls[key];

            if (call && !callIds.includes(call.id)) {
                // This current call in client does not exist on server : clear it
                this.logger.info("[telephonyService] [TEL] deleteCallsNotInList delete call " + call);

                if (call.callStatus !== CallStatus.ENDED) {
                    call.setStatus(CallStatus.ENDED);
                    call.sendEvent(CallEvents.ON_CALL_UPDATED_EVENT);
                    this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
                }
                delete this.calls[call.id];
            }
        });
    }

    public async checkCallWithLostAudio(call: CallRB): Promise<void> {
        if (call.isSecondary && call.isMPAudioOwner) {
            const webrtcGatewayService = this.mainService.inject<WebrtcGatewayService>(WEBRTC_GATEWAY_SVC);
            const mpContext = webrtcGatewayService?.getMediaPillarCallContext(call);
            if (!mpContext?.webrtcCallRef) {
                // The call is secondary and had webrtc audio in our client before tel/xmpp disconnection,
                // but there is no webtrtc audio anymore after reconnection => release this "ghost" call with blank audio
                this.logger.info("[telephonyService] checkCallWithLostAudio -- release call in computer mode which has lost audio " + call);
                try {
                    await this.releaseCall(call);
                } catch (error) {
                    this.logger.error("[telephonyService] checkCallWithLostAudio -- release call error " + error);
                }
            }
        }
    }

    /**
     * Retrieve the telephony connections snapshot service, implemented by REST api request,
     * used by gatewayservice in case od parallel oxe hunting groups
     * @public
     * @param {boolean} secondary True for a secondary call
     * @async
     * @returns {Promise<any>} Promise resolved with the snaphot result data
     * @instance
     * @function
     */
    public async snapshotDevice(secondary: boolean): Promise<any> {
        //sipWise++
        if (this.isSipWise()) {
            return undefined;
        }//sipWise--

        let reqUrl = this.portalURL + "snapshotdevice";
        if (secondary) {
            reqUrl += "?deviceType=SECONDARY";
        }

        try {
            const url = reqUrl;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }
            const responseBody: any = await response.json();
            const data: any = responseBody.data;
            this.logger.info("[telephonyService] snapshotdevice success ");
            return data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'snapshotdevice');
            this.logger.error(`[telephonyService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Retrieve the telephony call snapshot service
     * Note: Not Used currently
     * @public
     * @param {string} callId Call Identfier
     * @async
     * @returns {Promise<void>} Promise resolved with the handling is complete
     * @instance
     * @function
     */
    // private async getSnapshotCall(callId: string): Promise<void> {
    //     this.logger.info("[telephonyService] getSnapshotCall");
    //     try {
    //         // Create the iq request
    //         const iq = $iq({ type: "get", to: this.userJidTel + "/phone" })
    //             .c("callservice", { xmlns: this.CALLSERVICE_NS })
    //             .c("snapshotCall", { callId: callId });

    //         // Send "get SnapshotCall" iq
    //         const data = await this.xmppService.sendIQ(iq);
    //         this.logger.info("[telephonyService] getSnapshotCall => " + data);
    //     }
    //     catch (error) {
    //         const errorMessage = "getSnapshotCall failure : " + error.message;
    //         this.logger.error("[telephonyService] " + errorMessage);
    //         throw new Error(errorMessage);
    //     }
    // }

    /**
     * Call creation for one connection element extracted from the snapshot result
     * @private
     * @param {any} connectionElemObj Connection element of the snapshost data
     * @param {boolean} secondary True for a secondary call
     * @param serverSecondary
     * @async
     * @returns {Promise<CallRB>} Promise resolved with the created call
     * @instance
     * @function
     */
    private async createCallFromConnectionElem(connectionElemObj: any, secondary: boolean, serverSecondary: boolean): Promise<CallRB> {

        // Extract information
        const connectionElem = $(connectionElemObj);
        const jid = connectionElem.attr("endpointIm");
        let phoneNumber = connectionElem.attr("endpointTel");
        const connectionId = connectionElem.attr("callId");
        const endpointLci = connectionElem.attr("endpointLci");
        const lci = connectionElem.attr("lci");
        const participantsElem = connectionElem.find("participant");
        const identityFirstName = connectionElem.find("identity").attr("firstName");
        const identityLastName = connectionElem.find("identity").attr("lastName");

        const existingCall: boolean = Boolean(this.calls[connectionId]);

        if (secondary !== serverSecondary) {
            this.logger.warn("[telephonyService] createCallFromConnectionElem - device type mismatch : client secondary :  " + secondary + " / server secondary: " + serverSecondary);
            // if the device type is different in server response, we don't create call here, but we keep existing call with this connectionId
            return this.calls[connectionId];
        }

        let firstName = "";
        let lastName = "";

        if (!jid && !phoneNumber) { phoneNumber = "****"; }

        //manage name resolution
        if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_PHONE_BOOK)) {
            //find Xnames, here for simple call only
            if (participantsElem.length === 0 && identityLastName && identityLastName.length) {
                lastName = identityLastName;
                if (identityFirstName?.length) {
                    firstName = identityFirstName;
                }
                this.logger.info("[telephonyService] createCallFromConnectionElem - name resolution for: " + connectionId + " for phoneNumber:" + anonymizer(phoneNumber)
                    + " with firstname : " + firstName.slice(0, 1) + "***");
            }
        }

        // Create initiated calls only for nomadic computer case (ignore initiated calls in other cases)
        //if (lci === "LCI_INITIATED" && !(secondary && this.isMediaPillarUserSelected())) { return null; }

        // Define getParticipants promise
        // The participant elem only exists for a conference call
        const getParticipants = () => {
            //simple call
            if (participantsElem.length === 0) { return this.contactService.getOrCreateContact(jid, phoneNumber); }
            //conference call
            return this.getParticipantsFromParticipantsElem(participantsElem);
        };

        // Call the promise
        try {
            const response: any = await getParticipants();
            // response is a Contact object (simple call) or a Contact array (conference call)
            // Extract call status
            let callStatus = CallStatus.ACTIVE;
            if (lci === "LCI_INITIATED") { callStatus = CallStatus.DIALING; }
            if (lci === "LCI_HELD" && endpointLci === "LCI_CONNECTED") { callStatus = CallStatus.HOLD; }
            if (lci === "LCI_CONNECTED" && endpointLci === "LCI_HELD") { callStatus = CallStatus.PUT_ON_HOLD; }
            if (lci === "LCI_CONNECTED" && endpointLci === "LCI_QUEUED") { callStatus = CallStatus.QUEUED_OUTGOING; }
            if (lci === "LCI_QUEUED" && endpointLci === "LCI_CONNECTED") { callStatus = CallStatus.QUEUED_INCOMMING; }
            if (lci === "LCI_ALERTING") { callStatus = CallStatus.RINGING_INCOMMING; }

            // Create the call object
            let call: CallRB = null;
            if (participantsElem.length === 0) {
                // simple call
                if (response?.temp && lastName !== "") {
                    response.updateName(firstName, lastName);
                }
                call = this.getOrCreateCall(callStatus, connectionId, response);
                this.logger.info("[telephonyService] [TEL] createCallFromConnectionElem - create call " + call + " for user: " + response.id);
            }
            else {
                // conference call
                call = this.getOrCreateCall(callStatus, connectionId);
                call.setParticipants(response);
                call.isConference = true;
                this.logger.info("[telephonyService] [TEL] createCallFromConnectionElem - create conference call " + call);
            }

            this.setCalledPartyFromEvent(connectionElem, call, phoneNumber, jid);

            call.relevantEquipmentId = CallRB.getDeviceIdFromConnectionId(connectionId);
            if (!existingCall) { call.isSecondary = secondary; }

            // Note : we don't reset the 'pbxConnectionDown' attribute to keep the info that the call has been maintained after a telephony loss,
            // even if the telephony service has come back.
            // On oxe, we don't receive events for a call existing before the csta initialization: that's why we propose a degraded telephony service on such calls (no hold call...)

            call.sendEvent(CallEvents.ON_CALL_UPDATED_EVENT);
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);

            this.checkCallWithLostAudio(call);
            return call;
        }
        catch (error) {
            this.logger.error("[TelephonyService] createCallFromConnectionElem - failure - " + error.message);
            throw error;
        }
    }

    public async setCalledPartyFromEvent(event: any, call: CallRB, endpointPhoneNumber?: string, endpointJid?: string): Promise<boolean> {
        const partyElem: any = event.find("party");
        if (partyElem?.length) {
            const number: string = partyElem.attr('number') || "";
            if (number) {
                const role: string = partyElem.attr('role') || "";
                const jid: string = partyElem.attr('jid') || "";
                const displayName: string = partyElem.attr('displayName') || "";
                const firstName: string = partyElem.attr('firstName') || "";
                const lastName: string = partyElem.attr('lastName') || "";
                const previousCallRedirectionCause: CallRedirectionCause = call.redirectionCause;
                call.redirectionCause = role === "called" ? CallRedirectionCause.FORWARDED : (role === "calledGroup" ? CallRedirectionCause.HUNTGROUP : CallRedirectionCause.UNDEFINED);
                this.logger.info("[TelephonyService][CALLED] setCalledPartyFromEvent redirectionCause " + call.redirectionCause);

                return this.setCalledParty(call, number, jid, displayName, firstName, lastName, role)
                    || (previousCallRedirectionCause !== call.redirectionCause);
            }
        } else {
            // No party element in event
            // For an outgoing call, we use the endpoint to compute the called party (if callednumber not already set, and endpoint data available)
            if (call.direction === CallDirection.OUTGOING && !call.calledParty?.phoneNumber && (endpointPhoneNumber || endpointJid)) {
                const contact: Contact = await this.contactService.getOrCreateContact(endpointJid, endpointPhoneNumber);

                return this.setCalledParty(call, endpointPhoneNumber, endpointJid, contact?.displayName, contact?.firstname, contact?.lastname, "called");
            }
        }
        return false;
    }

    public setCalledParty(call: CallRB, phoneNumber: string, jid: string, displayName: string, firstName: string, lastName: string, role: string): boolean {
        if (phoneNumber) {
            let calledDisplayName: string = displayName || call.calledParty?.displayName;
            const calledFirstName: string = firstName || call.calledParty?.firstName;
            const calledLastName: string = lastName || call.calledParty?.lastName;

            if (calledFirstName || calledLastName) {
                calledDisplayName = this.computeDisplayName(calledFirstName, calledLastName);
            }
            else if (!calledDisplayName) {
                calledDisplayName = phoneNumber;
            }

            const previousCalledParty: CalledParty = call.calledParty ? JSON.parse(JSON.stringify(call.calledParty)) : undefined;
            call.calledParty = {
                phoneNumber,
                role,
                jid,
                displayName: calledDisplayName,
                firstName: calledFirstName,
                lastName: calledLastName
            };

            call.setIsVm(call.calledNumber === this.voicemailNumber || call.calledNumber === "voicebox"); //CRRAINB-44709

            const callUpdated: boolean = previousCalledParty?.phoneNumber !== call.calledParty.phoneNumber
                || previousCalledParty?.role !== call.calledParty.role
                || previousCalledParty?.jid !== call.calledParty.jid
                || previousCalledParty?.displayName !== call.calledParty.displayName
                || previousCalledParty?.firstName !== call.calledParty.firstName
                || previousCalledParty?.lastName !== call.calledParty.lastName;

            if (callUpdated) {
                //send specific update event on the call object to update the called party
                call.sendEvent('calledParty');
            }
            this.logger.info(`[TelephonyService][CALLED] setCalledParty call ${call.id} number ${anonymizer(phoneNumber)} jid ${jid} displayName '${displayName}' firstName '${firstName}' lastName '${lastName}' role '${role}' callUpdated ${callUpdated}`);
            return callUpdated;
        }
        return false;
    }

    /**
     * Calculate the DisplayName
     * @private
     * @param {string} firstname Contact firstname
     * @param {string} lastname Contact lastname
     * @returns {string} Computed displayName
     * @instance
     * @function
     */
    private computeDisplayName(firstname: string, lastname: string): string {
        const firstName = firstname ? firstname.charAt(0).toUpperCase() + firstname.slice(1) : null;
        const lastName = lastname ? lastname.charAt(0).toUpperCase() + lastname.slice(1) : null;
        let displayName: string = "";

        if (lastName && firstName) {
            if (this.settingsService.getSetting("displayOrder") === "FL") {
                displayName = firstName + " " + lastName;
            } else {
                displayName = lastName + " " + firstName;
            }
        } else if (lastName && !firstName) {
            displayName = lastName;
        } else if (firstName) {
            displayName = firstName;
        } else {
            displayName = "Anonymous";
        }

        return displayName;
    }

    /**
     * Contact list creation for participant elements extracted from the snapshot result
     * @private
     * @param {any} participants Participant elements of the snapshost data
     * @async
     * @returns {Promise<Contact[]>} Promise resolved with the list of contacts
     * @instance
     * @function
     */
    private async getParticipantsFromParticipantsElem(participants: any): Promise<Contact[]> {
        const confParticipants: Contact[] = [];

        // Create getParticipantPromise
        const participantPromises: Promise<void>[] = [];

        participants.each((__index: number, participant: Element) => {
            const participantElem = $(participant);
            let endpointTel = participantElem.find("endpointTel").text();
            const endpointIm = participantElem.find("endpointIm").text();

            if (!(endpointIm && this.contactService.isUserContactJid(endpointIm))) {
                participantPromises.push((async (): Promise<void> => {
                    if (!endpointIm && !endpointTel) { endpointTel = "****"; }
                    const contact = await this.contactService.getOrCreateContact(endpointIm, endpointTel);
                    confParticipants.push(contact);
                })());
            }
        });

        // Get participants asynchronously
        await Promise.all(participantPromises);
        return confParticipants;
    }

    /**
     * Retrieve an existing call or create a new call with the given status, connectionId and contact
     * @public
     * @param {any} status Call status
     * @param {string} connectionId Call connection identifier
     * @param {Contact} [contact] Contact identifying the other party of the call
     * @returns {Call} Found call or created call
     * @instance
     * @function
     */
    public getOrCreateCall(status: CallStatus, connectionId: string, contact: Contact = null): CallRB {
        // Get eventual existing call
        let call: CallRB = this.calls[CallRB.getIdFromConnectionId(connectionId)];

        if (call) {
            call.setConnectionId(connectionId);
            call.startDate = call.startDate || new Date();
            call.setStatus(status);
        }
        else {
            call = CallRB.create(status, null, CallType.PHONE, contact);
            call.setStatus(status); // in order to manage duration
            call.setConnectionId(connectionId);
            this.calls[call.id] = call;
        }
        return call;
    }

    /**
     * Retrieve an existing sipwise call or create a new sipwise call with the given status, callId
     * @public
     * @param {any} status Call status
     * @param {string} callId Call identifier
     * @returns {Call} Found call or created call
     * @instance
     * @function
     */
    public createCallSipWise(status: CallStatus, callId: string): CallRB {
        // Get eventual existing call
        let call: CallRB;
        if (callId) {
            call = this.calls[callId];
        }
        if (call) {
            call.startDate = call.startDate || new Date();
        }
        else {
            // Create call
            // First search for a pending makecall
            call = this.pendingMakeCalls.pop() as CallRB;
            if (call) {
                // Take this pending makecall as new call
                call.id = callId;
                call.setStatus(status);
                call.startDate = call.startDate || new Date();
                call.setRelevantEquipmentId(call.getDeviceIdSipWise());//PROVISOIRE_SIPWISE

                // keepAlive defence : Arm keep alive TO in case of NGCP dead case for this call
                call.autoClear = setTimeout(() => { this.logger.info("[TelephonyService] createCallSipWise keepAlive CallReset (" + call + ")"); this.clearCall(call); }, 10000);

                this.logger.info("[TelephonyService] createCallSipWise - " + callId + " => found pendingMakeCall: Call confirmed by csta event " + call);
            } else {
                call = CallRB.create(status, callId, CallType.PHONE);
            }
            this.calls[call.id] = call;
        }
        return call;
    }

    /**
     * Retrieve an existing sipwise call or create a new sipwise call with the given status, callId, legId, deviceId and contact
     * @public
     * @param {CallStatus} status Call status
     * @param {string} callId Call identifier
     * @param {string} legId Leg identifier
     * @param {string} [deviceId] Device identifier
     * @param {Contact} [contact] Contact identifying the other party of the call
     * @returns {Call} Found call or created call
     * @instance
     * @function
     */
    public getOrCreateCallSipWise(status: CallStatus, callId: string, legId: string, deviceId: string = undefined, contact: Contact = undefined): CallRB {
        // Get eventual existing call
        let call: CallRB;
        if (callId) {
            call = this.calls[callId];
        }
        if (call) {
            call.setConnectionIdSipWise(legId);
            if (contact) { call.setContact(contact); }
            if (deviceId) { call.setDeviceIdSipWise(deviceId); }
            call.startDate = call.startDate || new Date();
            call.setStatus(status);
        }
        else { //create it
            call = CallRB.create(status, callId, CallType.PHONE, contact);
            call.setStatus(status); // in order to manage call duration
            call.setConnectionIdSipWise(legId);
            call.setDeviceIdSipWise(deviceId);
            this.calls[call.id] = call;
        }
        return call;
    }

    /*********************************************************/
    /**                   CALL HANDLERS                     */
    /*********************************************************/

    // public getCallToHangOut(): CallRB {
    //     const calls = this.getCalls();
    //     if (!calls || calls.length === 0) { return null; }
    //     const callStatus = calls[0].status;
    //     if (calls.length === 1 || (callStatus === CallStatus.DIALING || callStatus === CallStatus.ACTIVE || callStatus === CallStatus.PUT_ON_HOLD)) { return calls[0]; }
    //     return calls[1];
    // }

    /**
     * Retrieve the active call (if one exists)
     * @public
     * @returns {Call} Found active call (or null if not found)
     * @instance
     * @function
     */
    public getActiveCall(): CallRB {
        return this.getCall(CallStatus.ACTIVE);
    }

    // /**
    //  * Retrieve the ringing outgoing call (if one exists)
    //  *
    //  * @public
    //  * @returns {Call} Found ringing outgoing call (or null if not found)
    //  * @instance
    //  * @function
    //  */
    // public getRingingOutgoingCall(): CallRB {
    //     return this.getCall(CallStatus.RINGING_OUTGOING);
    // }

    /**
     * Retrieve the call with the given searched status (if one exists)
     * @public
     * @param {any} searchedCallStatus Status of call to search
     * @returns {Call} Found call with searched status (or null if not found)
     * @instance
     * @function
     */
    public getCall(searchedCallStatus: any): CallRB {
        let foundCall = null;
        Object.keys(this.calls || {}).forEach((key) => {
            const call = this.calls[key];
            if (call.callStatus === searchedCallStatus) {
                foundCall = call;
            }
        });
        return foundCall;
    }

    /**
     * Retrieve the ongoing outgoing call (if one exists)
     * @public
     * @returns {Call} Found ongoing outgoing call (or null if not found)
     * @instance
     * @function
     */
    public getOngoingOutgoingCall(): CallRB {
        let ongoingOutgoingCall = null;
        Object.keys(this.calls || {}).forEach((key) => {
            const call = this.calls[key];
            if (call.callStatus === CallStatus.RINGING_OUTGOING
                || call.callStatus === CallStatus.QUEUED_OUTGOING
                || call.callStatus === CallStatus.DIALING
                || call.callStatus === CallStatus.ERROR) {
                ongoingOutgoingCall = call;
            }
        });
        return ongoingOutgoingCall;
    }

    /**
     * Retrieve the ongoing outgoing call with the given contact (if one exists)
     * @public
     * @param {Contact} contact Contact identifying the other party of the call
     * @returns {Call} Found ongoing outgoing call with given contact (or null if not found)
     * @instance
     * @function
     */
    public getOngoingOutgoingCallForContact(contact: Contact): CallRB {
        let ongoingOutgoingCall = null;
        Object.keys(this.calls || {}).forEach((key) => {
            const call = this.calls[key];
            if ((call.callStatus === CallStatus.RINGING_OUTGOING
                || call.callStatus === CallStatus.QUEUED_OUTGOING
                || call.callStatus === CallStatus.DIALING
                || call.callStatus === CallStatus.ERROR) && call.contact.jid === contact.jid) {
                ongoingOutgoingCall = call;
            }
        });
        return ongoingOutgoingCall;
    }

    /**
     * Retrieve the list of ringing incoming calls (if some exist)
     * @public
     * @returns {CallRB[]} Found ringing incoming call array (or empty array if no calls found)
     * @instance
     * @function
     */
    public getRingingIncomingCalls(): CallRB[] {
        return this.getCalls(CallStatus.RINGING_INCOMMING);
    }

    /**
     * Retrieve the list of queued incoming calls (if some exist)
     * @public
     * @returns {CallRB[]} Found queued incoming call array (or empty array if no calls found)
     * @instance
     * @function
     */
    public getQueuedIncomingCalls(): CallRB[] {
        return this.getCalls(CallStatus.QUEUED_INCOMMING);
    }

    /**
     * Retrieve the list of hold (put on hold by the user) calls (if some exist)
     * @public
     * @returns {CallRB[]} Found hold call array (or empty array if no calls found)
     * @instance
     * @function
     */
    public getCallsOnHold(): CallRB[] {
        return this.getCalls(CallStatus.HOLD);
    }

    /**
     * Retrieve the list of calls with a status equal to a given status (or a list of given status) (if some exist)
     * @public
     * @param {any} [searchedCallStatus] Status (or array of status) of calls to search. If not passed, it returns all calls except incoming/queued ringing calls
     * @returns {CallRB[]} List of calls matching the searched status (or empty array if no calls found)
     * @instance
     * @function
     */
    public getCalls(searchedCallStatus: CallStatus[] | CallStatus = null): CallRB[] {
        const calls: CallRB[] = [];
        const searchedStatusArray = searchedCallStatus
            ? (Array.isArray(searchedCallStatus) ? searchedCallStatus : [searchedCallStatus])
            : [
                //default returned calls: all calls except ringing incoming calls
                CallStatus.DIALING,
                CallStatus.RINGING_OUTGOING,
                CallStatus.QUEUED_OUTGOING,
                CallStatus.ACTIVE,
                CallStatus.HOLD,
                CallStatus.PUT_ON_HOLD,
                CallStatus.ERROR
            ];

        Object.keys(this.calls || {}).forEach((key) => {
            const call = this.calls[key];
            const status = call.callStatus;

            if (searchedStatusArray.includes(status)) {
                calls.push(call);
            }
        });
        return calls;
    }

    /**
     * Retrieve the list of calls which are not idle (not in UNKNOWN status). Note that this method also returns the ringing/queued incoming calls
     * @public
     * @returns {CallRB[]} List of calls not idle (not in ENDED status)
     * @instance
     * @function
     */
    public getNotIdleCalls(): CallRB[] {
        const calls = [];
        Object.keys(this.calls || {}).forEach((key) => {
            const call = this.calls[key];
            if (call.callStatus !== CallStatus.ENDED) { calls.push(call); }
        });
        return calls;
    }

    /**
     * Is the user in idle telephony state (no current telephony calls)
     * @public
     * @returns {boolean} true if the user is idle (no current telephony calls)
     * @instance
     * @function
     */
    public isUserIdle(): boolean {
        const existCall = Object.keys(this.calls || {}).some((key) => {
            const call = this.calls[key];
            return call.callStatus !== CallStatus.ENDED;
        });
        return !existCall;
    }

    /**
     * Retrieve the list of active calls linked to a given contact (other party).
     * Active calls means calls that are not idle and not in ringing/queued incoming status
     * @public
     * @param {Contact} contact Contact to search in the call contact
     * @returns {CallRB[]} List of calls linked to the given contact
     * @instance
     * @function
     */
    public getActiveCallsForContact(contact: Contact): CallRB[] {
        const calls = [];
        if (contact?.jid) {
            Object.keys(this.calls || {}).forEach((key) => {
                if (
                    (this.calls[key].contact && this.calls[key].contact.jid === contact.jid)
                    && (this.calls[key].callStatus === CallStatus.DIALING
                        || this.calls[key].callStatus === CallStatus.RINGING_OUTGOING
                        || this.calls[key].callStatus === CallStatus.QUEUED_OUTGOING
                        || this.calls[key].callStatus === CallStatus.ACTIVE
                        || this.calls[key].callStatus === CallStatus.HOLD
                        || this.calls[key].callStatus === CallStatus.PUT_ON_HOLD)) { calls.push(this.calls[key]); }
            });
        }
        return calls;
    }

    /**
     * Retrieve the list of calls linked to a given contact (other party), whatever the call status is (if any)
     * @public
     * @param {Contact} contact Contact to search in the call contact
     * @returns {CallRB[]} List of calls linked to the given contact; empty array if no calls found
     * @instance
     * @function
     */
    public getCallsForContact(contact: Contact): CallRB[] {
        const calls = [];
        if (contact?.jid) {
            Object.keys(this.calls || {}).forEach((key) => {
                const call = this.calls[key];
                if (
                    (call.contact && call.contact.jid === contact.jid)
                    && (call.callStatus !== CallStatus.ENDED)) {
                    calls.push(call);
                }
            });
        }
        return calls;
    }

    /**
     * Retrieve the conference call linked to a given contact (if one exists)
     * @public
     * @param {Contact} contact Contact for which we search a call
     * @returns {Call} Found conference call linked to the given contact (or null if not found)
     * @instance
     * @function
     */
    public getConferenceCallForContact(contact: Contact): CallRB {
        let conferenceCall = null;
        if (contact?.jid) {
            Object.keys(this.calls || {}).forEach((key) => {
                if ((this.calls[key].callStatus === CallStatus.ACTIVE)
                    && this.calls[key].isConference
                    && (this.calls[key].participants || []).some((participantContact) => {
                        return participantContact && participantContact.jid === contact.jid;
                    })) {
                    conferenceCall = this.calls[key];
                }
            });
        }
        return conferenceCall;
    }

    /**
     * Retrieve another call with same global call id
     * @public
     * @param {Call} sourceCall Source call with the globalCallId to search for
     * @returns {Call} Found call with same global call id as source call (or null if not found)
     * @instance
     * @function
     */
    public getOtherCallWithSameGlobalCallId(sourceCall: CallRB): CallRB {
        if (!sourceCall?.globalCallId) { return null; }

        const foundCallKey = Object.keys(this.calls || {}).reverse().find((key) => {
            const call = this.calls[key];
            this.logger.debug("[telephonyService] getOtherCallWithSameGlobalCallId check call " + call);
            return call.id !== sourceCall.id && call.globalCallId === sourceCall.globalCallId;
        });
        const foundCall = foundCallKey ? this.calls[foundCallKey] : null;
        this.logger.info("[telephonyService] getOtherCallWithSameGlobalCallId source call id: " + sourceCall.id + " globalCallId: " + sourceCall.globalCallId + " => " + (foundCall ? "found call " + foundCall : "not found"));
        return foundCall;
    }

    /**
     * Log the makecall request service data
     * @private
     * @param {any} reqData Data body of makecall request
     * @param {Contact} contact Contact for which we search a call
     * @instance
     * @function
     */
    public logMakecallReqData(reqData: any, contact: Contact): void {
        const reqDataForLogs = {};
        Object.keys(reqData).forEach((reqDataProperty) => {
            switch (reqDataProperty) {
                case "calleeExtNumber":
                case "calleeIntNumber":
                case "calleeShortNumber":
                case "calledNumber":
                    if (reqData[reqDataProperty] !== "" && reqData[reqDataProperty] !== undefined) {
                        reqDataForLogs[reqDataProperty] = anonymizer(reqData[reqDataProperty]);
                    } else {
                        reqDataForLogs[reqDataProperty] = reqData[reqDataProperty];
                    }
                    break;
                case "calleeDisplayName":
                    if (contact) {
                        reqDataForLogs[reqDataProperty] = (contact && contact.nameForLogs) ? contact.nameForLogs : "";
                    }
                    break;
                default:
                    reqDataForLogs[reqDataProperty] = reqData[reqDataProperty];
                    break;
            }
        });
        this.logger.info("[telephonyService] makeCall data: " + JSON.stringify(reqDataForLogs));
    }

    /*************************************************************/
    /*                CALL CONTROL REQUEST SERVICES              */
    /*************************************************************/

    /**
     * Makecall service
     * @public
     * @param {Contact} contact The contact to call
     * @param {string} phoneNumber The phone number to call
     * @param {string} [callSubject] Call subject
     * @param {any} [correlatorData] Call correlator data
     * @param {any} [correlatorDataHex] Call correlator data hexa
     * @param {string} [calledNumber] Called number
     * @async
     * @returns {Promise<string>} Promise resolved with the created call identifier (callId)
     * @instance
     * @function
     */
    public async makeCall(contact: Contact, phoneNumber: string, callSubject: string = undefined, correlatorData: any = undefined, correlatorDataHex: any = undefined, calledNumber: string = undefined, bubble: Bubble = undefined): Promise<string> {

        this.logger.info("[telephonyService] [TEL][API] makeCall to " + ((contact?.nameForLogs) ? contact.nameForLogs : anonymizer(phoneNumber)));

        if (this.makingCall) {
            this.logger.warn("[telephonyService] [TEL][API] makeCall failure - makeCall already making a call");
            return undefined;
        }

        const ringingOutgoingCall = this.getOngoingOutgoingCall(); //CRRAINB-32581
        if (ringingOutgoingCall) {
            this.logger.warn("[telephonyService] [TEL][API] makeCall failure - ringingOutgoing call in progress");
            this.displayNotAllowedNotification(contact, phoneNumber);
            return undefined;
        }

        const activeCall = this.getActiveCall();

        if (activeCall?.isConference) {
            this.logger.warn("[telephonyService] [TEL][API] makeCall failure - conference call in progress");
            this.displayNotAllowedNotification(contact, phoneNumber);
            return undefined;
        }

        if (!this.contactService.userContact.capabilities.telephony) {
            this.logger.warn("[telephonyService] [TEL][API] makeCall failure - user cannot make telephony call (telephony capability)");
            this.displayNotAllowedNotification(contact, phoneNumber);
            return undefined;
        }

        if (this.isMixPstnWebRtcAllowed) {
            //check if any active webrtc call and put it on hold before we make another call
            this.eventService.publish("PUT_ACTIVE_WEBRTC_CALL_OR_CONF_ON_HOLD");
        }

        // Get the correlator data from manifest file, if available
        const result = this.getCorrelatorDataFromManifest(phoneNumber);
        correlatorData = result?.correlatorData || correlatorData;
        correlatorDataHex = result?.correlatorDataHex || correlatorDataHex;

        // Do Makecall

        // TODO : remove after check with Pascal M. if ok for SDK
        // First activate telephony app view (for softphone setting)
        //this.publish(RBEvent.create('onMakeCall'));

        // Set makingCall flag
        this.makingCall = true;

        // Handle simpleCall
        if (!activeCall) {
            //sipWise++
            if (!this.isSipWise()) { //not sipWise
                return this.makeSimpleCall(contact, phoneNumber, callSubject, correlatorData, correlatorDataHex, calledNumber);
            } //sipWise case
            return this.telephonyServiceSipWise.makeSimpleCall(contact, phoneNumber, calledNumber, bubble);
            //sipWise--
        }

        // Handle consultationCall
        if (!this.isSipWise()) { //not sipWise
            return this.makeConsultationCall(contact, phoneNumber, activeCall.connectionId, correlatorData, correlatorDataHex);
        }

        //sipWise case ++
        if (this.isMediaPillarAvailable() && this.isMediaPillarUserSelected()) {
            //when webrtcgateway hold 1st call before make 2nd call
            try {
                await this.telephonyServiceSipWise.holdCall(activeCall);
                return this.telephonyServiceSipWise.makeSimpleCall(contact, phoneNumber);
            }
            catch (error) {
                return undefined;
            }
        } //not a webrtcgateway case
        return this.telephonyServiceSipWise.makeSimpleCall(contact, phoneNumber);
        //sipWise--
    }

    /**
     * Display a notification during 5 seconds in case of makecall service failures
     * @private
     * @param {Contact} contact The contact to call
     * @param {string} phoneNumber The phone number to call
     * @instance
     * @function
     */
    private displayNotAllowedNotification(contact: Contact, phoneNumber: string): void {
        this.logger.debug("[telephonyService] displayNotAllowedNotification");
        const call = CallRB.create(CallStatus.ERROR, null, CallType.PHONE, contact);
        call.errorMessage = "notAllowed";

        call.isSecondary = this.nomadicObject && !this.nomadicObject.makeCallInitiatorIsMain;
        call.isOwner = true; // The web client is the owner of this call
        call.setIsVm(phoneNumber === this.voicemailNumber); // Indicate whether it is a call to own voicemail

        this.calls[call.contact.id] = call;
        setTimeout(() => { this.clearCall(call); }, 5000);

        // Send call update event
        call.sendEvent(CallEvents.ON_CALL_UPDATED_EVENT);
        this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
    }

    /**
     * Make a call while no current call is in progress
     * @private
     * @param {Contact} contact The contact to call
     * @param {string} phoneNumber The phone number to call
     * @param {string} callSubject Call subject
     * @param {any} correlatorData Call correlator data
     * @param {any} correlatorDataHex Call correlator data hexa
     * @param {string} calledNumber Called number
     * @async
     * @returns {Promise<string>} Promise resolved with the created call identifier (callId)
     * @instance
     * @function
     */
    private async makeSimpleCall(contact: Contact, phoneNumber: string, callSubject: string, correlatorData: any, correlatorDataHex: any, calledNumber: string): Promise<string> {
        this.logger.info("[telephonyService] makeSimpleCall to " + ((contact?.nameForLogs) ? contact.nameForLogs : anonymizer(phoneNumber)));

        //reject not allowed operations
        if (!this.isBasicCallAllowed) {
            const profileError: any = new Error("makeSimpleCall failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);

            // Release makingCall flag
            this.makingCall = false;
            throw profileError;
        }

        //check already done with capabilities

        const phoneInfo = this.getPhoneInfo(contact, phoneNumber);

        const reqUrl = this.portalURL + "calls";
        const reqData = {
            calleeExtNumber: phoneInfo.longNumber, calleeIntNumber: phoneInfo.internalNumber, calleeShortNumber: phoneInfo.shortNumber,
            calleePbxId: phoneInfo.pbxId, calleeDisplayName: (contact ? contact.displayName : ""), callSubject: callSubject, correlatorData: correlatorData, correlatorDataHex: correlatorDataHex,
            secondaryDeviceMakeCall: (typeof this.nomadicObject.makeCallInitiatorIsMain !== 'undefined') && this.nomadicObject.featureActivated === true && this.isNomadicEnabled ? (this.nomadicObject.makeCallInitiatorIsMain ? "false" : "true") : undefined,
            resource: this.xmppService.getResourceFromJid(this.contactService.userContact.fullJid),
            calledNumber: ""
        };
        if (calledNumber) { reqData.calledNumber = calledNumber; }

        this.logger.info("[telephonyService] makeSimpleCall " + reqUrl);
        this.logMakecallReqData(reqData, contact);

        return this.doRequestMakecall(contact, phoneNumber, reqUrl, reqData);
    }

    /**
     * Make a consultation call while there is one existing active call
     * @private
     * @param {Contact} contact The contact to call
     * @param {string} phoneNumber The phone number to call
     * @param {string} callId  Call identifier of the existing active call
     * @param {any} correlatorData Call correlator data
     * @param {any} correlatorDataHex Call correlator data hexa
     * @param hidden
     * @async
     * @returns {Promise<string>} Promise resolved with the created call identifier (callId)
     * @instance
     * @function
     */
    private async makeConsultationCall(contact: Contact, phoneNumber: string, callId: string, correlatorData: any = undefined, correlatorDataHex: any = undefined, hidden: boolean = false): Promise<string> {
        this.logger.info("[telephonyService] makeConsultationCall to " + ((contact && contact.nameForLogs) ? contact.nameForLogs : anonymizer(phoneNumber)));

        //reject not allowed operations
        if (!this.isSecondCallAllowed) {
            const profileError: any = new Error("makeConsultationCall failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);

            // Release makingCall flag
            this.makingCall = false;
            throw profileError;
        }

        const phoneInfo = this.getPhoneInfo(contact, phoneNumber, correlatorData, correlatorDataHex);

        const reqUrl = this.portalURL + "calls/" + encodeURIComponent(callId) + "/consultation";
        const reqData = {
            calleeExtNumber: phoneInfo.longNumber,
            calleeIntNumber: phoneInfo.internalNumber,
            calleeShortNumber: phoneInfo.shortNumber,
            calleePbxId: phoneInfo.pbxId,
            calleeDisplayName: contact.displayName,
            correlatorData: phoneInfo.correlatorData,
            correlatorDataHex: phoneInfo.correlatorDataHex,
            resource: this.xmppService.getResourceFromJid(this.contactService.userContact.fullJid)
        };
        this.logger.info("[telephonyService] makeConsultationCall " + reqUrl);
        this.logMakecallReqData(reqData, contact);

        try {
            return this.doRequestMakecall(contact, phoneNumber, reqUrl, reqData, hidden);
        }
        catch (error) {
            //clean active client call not existing on server
            this.deleteCallIfInvalid(error, this.calls[CallRB.getIdFromConnectionId(callId)]);
            throw (error);
        }
    }

    /**
     * Send the makecall or consultation call service request to the server
     * @private
     * @param {Contact} contact The contact to call
     * @param {string} phoneNumber The phone number to call
     * @param {string} reqUrl Request url
     * @param {any} reqData Service request data
     * @param hidden
     * @async
     * @returns {Promise<string>} Promise resolved with the created call identifier (callId)
     * @instance
     * @function
     */
    private async doRequestMakecall(contact: Contact, phoneNumber: string, reqUrl: string, reqData: any, hidden: boolean = false): Promise<string> {
        this.logger.info("[telephonyService] doRequestMakecall to " + ((contact && contact.nameForLogs) ? contact.nameForLogs : anonymizer(phoneNumber)));

        // create call and save it in pending makecalls array
        const call = CallRB.create(CallStatus.ENDED, null, CallType.PHONE, contact);
        call.isHidden = hidden;
        call.isSecondary = this.nomadicObject && !this.nomadicObject.makeCallInitiatorIsMain;
        call.isOwner = true; // The web client is the owner of this call
        call.setIsVm(phoneNumber === this.voicemailNumber); // Indicate whether it is a call to own voicemail
        //save pending created call
        this.pendingMakeCalls.push(call);
        this.logger.info("[telephonyService] doRequestMakecall Call created (" + call + ")");

        try {
            const url = reqUrl;
            const headers = this.authService.getPostHeader();
            const data = reqData;
            const body = JSON.stringify(data);
            const response: Response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) {
                throw (response);
            }
            const responseBody: any = await response.json();
            this.logger.info("[telephonyService] [TEL][API] makecall - success : callee " + anonymizer(phoneNumber) + " callId: " + responseBody.data.callId);

            const isPendingMakeCall = this.pendingMakeCalls.includes(call);
            if (isPendingMakeCall) {
                // the created call is still pending : it means no csta event have been received for this call
                call.setStatus(CallStatus.DIALING);
                call.setConnectionId(responseBody.data.callId); //set connectionId and id
                call.setRelevantEquipmentId(CallRB.getDeviceIdFromConnectionId(call.connectionId));

                this.calls[call.id] = call;
                this.pendingMakeCalls.splice(this.pendingMakeCalls.indexOf(call), 1);
                this.logger.info("[telephonyService] doRequestMakecall Call confirmed by makecall acknowlegement (" + call + ")");

                // Send call update event
                call.sendEvent(CallEvents.ON_CALL_UPDATED_EVENT);
                this.eventService.publish("ON_CALL_UPDATED_EVENT", call);

                return call.id;
            }

            // the created call is not pending anymore : it means some csta events have been received for this call
            return call.id ? call.id : (responseBody.data.callId as string);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'makeCall');
            this.logger.error(`[telephonyService] [TEL][API] doRequestMakecall ${errorInfo.message}`);

            call.setStatus(CallStatus.ERROR);
            call.errorMessage = errorInfo.error.errorDetailsCode === "403"
                ? "notAllowed"
                : (errorInfo.error.errorDetailsCode === "487002" ? "operationNotPossible" : "invalidPhoneNumber");

            this.calls[call.contact.id] = call;
            this.pendingMakeCalls.splice(this.pendingMakeCalls.indexOf(call), 1);

            setTimeout(() => { this.clearCall(call); }, 5000);

            // Send call update event
            call.sendEvent(CallEvents.ON_CALL_UPDATED_EVENT);
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);

            throw errorInfo.error;
        } finally {
            // Release makingCall flag
            this.makingCall = false;
        }
    }

    /**
     * Makecall by phone number: this method is used to make a call to a given phone number
     * (where as the makecall service is used when the called destination is identified by a contact object)
     * Used by SDK (public)
     * Warning when modifying this method
     * @public
     * @param {string} phoneNumber The phone number to call
     * @param {string} [callSubject] Call subject
     * @param {any} [correlatorData] Call correlator data
     * @param {any} [correlatorDataHex] Call correlator data hexa
     * @async
     * @returns {Promise<string>} Promise resolved with the created call identifier (callId)
     * @instance
     * @function
     */
    public async makeCallByPhoneNumber(phoneNumber: string, callSubject: string = undefined, correlatorData: any = undefined, correlatorDataHex: any = undefined): Promise<void> {
        this.logger.info("[telephonyService] [TEL][API] makeCallByPhoneNumber : " + anonymizer(phoneNumber));

        if (!this.started) {
            const errorMessage = "makeCallByPhoneNumber failure: telephony not started";
            this.logger.error("[telephonyService] " + errorMessage);
            throw new Error(errorMessage);
        }

        if (this.contactService.userContact.phonePro === phoneNumber || this.contactService.userContact.phoneProCan === phoneNumber || this.contactService.userContact.phonePbx === phoneNumber) {
            const errorMessage = "makeCallByPhoneNumber failure: impossible to call its own phone number";
            this.logger.error("[telephonyService] " + errorMessage);
            throw new Error(errorMessage);
        }
        try {
            // We create a temporary contact with the given phone number and we call the makecall service with this contact
            const contact = await this.contactService.getOrCreateContact(null, phoneNumber);
            await this.makeCall(contact, phoneNumber, callSubject, correlatorData, correlatorDataHex);
        }
        catch (error) {
            const _errorMessage = "makeCallByPhoneNumber failure " + (error ? error.message : "");
            this.logger.error("[telephonyService] - callService - " + _errorMessage);
            throw new Error(_errorMessage);
        }
    }

    /* TO DO */
    /*		private makeCallWithMobile(mobileRessource, phoneNumber) {

                var defer = $q.defer();

                if (this.contactService.userContact.mobilePro === phoneNumber || this.contactService.userContact.mobilePerso === phoneNumber) {
                    var errorMessage = "makeCallWithMobile failure: impossible to call its own mobile phone number";
                    this.logger.error("[telephonyService] " + errorMessage);
                    defer.reject(new Error(errorMessage));
                    return defer.promise;
                }

                // Forge request IQ
                var makeMobileCallMsg = $iq({ type: "set", to: mobileRessource })
                    .c("call", { xmlns: "urn:xmpp:call", phoneNumber: phoneNumber, directCall: false });

                this.xmppService.sendIQ(makeMobileCallMsg)
                    .then(() => {
                        defer.resolve();
                    })
                    .catch((error) => {
                        var errorMessageMobile = "makeCallWithMobile failure : " + error.message;
                        this.logger.error("[telephonyService] - callService - " + errorMessageMobile);
                        defer.reject(new Error(errorMessageMobile));
                    });

                // Return the promise
                return defer.promise;
            };
    */

    /**
     * Extract phone info from given called contact and called number
     * @public
     * @param {Contact} contact The contact to call
     * @param {string} phoneNumber The phone number to call
     * @param {any} [correlatorData] Call correlator data
     * @param {any} [correlatorDataHex] Call correlator data hexa
     * @returns {any} The object with computed numbers
     * @instance
     * @function
     */
    public getPhoneInfo(contact: Contact, phoneNumber: string, correlatorData: any = null, correlatorDataHex: any = null): any {

        let longNumber = phoneNumber;
        let shortNumber = "";
        let internalNumber = "";//#29475
        let pbxId = "";
        if (contact) {
            if (phoneNumber === contact.phonePro || phoneNumber === contact.phoneProCan) {
                longNumber = contact.phoneProCan ? contact.phoneProCan : "";
                //if (!longNumber && contact.phonePro) { longNumber = contact.phonePro; }
                shortNumber = contact.phonePbx;
                pbxId = contact.pbxId;
                internalNumber = contact.phoneInternalNumber;//#29475
            }
            else if (phoneNumber === contact.phonePbx || phoneNumber === contact.phoneInternalNumber) {
                longNumber = "";
                shortNumber = contact.phonePbx;
                pbxId = contact.pbxId;
                internalNumber = contact.phoneInternalNumber;//#29475
            }
        }
        return { longNumber, shortNumber, pbxId, internalNumber, correlatorData, correlatorDataHex };//#29475
    }

    /**
     * Build an error message, used by connections snapshot service
     * @private
     * @param {any} data Connections snapshot service response data
     * @param {string} actionLabel context label
     * @returns {string} The built error message
     * @instance
     * @function
     */
    private getErrorMessage(data: any, actionLabel: string): string {
        let errorMessage = actionLabel + " failure : ";

        if ($(data).attr("type") === "error") {

            const error = $(data).find("error");
            if (error) {
                const errorType = error.attr("type");
                const errorCode = error.attr("code");
                if (errorType) {
                    errorMessage += (errorType + " : ");

                    if (errorType === "modify") {
                        errorMessage += error.find("text").text();
                    }
                }
                if (errorCode) {
                    if (errorCode === "503") {
                        errorMessage += "Agent error : service unavailable";
                    }
                }

                this.logger.error("[telephonyService] " + errorMessage);

            }
            else {
                errorMessage += "unknownError";
            }

            return errorMessage;
        }
        return null;
    }

    /**
     * Send the release call service to server
     * Used by SDK (public)
     * Warning when modifying this method
     * @public
     * @param {Call} call Call to relesae
     * @instance
     * @function
     */
    public async releaseCall(call: CallRB): Promise<void> {
        this.logger.info("[telephonyService] [TEL][API] releaseCall " + call.id);
        const promiseId = "releaseCall" + call.id;//only one attempt at a time per service & callId
        if (!this.telAPIPromiseInProgress[promiseId]) {
            this.telAPIPromiseInProgress[promiseId] = new Promise((resolve, reject) => {
                this.innerReleaseCall(call)
                    .then(() => {
                        resolve(null);
                        delete this.telAPIPromiseInProgress[promiseId];
                    })
                    .catch((error) => {
                        reject(error);
                        delete this.telAPIPromiseInProgress[promiseId];
                    });
            });
        } else {
            this.logger.info("[telephonyService] [TEL][API] releaseCall " + call.id + " already engaged then not re-sent to API");
        }
        return this.telAPIPromiseInProgress[promiseId];
    }

    /**
     * Send the release call service to server
     * Warning when modifying this method
     * @private
     * @param {Call} call Call to relesae
     * @returns {Call} The relesaed call
     * @instance
     * @function
     */
    public async innerReleaseCall(call: CallRB): Promise<CallRB> {
        //reject not allowed operations
        if (!this.isBasicCallAllowed) {
            const profileError: any = new Error("innerReleaseCall failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] [TEL][API] " + profileError.message);
            throw profileError;
        }

        //clear a mediapillar call without pbx csta connection : This mediapillar call has been maintained after telephony loss.
        //=> we release the webrtc call of the mediapillar context, because we cannot release the phone call (no csta pbx connection),
        //even if telephony has come back.
        if (call.pbxConnectionDown) {
            if (call.mediaPillarCall?.webrtcCallRef && call.mediaPillarCall.webrtcCallRef.callStatus !== CallStatus.ENDED) {
                //terminate the webrtc call
                this.logger.info("[telephonyService] innerReleaseCall " + call.id + " with pbx connection down : we release the mediapillar web call: " + call.mediaPillarCall.webrtcCallRef);
                this.webrtcP2PService?.releaseCall(call.mediaPillarCall.webrtcCallRef);
            } else {
                this.logger.info("[telephonyService] innerReleaseCall " + call.id + " with pbx connection down : "
                    + (!call.mediaPillarCall ? "no MP context!" : (!call.mediaPillarCall.webrtcCallRef ? "no MP webrtc call to release" : ("no need to release webrtc call with status " + call.mediaPillarCall.webrtcCallRef))));
            }

            //remove pbx call (to clear telephony bar)
            this.logger.info("[telephonyService] [TEL] innerReleaseCall " + call.id + " with pbx connection down : we remove the phone call");
            this.clearCall(call);
            return call;
        }

        //sipWise++
        if (this.isSipWise()) { //sipWise case
            return this.telephonyServiceSipWise.releaseCall(call);
        }
        //sipWise--

        this.logger.info("[telephonyService] [TEL] innerReleaseCall " + call.id);

        try {
            const url = this.portalURL + "calls/" + encodeURIComponent(call.connectionId);
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) {
                throw (response);
            }
            // Update call status
            call.setStatus(CallStatus.ENDED);
            call.startDate = null;
            call.setIsVm(false);
            this.logger.info("[telephonyService] [TEL][API] releaseCall " + call.id + " - success");

            // Send call update event
            call.sendEvent(CallEvents.ON_CALL_UPDATED_EVENT);
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);

            // The call is no more removed from the map because it is needed to stay in this map with ENDED state in some usecases:
            // example of usecase: HOLD event with cause RECALL in some OXE CCD usecases
            // Note that the map of calls is cleared when the telephony starts or restarts after a telephony or network reconnection
            // delete this.calls[call.id];

            return call;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'innerReleaseCall');
            //clean client call not existing on server
            this.deleteCallIfInvalid(errorInfo.error, call);
            this.logger.error(`[telephonyService] [TEL][API] releaseCall ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * release call service to server and notify event to stop ringing/popup, used to decline ringing incoming calls
     * @private
     * @param {Call} call Call to relesae
     * @returns {Promise<void>} Status promise resolved when the call is released
     * @instance
     * @function
     */
    private async releaseCallAndNotify(call: CallRB): Promise<void> {
        try {
            await this.releaseCall(call);
        }
        catch (error) {
            call.setStatus(CallStatus.ENDED);
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
            throw error;
        }
    }

    /**
     * Decline ringing incoming call service
     * @public
     * @param {Call} call Call to decline
     * @returns {Promise<void>} Status promise resolved when the call is declined
     * @instance
     * @function
     */
    public async declineCall(call: CallRB): Promise<void> {
        const webrtcGatewayService = this.mainService.inject(WEBRTC_GATEWAY_SVC);

        //RQRAINB-12845++
        if (config.forceCLOUD_PBX_REJECT_CALL) { this.isCloudPbxRejectCall = true; } //only for integration
        if (this.isSipWise() && this.isCloudPbxRejectCall) { //specific case when feature CLOUD_PBX_REJECT_CALL
            this.logger.info("[telephonyService] decline " + call.id + " feature case CLOUD_PBX_REJECT_CALL");
            this.telephonyServiceSipWise.releaseCall(call, "reject");
            return;
        }//RQRAINB-12845--

        if (this.isSipWise() && !call.isHuntingGroupCallSipWise()) {
            this.deflectCallFollowBusyRule(call);
        }//CRRAINB-16694
        else if (webrtcGatewayService?.isMediaPillarCallSituation(call) && !call.isHuntingGroupCallQueueSipWise()) {
            webrtcGatewayService.releaseCall(call);
        }
        else { this.releaseCallAndNotify(call); }
    }

    /**
     * Delete the call in web client if it doesn't exist on server (after an service error returned by server)
     * @private
     * @param {Error} error received error from the server
     * @param {Call} call Call to check
     * @instance
     * @function
     */
    private deleteCallIfInvalid(error: any, call: CallRB): void {
        //CRRAINB-32581
        if (error && error.portal === "telephony" && (error.errorDetailsCode === 486003 || error.errorDetailsCode === "486003" || error.errorDetailsCode === 487002 || error.errorDetailsCode === "487002")
            && error.message && (error.message.toLowerCase().includes("invalid connection identifier") || error.message.toLowerCase().includes("failed to send releasecall"))
            && call) { // Invalid connection identifier
            this.logger.warn("[telephonyService] deleteCallIfInvalid: delete client call non existing on server: " + call.id);

            call.setStatus(CallStatus.ENDED);
            // Send call update event
            call.sendEvent(CallEvents.ON_CALL_UPDATED_EVENT);
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
            // Clean the call array
            if (this.calls[call.id]) {
                delete this.calls[call.id];
            }
        }
    }

    /**
     * Send the answer Call service request to the server
     * Used by SDK (public)
     * Warning when modifying this method
     * @public
     * @param {Call} call The call to answer
     * @async
     * @returns {Promise<CallRB>} Promise resolved with the answered call
     * @instance
     * @function
     */
    public async answerCall(call: CallRB): Promise<CallRB> {
        this.logger.info("[telephonyService] [TEL][API] answerCall : " + call.id);

        //reject not allowed operations
        if (!this.isBasicCallAllowed) {
            const profileError: any = new Error("answerCall failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        // First hold the current active call
        const activeCall = this.getActiveCall();

        if (this.isMixPstnWebRtcAllowed && !call.webConferenceRoom) {
            //check if any active webrtc call and put it on hold before we make another call
            this.eventService.publish("PUT_ACTIVE_WEBRTC_CALL_OR_CONF_ON_HOLD", { call });
        }

        //call is taken by the current device
        call.isOwner = true;

        //sipWise++
        if (this.isSipWise()) { //sipWise case
            return this.telephonyServiceSipWise.answerCall(call);
        }//sipWise--

        if (call.callStatus === CallStatus.QUEUED_INCOMMING && activeCall && this.isOxe && !this.ccdAgentService.isCcdAgentLogged()) {
            // On Oxe pbx only, in order to answer a queued call on a multiline device, we must first hold the active call before answering the queued call
            try {
                await this.holdCall(activeCall);
            }
            catch (error) {
                //we don't reject if this holdCall service has failed because it is normal on monoline devices
                //Quest: Is it possible to have a queued call on a monoline device ?
                this.logger.warn("[telephonyService] holdCall (before answerCall) failed on callId:" + activeCall.connectionId + " " + error);
            }
            try {
                // We do launch the answerCall request to the server
                return await this.doRequestAnswerCall(call);
            }
            catch (error) {
                const errorMessage = "answerCall failure : " + error.message;
                this.logger.error("[telephonyService] - callService -  " + errorMessage);
                throw new Error(errorMessage);
            }
        }
        else {
            return this.doRequestAnswerCall(call);
        }
    }

    /**
     * Send the answer Call service request to the server
     * @private
     * @param {Call} call The call to answer
     * @async
     * @returns {Promise<CallRB>} Promise resolved with the answered call
     * @instance
     * @function
     */
    private async doRequestAnswerCall(call: CallRB): Promise<CallRB> {
        try {
            const url = this.portalURL + "calls/" + encodeURIComponent(call.connectionId) + "/answer";
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) {
                throw (response);
            }
            const responseBody: any = await response.json();
            const data: any = responseBody.data;
            this.logger.info("[telephonyService] [TEL][API] answerCall - success : " + anonymizer(call.contact.displayName) + " Call (" + call + ")");
            if (this.isOxo) { //RQ6394++
                this.logger.info("[telephonyService] answerCall call id " + call.id + " -- OXO specific mng wait established");
                return call;
            }//RQ6394--
            if (call.callStatus === CallStatus.RINGING_INCOMMING || call.callStatus === CallStatus.QUEUED_INCOMMING) {
                // Update call status
                call.setConnectionId(data.callId);
                call.setStatus(CallStatus.ACTIVE);

                // Send call update event
                call.sendEvent(CallEvents.ON_CALL_UPDATED_EVENT);
                this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
            } else {
                // don't modify call status if its status has changed (if it's no more a ringing incoming call)
                this.logger.info("[telephonyService] answerCall Call (" + call + ") not ringing anymore: don't modify it");
            }
            return call;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'answerCall');
            this.logger.error(`[telephonyService] [TEL][API] doRequestAnswerCall ${errorInfo.message}`);

            // UGLY ERROR MANAGMENT (NO ERROR CODE)
            if (errorInfo.error && errorInfo.error.errorDetails === "Device is not able to answer a call") {
                this.logger.info(`[telephonyService] [TEL][API] doRequestAnswerCall errorDetails ${errorInfo.error.errorDetails}`);
                this.mainService.showGlobalPopupMessage({
                    autoClose: false,
                    okLabel: "close",
                    popupTitle: "warning",
                    popupBody: "deviceDoesntAllowAnswer"
                });
            }

            // clean client call not existing on server
            this.deleteCallIfInvalid(errorInfo.error, call);

            throw errorInfo.error;
        }
    }

    /**
     * Send the hold Call service request to the server
     * Used by SDK (public)
     * Warning when modifying this method
     * @public
     * @param {Call} call The call to put on hold
     * @async
     * @returns {Promise<CallRB>} Promise resolved with the held call
     * @instance
     * @function holdCall
     */
    public async holdCall(call: CallRB): Promise<CallRB> {
        this.logger.info("[telephonyService] [TEL][API] holdCall : callId: " + call.id + " " + call.contact.nameForLogs);

        // Ignore call already hold
        if (!call || call.callStatus === CallStatus.HOLD) {
            return call;
        }

        //sipWise++
        if (this.isSipWise()) { //sipWise case
            return this.telephonyServiceSipWise.holdCall(call);
        }//sipWise--

        try {
            const url = this.portalURL + "calls/" + encodeURIComponent(call.connectionId) + "/hold";
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) {
                throw (response);
            }
            const responseBody: any = await response.json();
            const data: any = responseBody.data;
            this.logger.info("[telephonyService] [TEL][API] holdCall - success : " + anonymizer(call.contact.displayName) + " Call (" + call + ")");
            // Update call status
            call.setConnectionId(data.callId);
            call.setStatus(CallStatus.HOLD);

            // Send call update event
            call.sendEvent(CallEvents.ON_CALL_UPDATED_EVENT);
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);

            return call;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'holdCall');
            this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Send the retrieve Call service request to the server
     * Used by SDK (public)
     * Warning when modifying this method
     * @public
     * @param {Call} call The held call to retrieve
     * @async
     * @returns {Promise<CallRB>} Promise resolved with the held call
     * @instance
     * @function retrieveCall
     */
    public async retrieveCall(call: CallRB): Promise<CallRB> {
        this.logger.info("[telephonyService] [TEL][API] retrieveCall callId: " + call.id + " " + call.contact.nameForLogs);

        if (this.isMixPstnWebRtcAllowed) {
            //check if any active webrtc call and put it on hold before we retrieve that one
            this.eventService.publish("PUT_ACTIVE_WEBRTC_CALL_OR_CONF_ON_HOLD");
        }

        // First hold the current active call
        const activeCall = this.getActiveCall();

        if (activeCall) {
            try {
                try {
                    await this.holdCall(activeCall);
                } catch (error) {
                    if (this.isOxe && error?.errorDetailsCode === 487002) {
                        // hold call service has been rejected : it may be a monoline set
                        // try to invoke an alternateCall service (available on Oxe only)
                        await this.alternateCall(activeCall, call);
                        return call;
                    }
                    throw error;
                }
                return await this.retrieveCall(call);
            }
            catch (error) {
                const errorMessage = "retrieveCall failure : " + error.message;
                this.logger.error("[telephonyService] [TEL][API] - retrieveCall -  " + errorMessage);
                throw new Error(errorMessage);
            }
        }
        else {
            //sipWise++
            if (this.isSipWise()) { //sipWise case
                return this.telephonyServiceSipWise.retrieveCall(call);
            }//sipWise--

            try {
                //telephony starting or started means that the user is attached to a pbx device
                const url = this.portalURL + "calls/" + encodeURIComponent(call.connectionId) + "/retrieve";
                const headers = this.authService.getPostHeader();
                const response: Response = await fetch(url, { method: 'PUT', headers });
                if (!response.ok) {
                    throw (response);
                }
                const responseBody: any = await response.json();
                const data: any = responseBody.data;
                this.logger.info("[telephonyService] [TEL][API] retrieveCall - success : " + anonymizer(call.contact.displayName) + " Call (" + call + ")");
                // Update call status
                call.setConnectionId(data.callId);
                call.setStatus(CallStatus.ACTIVE);

                // Send call update event
                call.sendEvent(CallEvents.ON_CALL_UPDATED_EVENT);
                this.eventService.publish("ON_CALL_UPDATED_EVENT", call);

                //attach the audio for this call
                const mediaPillarCall = call.getMediaPillarCall();
                if (mediaPillarCall?.webrtcCallRef) {
                    this.webrtcP2PService?.attachDistantMediaStreams(true, mediaPillarCall?.webrtcCallRef);
                }

                return call;
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveCall');
                this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        }
    }

    /**
     * Send the alternate Call service request to the server
     * Used by SDK (public)
     * Warning when modifying this method
     * @public
     * @param {Call} activeCall The active call to alternate
     * @param {Call} heldCall The held call to alternate
     * @async
     * @returns {Promise<void>} Status promise resolved when the alternate service is done
     * @instance
     * @function alternateCall
     */
    public async alternateCall(activeCall: CallRB, heldCall: CallRB): Promise<void> {
        // Ignore wrong request
        if (!activeCall || !heldCall) { return; }

        //reject not allowed operations
        if (!this.isSecondCallAllowed) {
            const profileError: any = new Error("alternateCall failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        this.logger.info("[telephonyService] [TEL][API] alternateCall held(" + heldCall.contact.displayName + ") to active(" + activeCall.contact.displayName + ")");

        try {
            const url = this.portalURL + "calls/" + encodeURIComponent(activeCall.connectionId) + "/alternate/" + encodeURIComponent(heldCall.connectionId);
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            this.logger.info("[telephonyService] [TEL][API] alternateCall - success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'alternateCall');
            this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Deflect Call to voicemail service : request to the server with a voicemail destination (and stop ringing if MP call)
     * Used by SDK (public)
     * Warning when modifying this method
     * @public
     * @param {Call} call The incoming call to deflect
     * @async
     * @returns {Promise<void>} Status promise resolved when the call is deflected to the voicemail
     * @instance
     * @function deflectCallToVM
     */
    public async deflectCallToVM(call: CallRB): Promise<void> {
        this.logger.info("[telephonyService] [TEL][API] -- deflectCallToVM -- " + call.id + " " + call.contact.nameForLogs);

        try {
            await this.innerDeflectCallToVM(call);
        }
        finally {
            if (call.callType === CallType.PHONE) {
                const mediaPillarCall = call.getMediaPillarCall();
                if (mediaPillarCall?.webrtcCallRef && mediaPillarCall.telephonyCallRefs.length <= 1) {
                    this.webrtcP2PService?.terminateCall(mediaPillarCall.webrtcCallRef);
                }
                call.setStatus(CallStatus.ENDED);
                this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
            }
        }
    }

    /**
     * Send the deflect Call service request to the server with a voicemail destination
     * @private
     * @param {Call} call The incoming call to deflect
     * @async
     * @returns {Promise<void>} Status promise resolved when the call is deflected to the voicemail
     * @instance
     * @function innerDeflectCallToVM
     */
    private async innerDeflectCallToVM(call: CallRB): Promise<void> {
        // Ignore wrong request
        if (!call) { return; }

        //reject not allowed operations
        if (!this.isDeflectCallAllowed) {
            const profileError: any = new Error("deflectCall failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        this.logger.info("[telephonyService] innerDeflectCallToVM " + call.contact.nameForLogs);

        //sipWise++
        if (this.isSipWise()) { //sipWise case
            await this.telephonyServiceSipWise.deflectCall(call, this.voicemailNumber);
            return;
        }//sipWise--

        try {
            const url = this.portalURL + "calls/" + encodeURIComponent(call.connectionId) + "/deflect";
            const headers = this.authService.getPostHeader();
            const data = { calleeExtNumber: "", calleeIntNumber: "VOICEMAILBOX", calleeShortNumber: this.voicemailNumber, calleePbxId: this.pbxId };
            const body = JSON.stringify(data);
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            this.logger.info("[telephonyService] [TEL][API] deflectCall - success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'innerDeflectCallToVM');
            this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Send the deflect Call service request to the server with a phone number as destination
     * SipWise only
     * Used by SDK (public)
     * Warning when modifying this method
     * @public
     * @param {Call} call The active call to deflect
     * @param {string} number phone number of the deflect destination
     * @async
     * @returns {Promise<void>} Status promise resolved when the call is deflected
     * @instance
     * @function deflectCallToNumber
     */
    public async deflectCallToNumber(call: CallRB, number: string): Promise<void> {
        // Ignore wrong request
        if (!call || !number) { return; }

        //reject not allowed operations
        let profileError: any;
        if (!this.isDeflectCallAllowed) {
            profileError = new Error("deflectCall failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        this.logger.info("[telephonyService] [TEL][API] deflectCallToNumber " + call.contact.nameForLogs);

        if (this.isSipWise()) { //sipWise case
            await this.telephonyServiceSipWise.deflectCall(call, number);
            return;
        }

        try {
            const url = this.portalURL + "calls/" + encodeURIComponent(call.connectionId) + "/deflect";
            const headers = this.authService.getPostHeader();
            const data = { calleeExtNumber: number, calleeIntNumber: "", calleeShortNumber: "", calleePbxId: "" };
            const body = JSON.stringify(data);
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            this.logger.info("[telephonyService] [TEL][API] deflectCallToNumber - success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deflectCallToNumber');
            this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async deflectCallToContact(call: CallRB, contact: Contact): Promise<void> {
        // Ignore wrong request
        if (!call || !contact) { return; }

        //reject not allowed operations	(note : isDeflectCallAllowed is also for non VM deflect	)
        let profileError: any;
        if (!this.isDeflectCallAllowed) {
            profileError = new Error("deflectCallToContact failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        this.logger.info("[telephonyService] [TEL][API] deflectCallToContact " + call.contact.nameForLogs);

        if (this.isSipWise()) { //sipWise case
            await this.telephonyServiceSipWise.deflectCall(call, contact.phoneInternalNumber || contact.phoneProCan);
            return;
        }

        try {
            const url = this.portalURL + "calls/" + encodeURIComponent(call.connectionId) + "/deflect";
            const headers = this.authService.getPostHeader();
            const data = { calleeExtNumber: contact.phoneProCan, calleeIntNumber: contact.phoneInternalNumber, calleeShortNumber: contact.phonePbx, calleePbxId: contact.pbxId };
            const body = JSON.stringify(data);
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            this.logger.info("[telephonyService] [TEL][API] deflectCallToContact - success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deflectCallToContact');
            this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Send the deflect Call service request to the server according the forward busy rule
     * SipWise only
     * Used by SDK (public)
     * Warning when modifying this method
     * @public
     * @param {Call} call The active call to deflect
     * @async
     * @returns {Promise<void>} Status promise resolved when the call is deflected
     * @instance
     * @function deflectCallFollowBusyRule
     */
    public async deflectCallFollowBusyRule(call): Promise<void> {
        // Ignore wrong request
        if (!call) { return; }

        const busyForward = this.getForwardObject().busy;

        if (busyForward?.activate && busyForward.number) {
            this.deflectCallToNumber(call, busyForward.number);
        }
        else {
            this.deflectCallToVM(call);
        }

        this.logger.info("[telephonyService] [TEL][API] deflectCallFollowBusyRule");

    }

    /**
     * Send the transfer Call service request (transfer between 2 existing calls)
     * Used by SDK (public)
     * Warning when modifying this method
     * @public
     * @param {Call} activeCall The active call to transfer: this call may have a status ACTIVE or RINGING_OUTGOING
     * @param {string} heldCall The held call to transfer
     * @async
     * @returns {Promise<void>} Status promise resolved when the calls are transferred
     * @instance
     * @function transfertCall
     */
    public async transfertCall(activeCall: CallRB, heldCall: CallRB): Promise<void> {
        // Ignore wrong request
        if (!activeCall || !heldCall) { return; }

        //reject not allowed operations
        if (!this.isTransferAllowed) {
            const profileError: any = new Error("transferCall failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        this.logger.info("[telephonyService] [TEL][API] transfertCall (" + heldCall.contact.displayName + ") to active(" + activeCall.contact.displayName + ")");

        //sipWise++
        if (this.isSipWise()) { //sipWise case
            await this.telephonyServiceSipWise.transferCall(activeCall, heldCall);
            return;
        }//sipWise--

        try {
            const url = this.portalURL + "calls/" + encodeURIComponent(activeCall.connectionId) + "/transfer/" + encodeURIComponent(heldCall.connectionId);
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            this.logger.info("[telephonyService] [TEL][API] transferCall - success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'transfertCall');
            this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Send the blind transfer Call service request (transfer 1 existing active call to a destination number)
     * SipWise only
     * Used by SDK (public)
     * Warning when modifying this method
     * @public
     * @param {Call} activeCall The active call to transfer: this call must have a status ACTIVE
     * @param {string} calleeNumber The destination number of the transfer
     * @param {any} [destionationObject] Destination object containing the properties "userId" and "resource",
     * which identify the destination user identifier and xmpp client resource
     * @param {Contact} [contact] destination contact (used for hybrid systems)
     * @async
     * @returns {Promise<void>} Status promise resolved when the active call is transferred
     * @instance
     * @function blindTransfertCall
     */
    public async blindTransfertCall(activeCall: CallRB, calleeNumber: string, destionationObject: any = undefined, contact: Contact = undefined): Promise<void> {
        if (!activeCall || (!calleeNumber && !destionationObject && !contact)) {
            throw new Error("[telephonyService] blindTransfertCall -- missing parameters");
        }

        if ((calleeNumber || contact) && !this.isTransferAllowed) {
            const profileError: any = new Error("[telephonyService] blind transfertCall failure - Transfer Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        if (destionationObject && !this.isMixPstnWebRtcAllowed) {
            const profileError: any = new Error("[telephonyService] blind transfertCall failure - Mix WEBRTC_PSTN Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        this.logger.info("[telephonyService] [TEL][API] blindTransferCall : activeCall : " + activeCall.id + " to destination: " + calleeNumber);

        if (this.isSipWise()) {
            return this.telephonyServiceSipWise.blindTransferCall(activeCall, calleeNumber, destionationObject);
        }

        // Hybrid system
        if (activeCall.callStatus !== CallStatus.ACTIVE) {
            const error: any = new Error("[telephonyService] [TEL][API] blind transfertCall failure - call not active");
            error.status = error.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + error.message);
            throw error;
        }

        let consultCall: CallRB;
        try {
            if (!contact) {
                contact = await this.contactService.getOrCreateContact(null, calleeNumber);
            }
            this.logger.debug("[telephonyService] blindTransferCall : make consultation call with activeCall : " + activeCall.id + " to destination: " + calleeNumber);
            const consultCallId: string = await this.makeConsultationCall(contact, calleeNumber, activeCall.id, undefined, undefined, true); //true to hide the consultation call
            consultCall = this.calls[consultCallId];

            this.logger.debug("[telephonyService] blindTransferCall : wait ringing state of consultation call : " + consultCallId);
            await this.waitCallState(consultCall, [CallStatus.RINGING_OUTGOING, CallStatus.QUEUED_OUTGOING, CallStatus.ACTIVE]);

            this.logger.debug("[telephonyService] blindTransferCall : transfer the calls " + activeCall.id + " and " + consultCallId);
            await this.transfertCall(consultCall, activeCall); return;
        } catch (error) {
            this.logger.info("[telephonyService] [TEL][API] blindTransferCall : error" + error);
            if (consultCall) {
                this.logger.debug("[telephonyService] [TEL][API] blindTransferCall : error release consultation call " + consultCall.id);
                // if the transfer has failed, we release the possible consultation call created
                this.releaseCall(consultCall);
            }
            throw error;
        }
    }

    /**
     * Send the conference Call service request to the server (conferencing 2 existing calls of the current user:
     * this is a 3 parties conference with the current user and the 2 other call parties)
     * In case of RB Hub, the active call can also be a WEBRTC call (or PSTN), and the held can also be a WEBRTC call (or PSTN); One of both calls should be PSTN !
     * NB ! In case of RB Hub, we need to have the Web Conference Service and the Room / Bubble service included the project
     * @public
     * @param {Call} activeCall The active call to conference: this call must have a status ACTIVE
     * @param {Call} heldCall The held call to conference
     * @async
     * @returns {Promise<any>} Status promise resolved when the calls are conferenced; In case of RB Hub confrence call, the promise will return the bubble where the conference is started
     * @instance
     * @function conferenceCall
     */
    public async conferenceCall(activeCall: CallRB, heldCall: CallRB): Promise<any> {
        // Ignore wrong request
        if (!activeCall || !heldCall) { return undefined; }

        //reject not allowed operations
        if (!this.isConferenceAllowed) {
            const profileError: any = new Error("conferenceCall failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        this.logger.info("[telephonyService] [TEL][API] conferenceCall " + activeCall.connectionId + " (" + activeCall.contact.displayName + ") and " + heldCall.connectionId + " (" + heldCall.contact.displayName + ")");

        //sipWise++
        if (this.isSipWise()) { //sipWise case
            return this.conferenceCallSipWise(activeCall, heldCall);
        }//sipWise--

        try {
            const url = this.portalURL + "calls/" + encodeURIComponent(activeCall.connectionId) + "/conference/" + encodeURIComponent(heldCall.connectionId);
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            this.logger.info("[telephonyService] [TEL][API] conferenceCall - success");
            return undefined;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'conferenceCall');
            this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Make an adhoc conference call with 3 participants (for RB Hub case)
     * The call could be 2 PSTN calls, or 1 PSTN and 1 WEBRTC call
     * @private
     * @param {Call} activeCall The active call to conference: this call must have a status ACTIVE (it could be a PSTN call or WebRTC call)
     * @param {Call} heldCall The held call to conference (it could be a PSTN call or WebRTC call)
     * @async
     * @returns {Promise<any>} The room / bubble where the conference is started
     * @instance
     * @function conferenceCallSipWise
     */
    private async conferenceCallSipWise(activeCall: CallRB, heldCall: CallRB): Promise<any> {
        try {
            if (!this.isMixPstnWebRtcAllowed) {
                throw new Error("conferenceCallSipWise failure - Not Allowed -- missing feature flag");
            }

            const roomService: BubbleServiceRB = this.mainService.inject<BubbleServiceRB>(BUBBLE_SVC);
            if (!roomService) {
                throw new Error("conferenceCallSipWise failure - Room / Bubble service is missing");
            }

            const WebConferenceService: BubbleConferenceServiceRB = this.mainService.inject(BUBBLE_CONFERENCE_SVC);
            if (!WebConferenceService) {
                throw new Error("conferenceCallSipWise failure - Web conference service is missing");
            }

            this.logger.info(`[telephonyService] conferenceCallSipWise`);

            const caller = { userId: this.contactService.userContact.dbId };
            const particpants = [];

            let activeCallBlindTransfer = false;
            let heldCallBlindTransfer = false;

            //manage first call participant
            const firstParticipant = roomService.getParticipantDataForAdhocFromCall(activeCall);
            particpants.push(firstParticipant.participant);
            activeCallBlindTransfer = firstParticipant.blindTrasfer;

            //manage second call participant
            const secondParticipant = roomService.getParticipantDataForAdhocFromCall(heldCall);
            particpants.push(secondParticipant.participant);
            heldCallBlindTransfer = secondParticipant.blindTrasfer;

            const adhocData = await roomService.startAdHocConference(caller, particpants);
            this.logger.info(`[telephonyService] conferenceCallSipWise -- ad hoc started ${JSON.stringify(adhocData)}`);

            let room = roomService.getBubbleFromCache(adhocData.roomId);
            if (!room?.isBubbleConferenceStarted()) {
                room = await roomService.getServerBubble(adhocData.roomId);
            }
            const webrtcCallId = (activeCall.callType === CallType.WEBRTC) ? activeCall.id : ((heldCall.callType === CallType.WEBRTC) ? heldCall.id : null);
            const session = this.xmppService.connection.jingle.sessions[webrtcCallId];

            //send refer status to both calls
            //in case of webrtc call or PSTN call in computer mode with person from same company, send IQ REFER message
            //otherwise make blindTransfer to the bubble for that user
            await WebConferenceService.transferP2pCallToConference(session, adhocData.jingleJid, room as BubbleRB, true, activeCall.isMuted);

            const activeCallBlindTransferData = (adhocData?.gatewayPrefix && adhocData.participantGatewaySessionIds) ? adhocData.gatewayPrefix + adhocData.participantGatewaySessionIds[0] : "";
            const heldCallBlindTransferData = (adhocData?.gatewayPrefix && adhocData.participantGatewaySessionIds) ? adhocData.gatewayPrefix + adhocData.participantGatewaySessionIds[1] : "";
            const nameSpaceRefer = 'urn:xmpp:jingle:apps:rtp:info:1';//new nameSpace

            if (activeCallBlindTransfer) {
                this.logger.info(`[telephonyService] conferenceCallSipWise -- activeCallBlindTransfer ${activeCallBlindTransferData}`);
                //callee is sip then perform a sipWise blindTransfer (current call to SFU access for callee)
                this.blindTransfertCall(activeCall, activeCallBlindTransferData);
            }
            else if (activeCall.callType === CallType.PHONE) {
                this.logger.info(`[telephonyService] conferenceCallSipWise -- activeCall invite via IQ refer`);
                //Ask to callee to join SFU but not with IQ
                const id = "web_" + crypto.randomUUID();
                this.xmppService.send($msg({ from: this.contactService.userContact.fullJid, to: activeCall.contact.jid, id })
                    .c('refer', {
                        xmlns: nameSpaceRefer,
                        confEndpointId: room.dbId,
                        referTo: adhocData.jingleJid,
                        telCallId: activeCall.id
                    }));
            }
            else if (activeCall.callType === CallType.WEBRTC) {
                //P2P case, send standard refer
                this.webrtcP2PService?.referTo(session, room.dbId, adhocData.jingleJid);
            }

            //same for call on HOLD
            if (heldCallBlindTransfer) {
                //callee is sip then perform a sipWise blindTransfer (current call to SFU access for callee)
                //if we've already done blind transfer for the active call, add some delay before doing the second blind transfer as sipwise crash
                if (activeCallBlindTransfer) {
                    //add local settings to disable this
                    if (localStorage?.getItem("disableBlindTransferTimer") === "true") {
                        this.logger.info(`[telephonyService] conferenceCallSipWise -- heldCallBlindTransfer ${heldCallBlindTransfer} without timeout`);
                        this.blindTransfertCall(heldCall, heldCallBlindTransferData);
                    }
                    else {
                        setTimeout(() => {
                            this.logger.info(`[telephonyService] conferenceCallSipWise -- heldCallBlindTransfer ${heldCallBlindTransfer} after timeout`);
                            this.blindTransfertCall(heldCall, heldCallBlindTransferData);
                        }, 1000);
                    }
                }
                else {
                    this.logger.info(`[telephonyService] conferenceCallSipWise -- heldCallBlindTransfer ${heldCallBlindTransfer}`);
                    this.blindTransfertCall(heldCall, heldCallBlindTransferData);
                }
            }
            else if (heldCall.callType === CallType.PHONE) {
                this.logger.info(`[telephonyService] conferenceCallSipWise -- heldCall invite via IQ refer`);
                //Ask to callee to join SFU but not with IQ
                const id = "web_" + crypto.randomUUID();
                this.xmppService.send($msg({ from: this.contactService.userContact.fullJid, to: heldCall.contact.jid, id })
                    .c('refer', {
                        xmlns: nameSpaceRefer,
                        confEndpointId: room.dbId,
                        referTo: adhocData.jingleJid,
                        telCallId: heldCall.id
                    }));
            }
            else if (heldCall.callType === CallType.WEBRTC) {
                //P2P case, send standard refer
                this.webrtcP2PService?.referTo(session, room.dbId, adhocData.jingleJid);
            }

            return room;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'conferenceCallSipWise');
            this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Configure the sipwise forward to a device
     * @public
     * @param {string} phoneNumber phonenumber on which the call will be forwarded
     * @param {string} destType  Type of destination (internalNumber, externalNumber)
     * @param {string} forwardType Type of forward (immediate, busy, noreply)
     * @param {string} destId destination Id (if available, a contact or a HG Id) to manage the forward to welcome case
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function sipWiseForwardToDevice
     */
    public async sipWiseForwardToDevice(phoneNumber: string, destType: string, forwardType: string, destId?: string): Promise<void> {
        this.logger.info("[telephonyService] [TEL][API] sipWiseforwardToDevice : " + phoneNumber + " / destType = " + destType + " / fwType = " + forwardType);
        //reject not allowed operations
        if (!this.isForwardEnabled) {
            const profileError: any = new Error("sipWiseforwardToDevice failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }
        if (this.contactService.userContact.phonePro === phoneNumber || this.contactService.userContact.phoneProCan === phoneNumber || this.contactService.userContact.phonePbx === phoneNumber) {
            const errorMessage = "sipWiseforwardToDevice failure: impossible to forward its own phone number";
            this.logger.error("[telephonyService] " + errorMessage);
            throw new Error(errorMessage);
        }
        if (!phoneNumber || !forwardType || forwardType === "" || !destType || destType === "") {
            const errorMessage = "sipWiseforwardToDevice failure: bad parameters";
            this.logger.error("[telephonyService] " + errorMessage);
            throw new Error(errorMessage);
        }
        if (this.isSipWise()) { //only sipWise case
            try {
                const welcomeDestref = destId ? destId : null; //RQRAINB-9750
                await this.telephonyServiceSipWise.forwardToDeviceWithWelcome(welcomeDestref, phoneNumber, destType, forwardType);//RQRAINB-9750
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'sipWiseForwardToDevice');
                this.logger.error(`[telephonyService] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        } else {
            const errorMessage = "sipWiseforwardToDevice not implemented if not sipWise case";
            this.logger.error("[telephonyService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Cancel the sipwise forward to a device
     * @public
     * @param {string} forwardType Type of forward (immediate, busy, noreply)
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function sipWiseCancelForward
     */
    public async sipWiseCancelForward(forwardType: string): Promise<void> {
        this.logger.info("[telephonyService] [TEL][API] sipWiseCancelForward :  fwType = " + forwardType);
        //reject not allowed operations
        if (!this.isForwardEnabled) {
            const profileError: any = new Error("cancelForward failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }
        if (this.isSipWise()) { //only sipWise case
            try {
                await this.telephonyServiceSipWise.cancelForward(forwardType);
                return;
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'sipWiseCancelForward');
                this.logger.error(`[telephonyService] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        } else {
            const errorMessage = "sipWiseCancelForward not implemented if not sipWise case";
            this.logger.error("[telephonyService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * Configure forward to a device
     * @public
     * @param {string} phoneNumber phonenumber on which the call will be forwarded
     * @param {string} forwardType type of forward (immediate, busy, noreply)
     * @param {string} destId  the (optional) dest Id (a contact Id or HG id) in order to manage welcome forward
     * @param {Contact} [user] user (optional) for which the forward is modified (used by supervisors only). If not set, the user modified is the logged user
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function forwardToDevice
     */
    public async forwardToDevice(phoneNumber: string, forwardType: string, destId?: string, user?: Contact): Promise<void> {
        this.logger.info("[telephonyService] [TEL][API] forwardToDevice : " + phoneNumber + " forwardType :" + forwardType);

        //reject not allowed operations
        if (!this.isForwardEnabled) {
            const profileError: any = new Error("forwardToDevice failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        if (this.contactService.userContact.phonePro === phoneNumber || this.contactService.userContact.phoneProCan === phoneNumber || this.contactService.userContact.phonePbx === phoneNumber) {
            const errorMessage = "forwardToDevice failure: impossible to forward its own phone number";
            this.logger.error("[telephonyService] " + errorMessage);
            throw new Error(errorMessage);
        }

        //sipWise++
        if (this.isSipWise()) { //sipWise case
            try {
                let contact: Contact = this.contactService.getContactFromCacheContainsPhoneNumber(phoneNumber);
                if (!contact) {
                    contact = await this.contactService.getOrCreateContact(null, phoneNumber);
                }
                const phoneInfo = this.getPhoneInfo(contact, phoneNumber);
                let destType = "externalNumber";//as default
                forwardType = "immediate"; //as default
                let welcomeDestRef = null;
                if (destId) { //RQRAINB-9750 destId explicitely given
                    welcomeDestRef = destId;
                    destType = "internalNumber";
                    phoneNumber = phoneInfo.shortNumber;
                } else { //destId not provided only number
                    if (phoneInfo.longNumber !== "") {
                        destType = "externalNumber";
                        phoneNumber = phoneInfo.longNumber;
                    } else {
                        destType = "internalNumber";
                        phoneNumber = phoneInfo.shortNumber;
                        welcomeDestRef = contact.id; //RQRAINB-9750 the contactId (user or HG) to search the welcome service
                    }
                }
                await this.telephonyServiceSipWise.forwardToDeviceWithWelcome(welcomeDestRef, phoneNumber, destType, forwardType, user?.dbId);//RQRAINB-9750
                return;
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'forwardToDevice');
                this.logger.error(`[telephonyService] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        }//sipWise--

        try {
            let contact: Contact = this.contactService.getContactFromCacheContainsPhoneNumber(phoneNumber);
            if (!contact) {
                contact = await this.contactService.getOrCreateContact(null, phoneNumber);
            }

            const phoneInfo = this.getPhoneInfo(contact, phoneNumber);
            forwardType = forwardType ? forwardType : "immediate"; //as default

            let url = "";
            if (forwardType !== "overflow_to_associated") { //standart case
                url = user ? (this.portalURL + "admin/forward/" + user.jidtel) : (this.portalURL + "forward");
            } else { //RQRAINB-8850 specific OXE immediate overflow when Rainbow client not reachable
                url = this.portalURL + "forwardOverflow";
            }
            const headers = this.authService.getPostHeader();
            const data = {
                calleeExtNumber: phoneInfo.longNumber,
                calleeIntNumber: phoneInfo.internalNumber,
                calleeShortNumber: phoneInfo.shortNumber,
                calleePbxId: phoneInfo.pbxId,
                calleeDisplayName: contact.displayName,
                forwardType: forwardType
            };
            const body = JSON.stringify(data);
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            this.logger.info("[telephonyService] [TEL][API] forwardToDevice - success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'forwardToDevice');
            this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Configure the forward to the Voicemail
     * @public
     * @param {string} [forwardType] Type of forward (immediate, busy, noreply)
     * @param {Contact} [user] user for which the forward is modified (used by supervisors only). If not set, the user modified is the logged user
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function forwardToVoicemail
     */
    public async forwardToVoicemail(forwardType?: string, user?: Contact): Promise<void> {

        this.logger.info("[telephonyService] [TEL][API] forwardToVoicemail forwardType :" + forwardType);

        if (!this.voiceMailFeatureEnabled || !this.isForwardEnabled) {
            const profileError: any = new Error("forwardToVoicemail failure - voicemail/forward feature not enabled");
            //profileError.status = profileError.errorDetailsCode = "404";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        forwardType = forwardType ? forwardType : "immediate"; //as default
        //sipWise++
        if (this.isSipWise()) { //sipWise case
            const destType = "voicemail";
            try {
                await this.telephonyServiceSipWise.forwardToDeviceWithWelcome(null, this.voicemailNumber, destType, forwardType, user?.dbId);//RQRAINB-9750
                return;
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'forwardToVoicemail');
                this.logger.error(`[telephonyService] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        }//sipWise--

        try {
            const url = user ? (this.portalURL + "admin/forward/" + user.jidtel) : (this.portalURL + "forward");
            const headers = this.authService.getPostHeader();
            const data = {
                calleeExtNumber: "",
                calleeIntNumber: "VOICEMAILBOX",
                calleePbxId: this.pbxId,
                forwardType: forwardType
            };
            const body = JSON.stringify(data);
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            this.logger.info("[telephonyService] [TEL][API] forwardToVoicemail - success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'forwardToVoicemail');
            this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Cancel the forward
     * @public
     * @param {Contact} [user] user for which the forward is cancelled (used by supervisors only). If not set, the user modified is the logged user
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function cancelForward
     */
    public async cancelForward(user?: Contact): Promise<void> {

        this.logger.info("[telephonyService] [TEL][API] cancelForward");

        //reject not allowed operations
        if (!this.isForwardEnabled) {
            const profileError: any = new Error("cancelForward failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        //sipWise++
        if (this.isSipWise()) { //sipWise case
            const forwardType = "immediate"; //as default
            try {
                await this.telephonyServiceSipWise.cancelForward(forwardType, user?.dbId);
                return;
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'cancelForward');
                this.logger.error(`[telephonyService] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        }//sipWise--

        if (this.contactService.userContact.phonePbx || user) {
            try {
                const url = user ? (this.portalURL + "admin/forward/" + user.jidtel) : (this.portalURL + "forward");
                const headers = this.authService.getPostHeader();
                const data = {
                    forwardType: "no_forward"
                };
                const body = JSON.stringify(data);
                const response: Response = await fetch(url, { method: 'PUT', headers, body });
                if (!response.ok) throw response;
                this.logger.info("[telephonyService] [TEL][API] cancelForward - success");
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'cancelForward');
                this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        }
    }

    /**
     * Cancel the forwardOnCloudIssue
     * @public
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function cancelForwardOnCloudIssue
     */
    public async cancelForwardOnCloudIssue(): Promise<void> { //RQRAINB-8850

        this.logger.info("[telephonyService] [TEL][API] cancelForwardOnCloudIssue");

        //reject not allowed operations ???? TO BE CHECKED IF profile is to be controled
        if (!this.isForwardEnabled) {
            const profileError: any = new Error("cancelForward failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        if (this.contactService.userContact.phonePbx) {
            try {
                const url = this.portalURL + "forwardOverflow";
                const headers = this.authService.getPostHeader();
                const data = {
                    forwardType: "no_forward",
                    pbxForwardType: "overflow_to_associated"
                };
                const body = JSON.stringify(data);
                const response: Response = await fetch(url, { method: 'PUT', headers, body });
                if (!response.ok) throw response;
                this.logger.info("[telephonyService] [TEL][API] cancelForwardOnCloudIssue - success");
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'cancelForwardOnCloudIssue');
                this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        }
    }

    /**
     * To know the forward Status
     * @public
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function getForwardStatus
     */
    public async getForwardStatus(): Promise<void> {

        //reject not allowed operations
        if (!this.isForwardEnabled) {
            const profileError: any = new Error("getForwardStatus failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        if (this.starting || this.started) { //telephony starting or started means that the user is attached to a pbx device
            //sipWise++
            if (this.isSipWise()) {
                try {
                    await this.telephonyServiceSipWise.getForwardStatus();
                    return;
                }
                catch (error) {
                    const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getForwardStatus');
                    this.logger.error(`[telephonyService] ${errorInfo.message}`);
                    throw errorInfo.error;
                }
            }//sipWise--

            this.logger.info("[telephonyService] [TEL][API] getForwardStatus");

            try { //telephony starting or started means that the user is attached to a pbx device
                let url = this.portalURL + "forward";
                let headers = this.authService.getRequestHeader();
                let response: Response = await fetch(url, { method: 'GET', headers });
                if (!response.ok) {
                    throw (response);
                }
                let responseBody: any = await response.json();
                let data: any = responseBody.data;
                if (data) {
                    this.eventService.publish("ON_CALL_FORWARDED_EVENT", {
                        forwardType: data.forwardType,
                        forwardTo: data.pbxForwardType !== "do_not_disturb" ? data.forwardTo : "",
                        pbxForwardType: data.pbxForwardType
                    });

                    this.forwardObject.forwardType = data.forwardType;
                    this.forwardObject.forwardTo = data.pbxForwardType !== "do_not_disturb" ? data.forwardTo : "";
                    this.forwardObject.pbxForwardType = data.pbxForwardType;

                    this.logger.info("[telephonyService] getForwardStatus - success - forwardType: " + data.forwardType + " forwardTo: " + data.forwardTo + " pbxForwardType: " + data.pbxForwardType);
                }
                //RQRAINB-8850++
                if (this.isOxe && (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_OXE_OVERFLOW) || this.settingsService.getBooleanSetting("forwardOnCloudIssue"))) {
                    url = this.portalURL + "forwardOverflow";
                    headers = this.authService.getRequestHeader();
                    response = await fetch(url, { method: 'GET', headers });
                    if (!response.ok) {
                        throw (response);
                    }
                    responseBody = await response.json();
                    data = responseBody.data;

                    if (data && data.pbxForwardType === "overflow_to_associated") {
                        this.forwardObject.forwardOverflowType = data.forwardType;
                        this.forwardObject.forwardOverflowTo = data.forwardTo;
                        this.forwardObject.pbxForwardOverflowType = data.pbxForwardType;
                        this.eventService.publish("ON_CALL_FORWARDOVERFLOW_EVENT", {
                            forwardOverflowType: data.forwardType,
                            forwardOverflowTo: data.forwardTo,
                            pbxForwardOverflowType: data.pbxForwardType
                        });
                        this.logger.info("[telephonyService] getForwardOverflowStatus - success - forwardOverflowType: " + data.forwardOverflowType + " forwardOverflowTo: " + data.forwardOverflowTo + " pbxForwardOverflowType: " + data.pbxForwardOverflowType);
                    }
                }
                //RQRAINB-8850--
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getForwardStatus');
                this.logger.error(`[telephonyService] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        }
        else {
            throw new Error("getForwardStatus failure - Telephony not started");
        }
    }

    /**
     * Configure the sipwise Forward to a device
     * @public
     * @returns {any} return the forward object which defines the forward status
     * @instance
     * @function getForwardObject
     */
    public getForwardObject(): any {
        return this.forwardObject;
    }

    public isImmediateForwardActivated(): boolean {
        return this.forwardObject?.pbxForwardType === "immediate" && this.forwardObject?.forwardType === "Activation";
    }

    /**
     * For sipwise only
     * return the welcome Id associated to the targetId (a contact or HG)
     * @param targetId
     * @public
     * @async
     * @returns {Promise<any>} return the welcomeId or null
     * @instance
     * @function getSipWiseWelcomeInfo
     */
    public async getSipWiseWelcomeInfo(targetId: string): Promise<any> { //RQRAINB-9750

        if (this.isSipWise() && targetId) { //sipWise case
            let result: string = null;
            const response = await this.telephonyServiceSipWise.getWelcomeInfo(targetId);
            if (response?.length !== 0) {
                result = response[0].id; //as default we use the 1st welcome id
            }
            return result;
        }
        //Otherwise
        this.logger.info("[telephonyService] getSipWiseWelcomeInfo : Not sipWise or missing targetId " + targetId);
        return null;
    }

    /**
     * return if the overflow is activated to the voicemail
     * @public
     * @async
     * @returns {Promise<boolean>} return if the overflow is activated
     * @instance
     * @function isOverFlowStatusActivated
     */
    public async isOverFlowStatusActivated(): Promise<boolean> {
        //sipWise++
        if (this.isSipWise()) { //sipWise case
            const response = await this.telephonyServiceSipWise.getOverFlowStatus(this.contactService.userContact.systemId, this.contactService.userContact.phoneNumberId);
            return response.activate;
        }//sipWise--

        return false;
    }

    /**
     * Configure the nomadic mode
     * @public
     * @param {string} phoneNumber phonenumber which will be monitored
     * @param {boolean} isUnchangeableNumber  allow to define the phonenumber like a rainbowNumber
     * @param {boolean} NotTakeIntoAccount the result won't be taken in account (above used un graphical interface)
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function nomadicLogin
     */
    public async nomadicLogin(phoneNumber: string, isUnchangeableNumber: boolean = false, NotTakeIntoAccount: boolean = false): Promise<void> {

        //reject not allowed operations
        if (!this.isNomadicEnabled || !this.nomadicObject.featureActivated) {
            const profileError: any = new Error("nomadicLogin failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        if (this.contactService.userContact.phonePro === phoneNumber || this.contactService.userContact.phoneProCan === phoneNumber || this.contactService.userContact.phonePbx === phoneNumber) {
            const errorMessage = "nomadicLogin failure: impossible to use its own phone number like nomadic phone";
            const errorMessageKey = "nomadicImpossibleUseOwnPhonenumber";
            this.logger.error("[telephonyService] " + errorMessage);
            throw (new Error(errorMessageKey));
        }

        this.logger.info("[telephonyService] [TEL][API][NOMADIC] nomadicLogin : " + anonymizer(phoneNumber));

        NotTakeIntoAccount = NotTakeIntoAccount || false;
        isUnchangeableNumber = isUnchangeableNumber || false;
        this.nomadicAnswerNotTakedIntoAccount = NotTakeIntoAccount;

        try {
            let contact: Contact = this.contactService.getContactFromCacheContainsPhoneNumber(phoneNumber);
            if (!contact) {
                contact = await this.contactService.getOrCreateContact(null, phoneNumber);
            }

            const phoneInfo = this.getPhoneInfo(contact, phoneNumber);
            const url = this.portalURL + "nomadic/login";
            const data = {
                destinationExtNumber: phoneInfo.longNumber,
                destinationIntNumber: phoneInfo.internalNumber,
                destinationShortNumber: phoneInfo.shortNumber,
                destinationRainbowNumber: isUnchangeableNumber ? phoneNumber : "",
                destinationPbxId: phoneInfo.pbxId,
                destinationDisplayName: contact.displayName,
                destinationCountry: contact.country
            };
            const body = JSON.stringify(data);
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            this.logger.info("[telephonyService] [TEL][API][NOMADIC] nomadicLogin - success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'nomadicLogin');
            this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Configure the nomadic mode on the office phone
     * @public
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function nomadicLoginOnOfficePhone
     */
    public async nomadicLoginOnOfficePhone(): Promise<void> {

        //reject not allowed operations
        if (!this.isNomadicEnabled || !this.nomadicObject.featureActivated) {
            const profileError: any = new Error("nomadicLoginOnOfficePhone failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        this.logger.info("[telephonyService] [TEL][API][NOMADIC] nomadicLoginOnOfficePhone");

        try {
            const url = this.portalURL + "nomadic/login";
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            this.logger.info("[telephonyService] [TEL][API][NOMADIC] nomadicLoginOnOfficePhone - success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'nomadicLoginOnOfficePhone');
            this.logger.error(`[telephonyService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Remove the nomadic mode
     * @public
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function nomadicLogout
     */
    public async nomadicLogout(): Promise<void> {

        //reject not allowed operations
        if (!this.isNomadicEnabled || !this.nomadicObject.featureActivated) {
            const profileError: any = new Error("nomadicLogout failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw profileError;
        }

        this.logger.info("[telephonyService] [TEL][API][NOMADIC] nomadicLogout");

        try {
            const url = this.portalURL + "nomadic/logout";
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            this.logger.info("[telephonyService] [TEL][API][NOMADIC] nomadicLogout - success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'nomadicLogout');
            this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * To know the nomadic Status
     * @public
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function getNomadicStatus
     */
    public async getNomadicStatus(): Promise<void> {

        //sipWise++ PROVISOIRE_SIPWISE
        if (this.isSipWise()) {
            this.logger.info("[telephonyService] getNomadicStatus -- SipWise bypass Nomadic");
            return;
        }//sipWise-- PROVISOIRE_SIPWISE

        //reject not allowed operations
        if (!this.isNomadicEnabled) {
            const profileError: any = new Error("getNomadicStatus failure - Not Allowed");
            profileError.status = profileError.errorDetailsCode = "403";
            this.logger.error("[telephonyService] " + profileError.message);
            throw (profileError);
        }

        if (this.starting || this.started) {

            try { //telephony starting or started means that the user is attached to a pbx device
                const url = this.portalURL + "nomadic";
                const headers = this.authService.getRequestHeader();
                const response: Response = await fetch(url, { method: 'GET', headers });
                if (!response.ok) {
                    throw (response);
                }
                const responseBody: any = await response.json();
                const data: any = responseBody.data;
                this.updateNomadicData(data);
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getNomadicStatus');
                this.logger.error(`[telephonyService] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        }
        else {
            throw new Error("getNomadicStatus failure - Telephony not started");
        }
    }

    /**
     * this method is no more used
     * @public
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function setNomadicState
     */
    public async setNomadicState(): Promise<void> {

        this.logger.info("[telephonyService] [TEL][API] setNomadicState");
        try {
            const url = this.portalURL + "nomadic/state";
            const data = { makeCallInitiatorIsMain: "true" };
            const body = JSON.stringify(data);
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            this.logger.info("[telephonyService] [TEL][API][NOMADIC] setNomadicState - success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setNomadicState');
            this.logger.error(`[telephonyService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Update the Nomadic Object with new data received
     * @public
     * @param {any} response new data received
     * @async
     * @returns {Promise<boolean>} return if the main device is nomadic configured
     * @instance
     * @function updateNomadicData
     */
    public async updateNomadicData(response: any): Promise<boolean> {
        const destination = response.destination ? anonymizer(response.destination) : "";

        this.logger.info("[telephonyService] [TEL][API][NOMADIC] updateNomadicData destination: " + destination + " featureActivated: " + response.featureActivated
            + " makeCallInitiatorIsMain: " + response.makeCallInitiatorIsMain + " modeActivated: " + response.modeActivated + " monodevice: "
            + this.contactService.userContact.isVirtualTerm + " NomadicInitialized: " + response.nomadicModeInitialized + " otherPhoneModeDenied:" + response.otherPhoneModeDenied);

        const nomadicObjectOrg = JSON.parse(JSON.stringify(this.nomadicObject));

        this.nomadicObject.featureActivated = response.featureActivated === "true";
        this.nomadicObject.modeActivated = response.modeActivated === "true";
        this.nomadicObject.destination = response.destination;
        this.nomadicObject.makeCallInitiatorIsMain = response.makeCallInitiatorIsMain === "true";
        this.nomadicObject.otherPhoneModeDenied = response.otherPhoneModeDenied === "true";
        if (response.nomadicModeInitialized === undefined) {
            this.nomadicObject.nomadicModeInitialized = true;
        }
        else {
            this.nomadicObject.nomadicModeInitialized = response.nomadicModeInitialized === "true";
        }

        const makeCallInitiatorIsMainChanged = (nomadicObjectOrg.makeCallInitiatorIsMain !== this.nomadicObject.makeCallInitiatorIsMain);

        if (!this.nomadicAnswerNotTakedIntoAccount) {
            this.eventService.publish("ON_CALL_NOMADIC_EVENT", this.nomadicObject);
        }
        this.nomadicAnswerNotTakedIntoAccount = false;

        // By default if mobilepro or mobileperso exist, then add it on destination
        /*if (service.nomadicObject.featureActivated && (service.nomadicObject.destination === "" || service.nomadicObject.destination === undefined) && (this.contactService.userContact.mobileProCan || this.contactService.userContact.mobilePerso)) {
            var defaultNumber = this.contactService.userContact.mobileProCan ? this.contactService.userContact.mobileProCan : this.contactService.userContact.mobilePerso;
            service.nomadicLogin(defaultNumber)
                .then(() => {
                    service.nomadicLoginOnOfficePhone();
                });
        }*/

        //by default, in multidevices, if mediapillar exist, then add it on destination
        // else if by default, in multidevice, mobilepro or mobileperso exist, then add it on destination
        // by default, in monodevice, if mediapillar exist, then add it on destination
        // else if by default, in monodevice, mobilepro or mobileperso exist, then add it on destination
        if (this.nomadicObject.featureActivated && this.nomadicObject.nomadicModeInitialized && !this.nomadicObject.makeCallInitiatorIsMain && this.isMediaPillarConfigured() && this.contactService.userContact.useComputerMode && !this.contactService.userContact.useOtherPhoneMode) {
            const defaultNumber = await this.getMyMediaPillarRemoteExtensionRefreshed();
            if (this.nomadicObject.destination !== defaultNumber) {
                this.nomadicLogin(defaultNumber);
            }
        }
        else if (this.nomadicObject.featureActivated && !this.nomadicObject.nomadicModeInitialized && this.isMediaPillarConfigured() && this.contactService.userContact.useComputerMode) {
            const defaultNumber = await this.getMyMediaPillarRemoteExtensionRefreshed();
            this.nomadicLogin(defaultNumber);
        }
        else if (!this.contactService.userContact.isVirtualTerm && this.nomadicObject.featureActivated && !this.nomadicObject.nomadicModeInitialized
            && (this.contactService.userContact.mobileProCan || this.contactService.userContact.mobilePerso) && this.contactService.userContact.useOtherPhoneMode) {
            //const defaultNumber = this.contactService.userContact.mobileProCan ? this.contactService.userContact.mobileProCan : this.contactService.userContact.mobilePerso;
            (async (): Promise<void> => {
                //await this.nomadicLogin(defaultNumber, false, true);
                //this.nomadicLoginOnOfficePhone();
                this.nomadicLogout();
            })();
        }
        else if (this.contactService.userContact.isVirtualTerm && this.nomadicObject.featureActivated && !this.nomadicObject.nomadicModeInitialized && (this.contactService.userContact.mobileProCan || this.contactService.userContact.mobilePerso) && this.contactService.userContact.useOtherPhoneMode) {
            const defaultNumber = this.contactService.userContact.mobileProCan ? this.contactService.userContact.mobileProCan : this.contactService.userContact.mobilePerso;
            this.nomadicLogin(defaultNumber);
        }
        else if (this.contactService.userContact.isVirtualTerm && this.nomadicObject.featureActivated && !this.nomadicObject.makeCallInitiatorIsMain && !this.nomadicObject.modeActivated && this.isMediaPillarConfigured() && this.contactService.userContact.useComputerMode) {
            const defaultNumber = await this.getMyMediaPillarRemoteExtensionRefreshed();
            this.nomadicLogin(defaultNumber);
        }
        return makeCallInitiatorIsMainChanged;
    }

    /**
     * Return the nomadic object
     * @public
     * @returns {any} nomadic object returned
     * @instance
     * @function getNomadicObject
     */
    public getNomadicObject(): any {
        return this.nomadicObject;
    }

    /**
     * Return the nomadic destination
     * @public
     * @returns {string} nomadic destination returned
     * @instance
     * @function getNomadicDestination
     */
    public getNomadicDestination(): string {
        return this.nomadicObject.destination;
    }

    /**
     * Indicates wether the user is in Office mode
     * @public
     * @returns {boolean} true if the nomadic mode of the user is Office
     * @instance
     * @function isOfficeMode
     */
    public isOfficeMode(): boolean {
        return this.nomadicObject && (!this.nomadicObject.featureActivated || this.nomadicObject.makeCallInitiatorIsMain);
    }

    /**
     * Method isMediaPillarConfigured
     * @public
     * @returns {boolean} true if media pillar is configured for this user (configuration + rights)
     * @instance
     * @function isMediaPillarConfigured
     */
    public isMediaPillarConfigured(): boolean {
        return this.mediaPillarConfigured && this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_WEBRTC_PSTN_CALLING);
    }

    /**
     * Method getMyMediaPillarRemoteExtensionRefreshed
     * @public
     * @returns {string} my mediaPillar remote extension ("" if not already obtained from server )
     * @instance
     * @function getMyMediaPillarRemoteExtensionRefreshed
     */
    public async getMyMediaPillarRemoteExtensionRefreshed(): Promise<string> {
        if (!this.isSipWise()) {
            const webrtcGatewayService = this.mainService.inject(WEBRTC_GATEWAY_SVC);
            await webrtcGatewayService?.getMediaPillarData();
            return webrtcGatewayService.getMyMediaPillarRemoteExtension();
        } //sipwise case  RQRAINB-2660
        const webrtc = this.telephonyServiceSipWise.getDeviceWithType("webrtc")?.deviceId;
        return webrtc ? webrtc : "";
    }

    /**
     * Method getMyMediaPillarRemoteExtension
     * @public
     * @returns {string} my mediaPillar remote extension ("" if not already obtained from server )
     * @instance
     * @function getMyMediaPillarRemoteExtension
     */
    public getMyMediaPillarRemoteExtension(): string {
        if (!this.isSipWise()) {
            const webrtcGatewayService = this.mainService.inject(WEBRTC_GATEWAY_SVC);
            return webrtcGatewayService.getMyMediaPillarRemoteExtension();
        } //sipwise case  RQRAINB-2660
        const webrtc = this.telephonyServiceSipWise.getDeviceWithType("webrtc")?.deviceId;
        return webrtc ? webrtc : "";
    }

    /**
     * Method isMediaPillarAvailable
     * @public
     * @returns {boolean} true if media pillar is available for this user (configuration + infra Up)
     * @instance
     * @function isMediaPillarAvailable
     */
    public isMediaPillarAvailable(): boolean {
        return this.isMediaPillarConfigured() && this.mediaPillarAlive;
    }

    /**
     * Method isMediaPillarUserSelected
     * @public
     * @returns {boolean} true if media pillar (Computer mode) is selected for this user
     * @instance
     * @function isMediaPillarUserSelected
     */
    public isMediaPillarUserSelected(): boolean {
        let selected: boolean;
        if (!this.isSipWise()) { //not sipwise
            selected = (!this.nomadicObject.makeCallInitiatorIsMain && (this.getNomadicDestination() === this.getMyMediaPillarRemoteExtension()));
        } else { //sipwise case and webrtc device as current main device
            const current = this.telephonyServiceSipWise.getCurrentDevice();
            selected = (current && current.type === "webrtc");
        }
        return selected;
    }

    /**
     * Send DTMF to a call
     * @public
     * @param {Call} call callto send a DTMF
     * @param {string} dtmf  dtmf to send
     * @async
     * @returns {Promise<any>} Promise return the dtmf status
     * @instance
     * @function sendDtmf
     */
    public async sendDtmf(call: CallRB, dtmf: string): Promise<any> {
        if (call && dtmf) {
            this.logger.info("[telephonyService] [TEL][API] sendDtmf for call " + call.id);
            //sipWise++
            if (this.isSipWise()) { //sipWise case
                await this.telephonyServiceSipWise.sendDtmf(call, dtmf);
                return undefined;
            }//sipWise--

            try {
                const url = this.portalURL + "calls/" + encodeURIComponent(call.connectionId) + "/dtmf";
                const data = { dtmf: dtmf };
                const body = JSON.stringify(data);
                const headers = this.authService.getPostHeader();
                const response: Response = await fetch(url, { method: 'PUT', headers, body });
                if (!response.ok) throw response;

                this.logger.info("[telephonyService] [TEL][API] sendDtmf - success");
                const responseData = (await response.json()).data;
                return responseData;
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'sendDtmf');
                this.logger.error(`[telephonyService] [TEL][API] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        }
        return undefined;
    }

    public clearCall(call: CallRB, nobroadcast: boolean = false): void {
        if (!call) {
            const errorMessage = "clearCall failure : call null";
            this.logger.error("[telephonyService]" + errorMessage);
            return;
        }
        this.logger.info("[telephonyService] clearCall for call id: " + call.id);
        call.setStatus(CallStatus.ENDED);
        if (!nobroadcast) {
            call.sendEvent(CallEvents.ON_CALL_UPDATED_EVENT);
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
        }
        if (call.contact) { delete this.calls[call.contact.id]; }
    }

    public startAsPhoneNumber(phoneNumber: string): boolean {
        const cleanPhoneNumber = phoneNumber.trim().split(".").join("");
        if (cleanPhoneNumber.length < 1) { return false; }
        const pattern1 = /^(\+|\d|#|\*|\(|\)|\.|-|–|—|\s|\/)*$/;
        const match = cleanPhoneNumber.match(pattern1);
        if (!match) { return false; }
        return (match[0] === cleanPhoneNumber);
    }

    public updateContactFromOutlookInfos(__contact: Contact, __phoneNumber: string, __reload?, __mode?): any {
        this.logger.info("[telephonyService] default updateContactFromOutlookInfos");
    }

    /*********************************************************/
    /**                 SIPWISE STUFF                       */
    /*********************************************************/

    public isSipWiseRoot(): boolean {
        return (this.contactService.userContact && this.contactService.userContact.systDeviceName === Contact.SIPWISE_RVCP);
    }//end IsSipWise

    public setSipWise(condition: boolean): void {
        this.sipWise = condition;
    }//end setSipWise

    public isSipWise(): boolean {
        return this.sipWise;
    }//end IsSipWise

    public isSipWiseEmulatedRingBackTone(): boolean {
        let emulatedRingBackTone = false;
        const settings = this.telephonyServiceSipWise.getSettings();
        if (settings) {
            emulatedRingBackTone = Boolean(settings.emulatedRingBackTone);
        }
        return emulatedRingBackTone;
    }

    /************************* DEVICE STUFF *************************/

    /**
     * getSipWiseRoutingInfo
     * get from server the main device
     * (result update with event)
     */
    public getSipWiseRoutingInfo(): void {
        this.logger.info("[telephonyService] getSipWiseRoutingInfo");
        if (this.isSipWise()) {
            this.telephonyServiceSipWise.getUserRoutingInfo();
        } else {
            this.logger.info("[telephonyService] getSipWiseRoutingInfo Anomaly not a sipWise situation");
        }
    }

    /**
     * setSipWiseCurrentDevice
     * set on server the current device
     * currentDeviceId : the deviceId use as main/current
     * @param {string} currentDeviceId current device identifier
     */
    public setSipWiseCurrentDevice(currentDeviceId: string): void {
        if (this.isSipWise()) {
            this.telephonyServiceSipWise.putUserRoutingInfo(currentDeviceId, null);
        } else {
            this.logger.info("[telephonyService] setSipWiseCurrentDevice Anomaly not a sipWise situation");
        }
    }//end setSipWiseDevicesMain

    /****************************************************************************************/
    /************************* CLI (Call Line identification) STUFF *************************/
    /****************************************************************************************/

    /**
     * Method returns the CLI current option (only for SipWise user)
     * @param {boolean} [forceUpdate] whether the local data is updated
     * @returns {Promise<CliOption>} Current CLI option of user
     * @instance
     * @function getSipWiseCurrentCli
     */
    public async getSipWiseCurrentCli(forceUpdate: boolean = false): Promise<CliOption> { //RQ5934
        if (!this.isSipWiseRoot()) {
            this.logger.info("[telephonyService] getSipWiseCurrentCli Anomaly not a sipWise situation");
            throw new Error("Anomaly not a sipWise situation");
        }

        if (!this.currentCli || forceUpdate) {
            try {
                this.currentCli = await this.telephonyServiceSipWise.getCliOptionsCurrent();
            } catch (error) {
                this.logger.error("[telephonyService] getSipWiseCurrentCli - error retrieving option");
                this.currentCli = null;
                throw (error);
            }
        }
        return this.currentCli;
    }

    /**
     * Method returns the CLI options available (only for SipWise user)
     * @param {boolean} [forceUpdate] whether the local data is updated
     * @returns {Promise<CliOption[]>} All CLI options of user
     * @instance
     * @function getSipWiseAllCli
     */
    public async getSipWiseAllCli(forceUpdate: boolean = false): Promise<CliOption[]> {

        if (!this.isSipWiseRoot()) {
            this.logger.info("[telephonyService] getSipWiseAllCli Anomaly not a sipWise situation");
            throw new Error("Anomaly not a sipWise situation");
        }

        // retrieve data from server if requested explicitely or if there s no stored data
        if (this.cliOptions.length === 0 || forceUpdate) {
            try {
                this.cliOptions = await this.telephonyServiceSipWise.getCliOptionsAll();
                this.currentCli = this.cliOptions.find((cliOption) => { return cliOption.current; });
            } catch (error) {
                this.logger.error("[telephonyService] getSipWiseAllCli - error retrieving options, empty the list");
                this.cliOptions = [];
                throw (error);
            }
        }

        return this.cliOptions;
    }

    /**
     * Method returns the CLI current option if editable (only for SipWise user)
     * @returns {Promise<CliOption>} Current CLI option of user
     * @instance
     * @function getSipWiseCurrentCliIfEditable
     */
    public async getSipWiseCurrentCliIfEditable(): Promise<CliOption> {

        if (!this.isSipWiseRoot()) {
            this.logger.info("[telephonyService] getSipWiseCurrentCliIfEditable Anomaly not a sipWise situation");
            throw new Error("Anomaly not a sipWise situation");
        }

        const cliOptions = await this.getSipWiseAllCli();
        if (cliOptions && cliOptions.length > 1) {
            return this.currentCli;
        }

        return null;
    }

    public async setSipWiseCliOption(cliOption: CliOption): Promise<any> {

        if (!this.isSipWiseRoot()) {
            this.logger.info("[telephonyService] setSipWiseCliOption Anomaly not a sipWise situation");
            throw new Error("Anomaly not a sipWise situation");
        }

        return await this.telephonyServiceSipWise.setCliOption(cliOption);
    }

    /**
     * Method returns the current CLIR option (only for SipWise user)
     * @returns {Promise<boolean>} Current CLIR option of user
     * @instance
     * @function
     */
    public async getSipWiseClir(): Promise<boolean> {

        if (!this.isSipWiseRoot()) {
            this.logger.info("[telephonyService] getSipWiseClir Anomaly not a sipWise situation");
            throw new Error("Anomaly not a sipWise situation");
        }

        return await this.telephonyServiceSipWise.getClirOption();
    }

    /**
     * Method used to change the value of CLIR
     * @param {boolean} clirActivation whether CLIR should be activated
     * @returns {Promise<any>} resolve when option is set
     * @instance
     * @function
     */
    public async setSipWiseClirOption(clirActivation: boolean): Promise<any> {

        if (!this.isSipWiseRoot()) {
            this.logger.info("[telephonyService] setSipWiseClirOption Anomaly not a sipWise situation");
            throw new Error("Anomaly not a sipWise situation");
        }

        await this.telephonyServiceSipWise.setClirOption(clirActivation);
    }

    /**
     * @public
     * @function isMonolineIncomingModeActivated
     * @instance
     * @description
     * Returns TRUE if the monoline settings is activated for user or PBX system used by the user.
     * In that case, all second incoming calls should be ignored by the device that is currently in a call
     * @returns {boolean} resolve when option is set
     */
    public isMonolineIncomingModeActivated(): boolean {
        return this.telephonyServiceSipWise.isMonolineIncomingModeActivated();
    }

    /**
     * @public
     * @function getTelephonyLinePolicy
     * @instance
     * @description
     * Returns the telephonyline policy of the current user (used for hub systems only)
     * Possible values are "mono_line" / "multi_lines" / "multi_lines_plus"
     * @returns {boolean} resolve when data is set
     */
    public getTelephonyLinePolicy(): string {
        return this.telephonyServiceSipWise.getTelephonyLinePolicy();
    }

    /************************* GROUPS STUFF *************************/

    /**
     * Retrieve the user's hunt groups
     * @public
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function getUserHuntGroupsConfig
     */
    public async getUserHuntGroupsConfig(): Promise<void> {

        if (this.starting || this.started) { //telephony starting or started means that the user is attached to a pbx device
            if (this.isSipWise()) {
                try {
                    this.getSipWiseUserGroups();
                    return;
                }
                catch (error) {
                    const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getUserHuntGroupsConfig');
                    this.logger.error(`[telephonyService] ${errorInfo.message}`);
                    throw errorInfo.error;
                }
            } else if (!this.isOxe) { return; }

            this.logger.info("[telephonyService] [TEL][API] getUserHuntGroupsConfig for Oxe user");

            try {
                //retrieve the oxe group info
                const url: string = this.portalURL + "dynamicstate/huntinggroupinfo";
                const headers: any = this.authService.getRequestHeader();
                const response: Response = await fetch(url, { method: 'GET', headers });
                if (!response.ok) {
                    throw (response);
                }
                const responseBody: any = await response.json();
                const data: any = responseBody.data;

                if (data?.huntingGroupNumber) {
                    // user is member of a hunt group
                    if (data.huntingGroupNumber !== this.oxeUserGroupData?.huntingGroupNumber) {
                        // we don't already know the group number
                        this.oxeUserGroupData = { huntingGroupNumber: data.huntingGroupNumber, huntingGroupName: data.huntingGroupName, logged: undefined };
                    }
                    this.logger.info(`[telephonyService] getUserHuntGroupsConfig - success - huntingGroupNumber: ${data.huntingGroupNumber} huntingGroupName: ${data.huntingGroupName}`);
                } else {
                    this.oxeUserGroupData = undefined;
                    this.logger.info("[telephonyService] getUserHuntGroupsConfig - success - no hunt group associated to user");
                }
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getUserHuntGroupsConfig');
                if (errorInfo?.error?.status === 406) {
                    this.logger.info("[telephonyService] getUserHuntGroupsConfig - no hunt group associated to user");
                } else {
                    this.logger.error(`[telephonyService] ${errorInfo.message}`);
                }
                this.oxeUserGroupData = undefined;
            }

        } else {
            throw new Error("getUserHuntGroupsConfig failure - Telephony not started");
        }
    }

    /**
     * Retrieve the Dynamic State
     * @public
     * @async
     * @param {string} userJidTel Telephony jid identifier of the user for which we request the ringing state
     * (this param is optional : not used to get the logged user's ringing state; it is used
     * by an attendant to get the ringing state of a supervised user)
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function getOxeDynamicState
     */
    public async getOxeDynamicState(userJidTel?: string): Promise<any> {
        this.logger.info("[telephonyService] [TEL][API] getOxeDynamicState");

        try {
            // retrieve the oxe dynamic state
            const url: URL = new URL(userJidTel
                ? (this.portalURL + "admin/dynamicstate/" + userJidTel)
                : (this.portalURL + "dynamicstate"));

            const headers: any = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }
            const responseBody: any = await response.json();
            const data: any = responseBody.data;

            if (!userJidTel) {
                // apply changes to our user data
                if (data.ringingActivated) {
                    this.setOxeDeskPhoneNonRinging(data.ringingActivated !== "true");
                    this.publish(RBEvent.create('onSilentRingingUpdatedEvent', this.getOxeDeskPhoneNonRinging()));
                }

                if (data.secretIdentity) { //RQRAINB-9582
                    this.setOxeSecretIdentity(data.secretIdentity === "true");
                    this.publish(RBEvent.create('onSecretIdentityUpdatedEvent', this.getOxeSecretIdentity()));
                }

                if (data.doNotDisturb) {
                    this.setOxeDoNotDisturb(data.doNotDisturb === "true");
                    this.publish(RBEvent.create('onDoNotDisturbUpdatedEvent', this.getOxeDoNotDisturb()));
                }

                if (data.inHuntingGroup) {
                    this.handleOxeUserGroupData(data.inHuntingGroup === "true");
                }
            }

            this.logger.info(`[telephonyService] getOxeDynamicState - success - doNotDisturb: ${data.doNotDisturb} inHuntingGroup: ${data.inHuntingGroup} lock: ${data.lock} ringingActivated: ${data.ringingActivated} secretCodeBlocked: ${data.secretCodeBlocked} secretIdentity: ${data.secretIdentity} waitingOnBusy: ${data.waitingOnBusy}`);
            return data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getOxeDynamicState');
            this.logger.error(`[telephonyService] ${errorInfo.message}`);
        }
        return undefined;
    }

    public async handleOxeUserGroupData(inHuntingGroupAttr: boolean): Promise<any> {
        this.logger.info("[TelephonyService] handleOxeUserGroupData with attribute inHuntingGroup: " + inHuntingGroupAttr);
        const orgOxeUserGroupData: IOxeGroup = this.getOxeUserGroupData();

        // retrieve user's huntgroup config to know wether the user is member of an oxe huntgroup or not
        await this.getUserHuntGroupsConfig();

        if (this.getOxeUserGroupData()) { // user is member of an oxe hunt group
            // read logged state in event
            const isLoggedInHuntingGroup: boolean = inHuntingGroupAttr;
            // store user logged state
            this.setOxeUserGroupLogged(isLoggedInHuntingGroup);
            this.logger.info("[TelephonyHybridEventHandler] onOxeDynamicStateEvent configured in group: " + this.getOxeUserGroupData().huntingGroupNumber + " logged: " + isLoggedInHuntingGroup);
            // notify change
            this.publish(RBEvent.create('oxeHuntingGroupLoggedStatus', this.getOxeUserGroupData()));
        } else {
            // user is not member of an oxe hunt group
            if (orgOxeUserGroupData) {
                // user was previously in an oxe hunt group: he has been removed from the oxe hunt group
                this.logger.info("[TelephonyHybridEventHandler] OxeDynamicStateEvent received: user removed from oxe hunt group " + orgOxeUserGroupData.huntingGroupNumber);
                this.publish(RBEvent.create('oxeHuntingGroupLoggedStatus', this.getOxeUserGroupData()));
            }
        }
    }

    /**
     * getOxeUserGroupData
     * @returns {IOxeGroup} Oxe hunt group data of the logged user:
     *          it returns undefined if the user does not belong to an Oxe hunting group
     */
    public getOxeUserGroupData(): IOxeGroup {
        return this.oxeUserGroupData;
    }

    /**
     * setOxeUserGroupLogged
     * Modify the internal logged state in the oxeUserGroupData structure
     * @param {boolean} logged true if the user is looged in the group
     */
    public setOxeUserGroupLogged(logged: boolean): void {
        if (this.oxeUserGroupData) {
            this.oxeUserGroupData.logged = logged;
        }
    }

    /**
     * setOxeDeskPhoneNonRinging
     * Modify the OXE set Deskphone in non Ringing state
     * @param {boolean} activated true if Deskphone in non Ringing state
     * @param activated
     */
    public setOxeDeskPhoneNonRinging(activated: boolean): void {
        this.isOXEDeskphoneNonRinging = activated;
    }

    /**
     * getOxeDeskPhoneNonRinging
     * @returns boolean OXE set Deskphone in non Ringing state:
     */
    public getOxeDeskPhoneNonRinging(): boolean {
        return this.isOXEDeskphoneNonRinging;
    }

    /**
     * setOxeDynamicStateReceived
     * Modify the OXE dynamic state with true if received
     */
    public setOxeDynamicStateReceived(): void {
        this.isOXEDynamicStateReceived = true;
    }

    /**
     * getOxeDynamicStateReceived
     * @returns boolean Dynamic State Oxe is received
     */
    public getOxeDynamicStateReceived(): boolean {
        return this.isOXEDynamicStateReceived;
    }

    /**
     * Set the oxe user's hunt group logged state (login / logout)
     * @public
     * @param {boolean} logged true for group login, false for group logout
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function setOxeUserHuntGroupLogin
     */
    public async setOxeUserHuntGroupLogin(logged: boolean): Promise<void> {
        this.logger.info(`[telephonyService] [TEL][API] setOxeUserHuntGroupLogin logged '${logged}'`);

        try {
            const url = this.portalURL + "dynamicstate";
            const headers = this.authService.getPostHeader();

            const reqData = { intoHuntingGroup: logged };
            const body = JSON.stringify(reqData);

            const response: Response = await fetch(url, { method: 'PUT', headers, body });

            if (!response.ok) throw response;

            this.logger.info("[telephonyService] setOxeUserHuntGroupLogin : success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setOxeUserHuntGroupLogin');
            this.logger.error(`[telephonyService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Set the oxe user Deskphone in non ringing state
     * @public
     * @param {boolean} activated true for silence, false for ringing
     * @param {string} userJidTel Telephony jid identifier of the user for which the ringing state must be changed
     * (this param is optional : not used to change the logged user's ringing state: it is used
     * to change the ringing state of a supervised user by an attendant)
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function setOxeDeskphoneRingingState
     */
    public async setOxeDeskphoneRingingState(activated: boolean, userJidTel?: string): Promise<void> {
        this.logger.info(`[telephonyService] [TEL][API] setOxeDeskphoneRIngingState activated '${activated}'`);

        try {
            const url: URL = new URL(userJidTel
                ? (this.portalURL + "admin/dynamicstate/" + userJidTel)
                : (this.portalURL + "dynamicstate"));

            const headers = this.authService.getPostHeader();

            const reqData = { ringingActivate: activated };
            const body = JSON.stringify(reqData);

            const response: Response = await fetch(url, { method: 'PUT', headers, body });

            if (!response.ok) throw response;

            this.logger.info("[telephonyService] setOxeDeskphoneRingingState : success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setOxeDeskphoneRingingState');
            this.logger.error(`[telephonyService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Set on server the oxe user's secret identity mode
     * @public
     * @param {boolean} secretIdentityActivated true if activated
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function fetchOxeSecretIdentity
     */
    public async fetchOxeSecretIdentity(secretIdentityActivated: boolean): Promise<void> { //RQRAINB-9582
        this.logger.info(`[telephonyService] [TEL][API] fetchOxeSecretIdentity  '${secretIdentityActivated}'`);

        try {
            if (!this.isOxe) {
                this.logger.error("[telephonyService] fetchOxeSecretIdentity : Not an OXE ");
                return;
            }

            const url = this.portalURL + "dynamicstate";
            const headers = this.authService.getPostHeader();

            const reqData = { secretIdentity: secretIdentityActivated };
            const body = JSON.stringify(reqData);

            const response: Response = await fetch(url, { method: 'PUT', headers, body });

            if (!response.ok) throw response;
            this.setOxeSecretIdentity(secretIdentityActivated); //ser the local flag
            this.logger.info("[telephonyService] fetchOxeSecretIdentity : success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'fetchOxeSecretIdentity');
            this.logger.error(`[telephonyService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * setOxeSecretIdentity
     * Modify the local var for oxe secret identity state
     * @param {boolean} oxeSecretIdentity true if secret identity activated
     * @param activated
     */
    public setOxeSecretIdentity(oxeSecretIdentity: boolean): void {
        if (this.isOxe) {
            this.isOxeSecretIdentity = oxeSecretIdentity;
            this.logger.info("[telephonyService] OxeSecretIdentity = " + oxeSecretIdentity);
        } else {
            this.logger.error("[telephonyService] setOxeSecretIdentity : Not an OXE ");
        }
    }

    /**
     * getOxeSecretIdentity
     * @returns boolean OXE secret identity local state:
     */
    public getOxeSecretIdentity(): boolean { //RQRAINB-9582
        if (!this.isOxe) {
            this.logger.error("[telephonyService] getOxeSecretIdentity : Not an OXE");
        }
        return (this.isOxeSecretIdentity && this.isOxe);
    }

    /**
     * Set the oxe user Do Not Disturb
     * @public
     * @param {boolean} activated true if activated
     * @async
     * @returns {Promise<void>} Status promise resolved when the process is complete
     * @instance
     * @function setOxeDoNotDisturbState
     */
    public async setOxeDoNotDisturbState(DoNotDisturbActivated: boolean): Promise<void> {
        this.logger.info(`[telephonyService] [TEL][API] setOxeDoNotDisturbState  '${DoNotDisturbActivated}'`);

        try {
            if (!this.isOxe) {
                this.logger.error("[telephonyService] setOxeDoNotDisturbState : Not an OXE ");
                return;
            }

            const url = this.portalURL + "dynamicstate";
            const headers = this.authService.getPostHeader();

            const reqData = { doNotDisturb: DoNotDisturbActivated };
            const body = JSON.stringify(reqData);

            const response: Response = await fetch(url, { method: 'PUT', headers, body });

            if (!response.ok) throw response;
            this.setOxeDoNotDisturb(DoNotDisturbActivated); //ser the local flag
            this.logger.info("[telephonyService] setOxeDoNotDisturbState : success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setOxeDoNotDisturbState');
            this.logger.error(`[telephonyService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * setOxeDoNotDisturb
     * Modify the local var for oxe Do Not Disturb
     * @param {boolean} oxeDoNotDisturb true if Do Not DIsturb activated
     * @param activated
     */
    public setOxeDoNotDisturb(oxeDoNotDisturb: boolean): void {
        if (this.isOxe) {
            this.isOxeDoNotDisturb = oxeDoNotDisturb;
            this.logger.info("[telephonyService] OxeoxeDoNotDisturb = " + oxeDoNotDisturb);
        } else {
            this.logger.error("[telephonyService] setOxeoxeDoNotDisturb : Not an OXE ");
        }
    }

    /**
     * getOxeDoNotDisturb
     * @returns boolean OXE Do Not Disturb:
     */
    public getOxeDoNotDisturb(): boolean {
        if (!this.isOxe) {
            this.logger.error("[telephonyService] getOxeDoNotDisturb : Not an OXE");
        }
        return (this.isOxeDoNotDisturb && this.isOxe);
    }

    /**
     * sipWiseUserGroupsDataClear
     * clear user's groups data
     *
     */
    public sipWiseUserGroupsDataClear(): void {
        //Former mng-assist way Deprecated++
        this.sipWiseUserGroupsData.myMemberId = "";
        this.sipWiseUserGroupsData.managerAssistGrId = 0;
        this.sipWiseUserGroupsData.managerId = 0;
        this.sipWiseUserGroupsData.managerName = "";
        this.sipWiseUserGroupsData.managerStatus = "";
        this.sipWiseUserGroupsData.managerForward = "unknow";
        this.sipWiseUserGroupsData.assistantId = 0;
        this.sipWiseUserGroupsData.assistantName = "";
        this.sipWiseUserGroupsData.assistantStatus = "";
        //Former mng-assist way Deprecated--
        //new mng-assist way : based on HG (type:hunting_group/subType:manager_assistant)
        this.sipWiseUserGroupsData.isSomeMngAssistAsHG = false;
        this.sipWiseUserGroupsData.huntingGroupNb = 0;
        this.sipWiseUserGroupsData.managerAssistantNb = 0;
        this.sipWiseUserGroupsData.managerAssistantMyAssistList = [];
        this.sipWiseUserGroupsData.managerAssistantMyMngList = [];
        this.sipWiseUserGroupsData.sipWiseUserGroupsList = [];
    }

    /**
     * sipWiseGetFormerManagerAssistGrId
     * @returns {string} the former only one (1:1) mng-assist group (Deprecated but for compatibility)
     */
    public sipWiseGetFormerManagerAssistGrId(): string {
        return this.sipWiseUserGroupsData.managerAssistGrId; //if not 0 => remaining at least 1 former mng-assist (1:1)
    } //end sipWiseGetFormerManagerAssistGrId

    /**
     * sipWiseUserGroupsDataIsManagerAssistant
     * @returns {boolean}  true if manager / assistant group
     */
    public sipWiseUserGroupsDataIsManagerAssistant(): boolean {
        //return false;//force value for debug or feature ctrl
        if (this.isSipWise() && (this.sipWiseUserGroupsData.managerAssistantNb > 0)) {
            //CURRENTLY ONLY 1:1 manager/assistant per group is accepted
            if (this.sipWiseUserGroupsData.managerAssistantNb === 1) {
                return true;
            }
            this.logger.info("[telephonyService] sipWiseUserGroupsDataIsManagerAssistant Anomaly not 1:1 Manager/Assistant");
            return false;
        }
        return false;
    }

    /**
     * sipWiseManAssistGetMyRole
     * @returns {string} my role
     */
    public sipWiseManAssistGetMyRole(): string {
        let myRole = "";
        if (this.isSipWise() && (this.sipWiseUserGroupsData.managerAssistantNb > 0)) {
            const foundGr = this.sipWiseUserGroupsData.sipWiseUserGroupsList.find(elt => elt.type === "manager_assistant");
            if (foundGr) {
                const me = foundGr.members.find(elt => elt.memberId === this.contactService.userContact.dbId);//CRRAINB-18582 & CRRAINB-18989
                if (me) { myRole = me.roles[0]; }
            }
        }
        return myRole;
    } //end sipWiseManAssistGetMyRole

    /**
     * sipWiseManAssistIamManagerForwarded
     * @returns {boolean} true if i'am a manager forwarded on assistant
     */
    public sipWiseManAssistIamManagerForwarded(): boolean {
        let managerForward = "unknown";
        let result = false;
        if (this.isSipWise() && (this.sipWiseUserGroupsData.managerAssistantNb >= 1)) { //RQRAINB-7479
            if (this.sipWiseManAssistGetMyRole() === "manager") {
                managerForward = this.sipWiseUserGroupsData.managerForward;
            }
        }
        if (managerForward === "activated") {
            result = true;
        }
        return result;
    } //end sipWiseManAssistIamManagerForwarded

    /**
     * getSipWiseUsersGroupData
     * @returns {any} group data
     */
    public getSipWiseUserGroupsData(): any {
        return this.sipWiseUserGroupsData;
    }

    /**
     * getSipWiseUserGroups
     * get on server the user's groups
     *
     */
    public async getSipWiseUserGroups(): Promise<void> {
        this.logger.info("[telephonyService] getSipWiseUserGroups");
        if (!this.isSipWise()) {
            const errorMessage = "sipwise getUserGroups -- not a sipWise situation";
            this.logger.error("[telephonyService] " + errorMessage);
            return;
        }

        try {
            await this.telephonyServiceSipWise.getUserGroups();
            await this.telephonyServiceSipWise.getAllUserGroupsForwards();
            this.eventService.publish("ON_TEL_USER_GROUPS_EVENT", this.sipWiseUserGroupsData);
        }
        catch (error) {
            this.logger.info("[telephonyService] sipwise getSipWiseUserGroups CATCH");
            const errorMessage = "sipwise getUserGroups -- " + error.message;
            this.logger.error("[telephonyService] " + errorMessage);
            throw error;
        }
    }

    public sipWiseGetMyHuntGroups(): HuntGroup[] {
        return (this.sipWiseUserGroupsData?.sipWiseUserGroupsList || []).filter((group) => {
            return group.type === "hunting_group" || group.type === "call_queue";
        });
    }

    public sipWiseGetMyBasicCallCenterGroups(): HuntGroup[] {
        const featureBasicCallCenterAllowed: boolean = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.CLOUD_PBX_BASIC_CALL_CENTER);
        if (!featureBasicCallCenterAllowed) {
            return [];
        }

        return (this.sipWiseUserGroupsData?.sipWiseUserGroupsList || []).filter((group) => {
            return group.type === "call_queue" && group.subType === "call_center_queue";
        });
    }

    /**
     * setSipWiseJoinGroup
     * @param {string} groupId group identifier
     */
    public async setSipWiseJoinGroup(groupId: string): Promise<void> {
        this.logger.info("[telephonyService] [TEL][API] setSipWiseJoinGroup");
        if (!this.isSipWise()) {
            const errorMessage = "sipwise join Group -- not a sipWise situation";
            this.logger.error("[telephonyService] " + errorMessage);
            return;
        }
        if (!groupId) {
            const errorMessage = "sipwise join Group -- groupId anomaly";
            this.logger.error("[telephonyService] " + errorMessage);
            return;
        }

        try {
            //add groupId control
            await this.telephonyServiceSipWise.joinGroup(groupId);
            //this.eventService.publish("ON_TEL_USER_GROUPS_EVENT", this.sipWiseUserGroupsData);
        }
        catch (error) {
            this.logger.info("[telephonyService] sipwise joinGroup CATCH");
            const errorMessage = "sipwise join Group -- " + error.message;
            this.logger.error("[telephonyService] " + errorMessage);
            throw error;
        }
    }

    /**
     * setSipWiseLeaveGroup
     *@param {string} groupId group identifier
     */
    public async setSipWiseLeaveGroup(groupId: string): Promise<void> {
        this.logger.info("[telephonyService] [TEL][API] setSipWiseLeaveGroup");
        if (!this.isSipWise()) {
            const errorMessage = "sipwise leave Group -- not a sipWise situation";
            this.logger.error("[telephonyService] " + errorMessage);
            return;
        }
        if (!groupId) {
            const errorMessage = "sipwise leave Group -- groupId anomaly";
            this.logger.error("[telephonyService] " + errorMessage);
            return;
        }

        try {
            //add groupId control
            await this.telephonyServiceSipWise.leaveGroup(groupId);
            //this.eventService.publish("ON_TEL_USER_GROUPS_EVENT", this.sipWiseUserGroupsData);
        }
        catch (error) {
            this.eventService.publish("ON_TEL_USER_GROUPS_EVENT", this.sipWiseUserGroupsData);
            if (error.errorDetailsCode === 403083) { //CRRAINB-45955
                throw error;
            } else {
                this.logger.info("[telephonyService] sipwise leaveGroup CATCH");
                const messageDetails = error.errorDetails ? error.errorDetails : "";
                const errorMessage = "sipwise leave Group -- " + messageDetails;
                this.logger.error("[telephonyService] " + errorMessage);
                throw error;
            }
        }
    }

    /**
     * setSipWiseJoinAllGroups
     *
     */
    public async setSipWiseJoinAllGroups(): Promise<void> {
        this.logger.info("[telephonyService] [TEL][API] setSipWiseJoinAllGroups");
        if (!this.isSipWise()) {
            const errorMessage = "sipwise joinAllGroups -- not a sipWise situation";
            this.logger.error("[telephonyService] " + errorMessage);
            return;
        }

        try {
            await this.telephonyServiceSipWise.joinAllGroups();
            //this.eventService.publish("ON_TEL_USER_GROUPS_EVENT", this.sipWiseUserGroupsData);
        }
        catch (error) {
            this.logger.info("[telephonyService] setSipWiseJoinAllGroups CATCH");
            const errorMessage = "sipwise joinAllGroups -- " + error.message;
            this.logger.error("[telephonyService] " + errorMessage);
            throw error;
        }
    }

    /**
     * setSipWiseLeaveAllGroups
     *
     */
    public async setSipWiseLeaveAllGroups(): Promise<any> {
        this.logger.info("[telephonyService] [TEL][API] setSipWiseLeaveAllGroups");
        if (!this.isSipWise()) {
            const errorMessage = "sipwise leaveAllGroups -- not a sipWise situation";
            this.logger.error("[telephonyService] " + errorMessage);
            return undefined;
        }

        try {
            const response = await this.telephonyServiceSipWise.leaveAllGroups();
            //this.eventService.publish("ON_TEL_USER_GROUPS_EVENT", this.sipWiseUserGroupsData);
            return response;
        }
        catch (error) {
            this.logger.info("[telephonyService] setSipWiseLeaveAllGroups CATCH");
            const errorMessage = "sipwise leaveAllGroups -- " + error.message;
            this.logger.error("[telephonyService] " + errorMessage);
            throw error;
        }
    }

    /**
     * getSipWiseUserGroupsForwards
     * @param {string} groupId group identifier
     */
    public async getSipWiseUserGroupsForwards(groupId: string): Promise<void> {
        this.logger.info("[telephonyService] [TEL][API] getSipWiseUserGroupsForwards");
        if (!this.isSipWise()) {
            const errorMessage = "sipwise getUserGroupsForward -- not a sipWise situation";
            this.logger.error("[telephonyService] " + errorMessage);
            return;
        }
        //groupId control
        //to be added !!!!!!!!!!!!!!!!!!

        try {
            await this.telephonyServiceSipWise.getUserGroupForwards(groupId);
            this.eventService.publish("ON_TEL_USER_GROUPS_EVENT", this.sipWiseUserGroupsData);
        }
        catch (error) {
            this.logger.info("[telephonyService] sipwise getSipWiseUserGroupsForwards CATCH");
            const errorMessage = "sipwise getUserGroupsForwards -- " + error.message;
            this.logger.error("[telephonyService] " + errorMessage);
            throw error;
        }
    }

    /**
     * setSipWiseUserGroupsForwards
     * @param {string} groupId group identifier
     * @param {string} callForwardType forward type
     * @param {any} forwardData forward data
     */
    public async setSipWiseUserGroupsForwards(groupId: string, callForwardType: string, forwardData: any): Promise<void> {
        this.logger.info("[telephonyService] [TEL][API] setSipWiseUserGroupsForwards");
        if (!this.isSipWise()) {
            const errorMessage = "sipwise setSipWiseUserGroupsForwards -- not a sipWise situation";
            this.logger.error("[telephonyService] " + errorMessage);
            return;
        }

        try {
            //manage user group forward para
            await this.telephonyServiceSipWise.setUserGroupForward(groupId, callForwardType, forwardData);
        }
        catch (error) {
            this.logger.info("[telephonyService] setSipWiseUserGroupsForwards CATCH");
            const errorMessage = "sipwise setSipWiseUserGroupsForwards -- " + error.message;
            this.logger.error("[telephonyService] " + errorMessage);
            throw error;
        }
    }

    /**
     * sipWiseManAssistActivat
     * @param {boolean} active true to activate the manager forward to assistant
     * @param {string} groupName Name of manager assistant group
     * @description Deprecated / former mng Assist way to activate manager forward
     * for new manager-Assistant as hunting group use instead
     */
    public sipWiseManAssistActivate(active: boolean, groupName: string = undefined): void {
        let groupNotFound = true;
        let groupId: string;
        this.logger.info("[telephonyService] [TEL][API] sipWiseManAssistActivate  Activ : " + active + " groupName : " + groupName);
        if (this.sipWiseUserGroupsData.managerAssistantNb === 0) {
            const errorMessage = "sipwise sipWiseManAssistActivate -- no Manager Assistant group available";
            this.logger.error("[telephonyService] " + errorMessage);
            return;
        }
        if (!groupName) { groupName = ""; }
        this.sipWiseUserGroupsData.sipWiseUserGroupsList.forEach((group) => {
            if (groupNotFound && group.type === "manager_assistant") {
                if (groupName === group.name || groupName === "") { //if empty groupName use the 1st found
                    groupId = group.id;
                    groupNotFound = false;
                }
            }
        });
        if (groupNotFound) {
            return;
        }
        const forwardData = { destinationType: "managersecretary", activate: active };
        this.setSipWiseUserGroupsForwards(groupId, "immediate", forwardData);
    }

    /************************* Manager-Assistant as Hunting Group STUFF *************************/
    /**
     * sipWiseManAssistAsHGJoinLeave
     * @param {string} action from {"join" or "leave"}
     * @param {string} groupId manager-assistant group id (optional)
     * @param {string} leaderId manager-assistant leader id from assistant point of view (optional)
     * @description
     */
    public async sipWiseManAssistAsHGJoinLeave(action: string = "", groupId: string = undefined, leaderId: string = undefined): Promise<any> {

        this.logger.info("[telephonyService] [TEL][API] sipWiseManAssistAsHGJoinLeave  Activ : " + action + " groupId: " + groupId + " leaderId: " + leaderId);
        //some controls
        if (!this.isSipWise()) {
            const errorMessage = "sipWiseManAssistAsHGJoinLeave -- not a sipWise situation";
            this.logger.error("[telephonyService] " + errorMessage);
            return;
        }

        if (!this.sipWiseUserGroupsData.isSomeMngAssistAsHG) {
            const errorMessage = "sipwise sipWiseManAssistAsHGJoinLeave -- no Manager Assistant group available";
            this.logger.error("[telephonyService] " + errorMessage);
            return;
        }
        if (action !== "join" && action !== "leave") {
            const errorMessage = "sipwise sipWiseManAssistAsHGJoinLeave -- invalid action";
            this.logger.error("[telephonyService] " + errorMessage);
            return;
        }
        if (groupId) {
            const found = this.sipWiseUserGroupsData.sipWiseUserGroupsList.find(group =>
                (group.type === "hunting_group" && group.subType === "manager_assistant" && group.id === groupId)
            );
            if (!found) {
                const errorMessage = "sipwise sipWiseManAssistAsHGJoinLeave -- invalid groupId";
                this.logger.error("[telephonyService] " + errorMessage);
                return;
            }
        }
        if (leaderId) {
            const found = this.sipWiseUserGroupsData.sipWiseUserGroupsList.find(group =>
            // eslint-disable-next-line @typescript-eslint/indent
                (group.type === "hunting_group" && group.subType === "manager_assistant"
                && group.members[0].memberId === leaderId)
            );
            if (!found) {
                const errorMessage = "sipwise sipWiseManAssistAsHGJoinLeave -- invalid leaderId";
                this.logger.error("[telephonyService] " + errorMessage);
                return;
            }
        }
        // perform the action following situation
        if (groupId) {
            if (action === "join") {
                try {
                    await this.telephonyServiceSipWise.joinGroup(groupId, leaderId); return;
                    //this.eventService.publish("ON_TEL_USER_GROUPS_EVENT", this.sipWiseUserGroupsData);
                }
                catch (error) {
                    this.logger.info("[telephonyService] sipWiseManAssistAsHGJoinLeave CATCH");
                    const errorMessage = "sipwise joinGroup -- " + error.message;
                    this.logger.error("[telephonyService] " + errorMessage);
                    throw error;
                }
            } else {
                try {
                    await this.telephonyServiceSipWise.leaveGroup(groupId, leaderId); return;
                    //this.eventService.publish("ON_TEL_USER_GROUPS_EVENT", this.sipWiseUserGroupsData);
                }
                catch (error) {
                    this.logger.info("[telephonyService] sipWiseManAssistAsHGJoinLeave CATCH");
                    const errorMessage = "sipwise leaveGroup -- " + error.message;
                    this.logger.error("[telephonyService] " + errorMessage);
                    throw error;
                }
            }
        } else //else it is a global joinAllGroups or leaveAllgroups as manager-assistant group
            if (action === "join") {
                try {
                    await this.telephonyServiceSipWise.joinAllGroups("manager_assistant"); return;
                    //this.eventService.publish("ON_TEL_USER_GROUPS_EVENT", this.sipWiseUserGroupsData);
                }
                catch (error) {
                    this.logger.info("[telephonyService] setSipWiseJoinAllGroups CATCH");
                    const errorMessage = "sipwise joinAllGroups -- " + error.message;
                    this.logger.error("[telephonyService] " + errorMessage);
                    throw error;
                }
            } else { //else it is a leave All group
                try {
                    return await this.telephonyServiceSipWise.leaveAllGroups("manager_assistant");
                    //this.eventService.publish("ON_TEL_USER_GROUPS_EVENT", this.sipWiseUserGroupsData);
                }
                catch (error) {
                    this.logger.info("[telephonyService] setSipWiseLeaveAllGroups CATCH");
                    const errorMessage = "sipwise leaveAllGroups -- " + error.message;
                    this.logger.error("[telephonyService] " + errorMessage);
                    throw error;
                }
            }
    } //end sipWiseManAssistAsHGJoinLeave

    /**
     * sipWiseUserGroupsDataIsMngAssistAsHG
     * @returns {boolean}  true if manager / assistant group as hunting group
     */
    public sipWiseUserGroupsDataIsMngAssistAsHG(): boolean {
        return this.sipWiseUserGroupsData.isSomeMngAssistAsHG;
    }

    private async onUpdateHuntingGroupsData() {

        try {
            await this.getSipWiseUserGroups();
        } catch (error) {
            this.logger.info("[telephonyService] onUpdateHuntingGroupsData CATCH");
            const errorMessage = "onUpdateHuntingGroupsData -- " + error.message;
            this.logger.error("[telephonyService] " + errorMessage);
            throw error;
        }
    }

    /************************* MISC STUFF *************************/

    //RQ6394 - RQ7175 ++
    public oxoMultiCallMode(): boolean {
        //For specific oxo release only, in order to manage 2 "simultaneous" incoming ringing call
        if (!this.isOxo) { return false; }

        let result: boolean = false;
        result = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_OXO_MULTICALL);
        result = result && this.agentStatus.featureOXOMULTICALL;

        //++ temporary hack sipWiseMode for oxo demo mode to force activate/desactivate for RQ6394
        const sipWiseFlagHack: string = this.settingsService.getSetting("sipWiseMode");
        result = result || (sipWiseFlagHack === "RQ6394");
        //++ temporary hack
        return result;
    }//RQ6394 - RQ7175 --

    public sipWiseMode(): string {
        let mode: string = this.settingsService.getSetting("sipWiseMode");
        if (!this.sipWise) { mode = "false"; }
        return mode;
    }//end SipWiseMode

    public async resetAllSipWiseCall(): Promise<void> {
        /*WARNING:
            That is a servicing IQ for maintenance only,
            requesting PCG to force cleaning all ghost stucked user's calls
        */
        if (this.isSipWise()) {
            // Forge request iq
            const iq = $iq({ type: "set", to: this.userJidTel + "/pcg2" })
                .c("telephony", { xmlns: "urn:xmpp:pbxagent:telephony:1" })
                .c("service")
                .c("deleteCalls", { forced: "true" });

            try {
                // Send "get SnapshotCall" iq
                const data = await this.xmppService.sendIQ(iq, 60000);
                this.logger.error("[telephonyService] resetAllSipWiseCall : try to reset all ghost calls !" + data);
            }
            catch (error) {
                const errorMessage = "resetAllSipWiseCall failure : " + error.message;
                this.logger.error("[telephonyService]" + errorMessage);
            }
        }
    }//end resetAllSipWiseCall

    /******************************************************* */
    /*                         OXO telephony                 */
    /******************************************************* */

    public async oxopbxsUpdateIndividualPassword(newPassword: string): Promise<any> {
        try {
            const url = "${config.restServerUrl}/api/rainbow/oxopbxprovisioning/v1.0/oxopbxs/" + this.contactService.userContact.systemId + "/subscribers/" + this.contactService.userContact.dbId + "/password";
            const reqData = { password: newPassword };
            const body = JSON.stringify(reqData);
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            this.logger.info("[telephonyService] oxopbxsUpdateIndividualPassword : success");
            const responseData = (await response.json()).data;
            return responseData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "oxopbxsUpdateIndividualPassword");
            this.logger.error(`[telephonyService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Callback method called on reception of ON_WEBRTCGATEWAY_CONFIG_CHANGE event
     * @param {Event} __event Native event
     * @param {boolean} isConfigured True if mediapillar is configured
     * @param {string} remoteExtension Remote extension number of the mediapillar user
     */
    public onMediaPillarConfigChanged(__event: Event, isConfigured: boolean, remoteExtension: string): void {
        this.mediaPillarConfigured = isConfigured;
        this.mediaPillarRemoteExtension = remoteExtension;
        this.logger.info("[telephonyService] onMediaPillarConfigChanged isConfigured: " + isConfigured + " remoteExtension: " + remoteExtension);
    }

    /**
     * Callback method called on reception of ON_WEBRTCGATEWAY_STATE_CHG event
     * @param {Event} __event Native event
     * @param {boolean} mediaPillarAlive true if mediapillar is alive
     */
    public onMediaPillarStateChanged(__event: Event, mediaPillarAlive: boolean): void {
        this.mediaPillarAlive = mediaPillarAlive;
        this.logger.info("[telephonyService] onMediaPillarStateChanged alive: " + mediaPillarAlive);
    }

    /*********************************************************/
    /**              TELEPHONY CAPABILITIES                 */
    /*********************************************************/

    /**
     * @param {Call} call call
     * @param {Contact} destinationContact destination contact for which some capabilities are returned (such as makecall , blindtransfer)
     * @returns {any} object capabilities
     */
    public getCapabilitiesForCall(call: CallRB, destinationContact: Contact = null): any {
        if (!call) return {};

        const capabilities = {
            makeCall: false,
            answer: false,
            drop: false,
            hold: false,
            retrieve: false,
            transfer: false,
            conference: false,
            deflect: false,
            deflectToVoicemail: false,
            blindTransfer: false,
            adhocConference: false,
            transferToOtherCall: false,
            conferenceWithOtherCall: false,
            transferOrConferenceDestinations: [],
            decline: false
        };

        const ongoingOutgoingCall = this.getOngoingOutgoingCall();
        const activeCall: CallRB = this.getActiveCall();
        const holdCalls: CallRB[] = this.getCallsOnHold();
        const selectedCall = this.activeConversation ? this.activeConversation.audioCall : null;

        switch (call.callStatus) {
            case CallStatus.RINGING_INCOMMING:
                capabilities.answer = true;
                capabilities.drop = capabilities.decline = this.isSipWise(); // no decline for delivered incoming call on hybrid system
                capabilities.deflectToVoicemail = Boolean(this.voicemailNumber) && !this.isSipWise();
                capabilities.makeCall = this.isSecondCallAllowed && !ongoingOutgoingCall;
                capabilities.deflect = true;
                break;

            case CallStatus.QUEUED_INCOMMING:
                capabilities.answer = this.isSecondCallAllowed && (this.oxoMultiCallMode() || Boolean(activeCall) || holdCalls.length > 0); // on hybrid system, not possible to answer queued call if no active (or hold) call
                capabilities.drop = capabilities.decline = false; // no decline for queued incoming call on hybrid system
                capabilities.deflectToVoicemail = Boolean(this.voicemailNumber) && !this.isSipWise();
                capabilities.makeCall = this.isSecondCallAllowed && !ongoingOutgoingCall;
                capabilities.deflect = true;
                break;

            case CallStatus.DIALING:
            case CallStatus.QUEUED_OUTGOING:
                capabilities.drop = true;
                break;

            case CallStatus.RINGING_OUTGOING:
                capabilities.drop = true;
                capabilities.transfer = this.isTransferAllowed && Boolean(selectedCall) && (selectedCall.callStatus === CallStatus.HOLD) && !this.isSipWise();
                capabilities.transferToOtherCall = this.isTransferAllowed && Boolean(holdCalls.length) && !this.isSipWise();
                capabilities.transferOrConferenceDestinations = capabilities.transferToOtherCall ? holdCalls.map((holdCall: CallRB) => holdCall.id) : [];
                break;

            case CallStatus.ACTIVE:
                capabilities.drop = true;
                capabilities.hold = !call.isConference && !call.getIsVm() && !call.isSfuPbxCall();
                capabilities.makeCall = this.isSecondCallAllowed && !call.isConference && (!destinationContact || call.contact.jid !== destinationContact.jid);
                capabilities.blindTransfer = this.isTransferAllowed && !call.isConference && !call.getIsVm()
                    && (this.contactService.userContact.attendantConsoleEnabled || holdCalls.length === 0)
                    && (!destinationContact || call.contact.jid !== destinationContact.jid);
                capabilities.adhocConference = this.isSipWise() && this.isAdhocConferenceAllowed && !(this.contactService.userContact?.attendantConsoleEnabled) && !call.getIsVm();
                capabilities.transfer = this.isTransferAllowed && Boolean(selectedCall) && (selectedCall.callStatus === CallStatus.HOLD) && !call.getIsVm();
                capabilities.transferToOtherCall = this.isTransferAllowed && Boolean(holdCalls.length) && !call.getIsVm();
                capabilities.conferenceWithOtherCall = this.isConferenceAllowed && (holdCalls.length === 1) && (!this.isSipWise() || this.isMixPstnWebRtcAllowed) && !call.getIsVm();
                capabilities.transferOrConferenceDestinations = (capabilities.transferToOtherCall || capabilities.conferenceWithOtherCall) ? holdCalls.map((holdCall: CallRB) => holdCall.id) : [];
                break;

            case CallStatus.HOLD:
                // We have put on hold the other party
                capabilities.retrieve = this.isSecondCallAllowed && !ongoingOutgoingCall;
                capabilities.makeCall = this.isSecondCallAllowed && !ongoingOutgoingCall;
                capabilities.transfer = this.isTransferAllowed && Boolean(selectedCall)
                    && (selectedCall.callStatus === CallStatus.ACTIVE
                        || (selectedCall.callStatus === CallStatus.RINGING_OUTGOING && !this.isSipWise()));
                capabilities.conference = this.isConferenceAllowed && Boolean(selectedCall)
                    && (selectedCall.callStatus === CallStatus.ACTIVE) && (!this.isSipWise() || this.isMixPstnWebRtcAllowed);
                capabilities.transferToOtherCall = this.isTransferAllowed && ((Boolean(activeCall) && !activeCall.getIsVm()) || (Boolean(ongoingOutgoingCall) && !ongoingOutgoingCall.getIsVm() && !this.isSipWise()));
                capabilities.conferenceWithOtherCall = this.isConferenceAllowed && Boolean(activeCall) && !activeCall.getIsVm() && (holdCalls.length === 1) && (!this.isSipWise() || this.isMixPstnWebRtcAllowed);
                capabilities.transferOrConferenceDestinations = (capabilities.transferToOtherCall || capabilities.conferenceWithOtherCall)
                    ? [(activeCall || ongoingOutgoingCall).id]
                    : [];
                break;

            case CallStatus.PUT_ON_HOLD:
                // This call is put on hold by the other party
                capabilities.drop = true;
                capabilities.makeCall = this.isSecondCallAllowed && !ongoingOutgoingCall;
                break;

            case CallStatus.ERROR:
                // failed outgoing call
                capabilities.drop = true;
                break;

            default:
                break;
        }

        //this.logger.debug("[telephonyService] TelCapabilities getCapabilitiesForCall call: " + call + " => " + JSON.stringify(capabilities));
        return capabilities;
    }

    /**
     *
     * @param {Contact} contact contact for which capabilities are returned
     * @returns {any} object contiaining capabilities of given contact
     */
    public getCapabilitiesForContact(contact: Contact): any {
        if (!contact) return undefined;

        if (this.contactService.userContact && contact.jid === this.contactService.userContact.jid) {
            return this.getCapabilitiesForMyContact();
        }

        const capabilities = {
            makeCall: false,
            answer: false,
            drop: false,
            hold: false,
            retrieve: false,
            transfer: false,
            conference: false,
            deflect: false,
            blindTransfer: false,
            adhocConference: false,
        };

        const callsForContact = this.getCallsForContact(contact);

        if (callsForContact.length === 1) {
            // we are in call with this contact
            // return the capabilities for this call
            return this.getCapabilitiesForCall(callsForContact[0], contact);

        } else if (callsForContact.length === 0) {
            // we are not in call with contact
            const phoneNumberAvailable: boolean = Boolean(contact.phonePro || (contact.phonePbx && contact.pbxId) || contact.mobilePro || contact.phonePerso || contact.mobilePerso);
            // The capabilities are not depending on this contact, but are depending on my global calls situation
            return phoneNumberAvailable ? this.getCapabilitiesForMyContact() : capabilities;
        }

        //this.logger.debug("[telephonyService] TelCapabilities getCapabilitiesForContact contact: " + contact + " => " + JSON.stringify(capabilities));
        return capabilities;
    }

    /**
     *
     * @returns {any} object containing my capabilities
     */
    public getCapabilitiesForMyContact(): any {
        const capabilities = {
            makeCall: false,
            deflect: false,
            blindTransfer: false,
        };

        if (this.contactService.userContact.isRainbowHubUser()) {
            capabilities.makeCall = !this.getOngoingOutgoingCall();

        } else if (this.contactService.userContact.isRainbowHybridUser()) {
            capabilities.makeCall = ((this.isBasicCallAllowed && this.getNotIdleCalls().length === 0)
                || /* free user */ (this.isSecondCallAllowed
                 && this.getCalls([CallStatus.ACTIVE, CallStatus.HOLD]).length >= 1
                 && !this.getOngoingOutgoingCall()
                 && !this.getActiveCall()?.isConference));
        }

        const selectedCall: CallRB = this.activeConversation ? this.activeConversation.audioCall : null;
        const selectedCallStatus = selectedCall ? selectedCall.callStatus : null;

        switch (selectedCallStatus) {
            case CallStatus.RINGING_INCOMMING:
            case CallStatus.QUEUED_INCOMMING:
                // deflect is not allowed for a group incoming call (a group call is identified by the "distributed" value in its initCause)
                capabilities.deflect = !selectedCall.sipWiseData || selectedCall.sipWiseData.initCause !== "distributed";
                break;

            case CallStatus.ACTIVE:
                capabilities.blindTransfer = !selectedCall.isConference;
                break;

            default:
                break;
        }

        //this.logger.debug("[telephonyService] TelCapabilities getCapabilitiesForMyContact => " + JSON.stringify(capabilities));
        return capabilities;
    }

    public computeTelCapabilities(): void {
        // evaluate and notify tel capabilities for contacts of all calls
        this.computeAndNotifyTelCapabilitiesForCallsContacts();

        // Check if we need to notify change of capabilities for our userContact
        // This is needed for capabilities such as makeCall, deflect, blindTransfer which don't depend on any contact but on our global active conversation/calls state
        this.computeAndNotifyTelCapabilitiesForMyContact();
    }

    private computeAndNotifyTelCapabilitiesForCallsContacts() {
        Object.keys(this.calls || {}).forEach((key) => {
            const call: CallRB = this.calls[key];
            this.computeAndNotifyTelCapabilitiesForCallContact(call);
        });
    }

    private computeAndNotifyTelCapabilitiesForCallContact(call: CallRB): void {
        // save new capabilities in contact and notify
        if (call?.contact) {
            // compute the tel capabilities and notify if necessary the contact of this call
            const newCapabilities = this.getCapabilitiesForCall(call);

            const telCapabilitiesContactChange = !this.telCapabilitiesEquals(newCapabilities, call.contact.supervisionCallCapabilities);
            if (telCapabilitiesContactChange) {
                // save new capabilities in contact and notify change
                //this.logger.debug("[telephonyService] TelCapabilities change notified for contact: " + call.contact.jid + " => " + JSON.stringify(newCapabilities));
                call.contact.supervisionCallCapabilities = newCapabilities;
            } else {
                //this.logger.debug("[telephonyService] TelCapabilities nothing to notify for jid: " + call.contact.jid);
            }

            // Reevaluate supervision capabilities of the call contact: the pickup capability for the call contact may change based on the call status
            this.computeAndNotifySupervisionCapabilities(call.contact, false);
        }
    }

    private computeAndNotifyTelCapabilitiesForMyContact(): void {
        const myNewCapabilities = this.getCapabilitiesForMyContact();
        const myCapabilitiesChange = !this.telCapabilitiesEquals(myNewCapabilities, this.contactService.userContact.supervisionCallCapabilities);
        if (myCapabilitiesChange) {
            // save new capabilities and notify change
            //this.logger.debug("[telephonyService] TelCapabilities Notify for my jid => " + JSON.stringify(myNewCapabilities));
            this.contactService.userContact.supervisionCallCapabilities = myNewCapabilities;
        } else {
            //this.logger.debug("[telephonyService] TelCapabilities nothing to notify for my jid");
        }
    }

    private telCapabilitiesEquals(capabilities1: any, capabilities2: any): boolean {
        if (capabilities1 === null || capabilities1 === undefined || capabilities2 === null || capabilities2 === undefined) { return capabilities1 === capabilities2; }

        // object properties equality check
        const capa1keys = Object.keys(capabilities1);
        return (Object.keys(capabilities2).every((capability2) => { return capa1keys.includes(capability2); }))
            && (capa1keys.every((capability) => {
                if (capability === "transferOrConferenceDestinations") {
                    const isEqual: boolean = (capabilities1[capability].length === capabilities2[capability].length)
                        && capabilities1[capability].every((callId1: string) => { return capabilities2[capability].includes(callId1); });
                    // this.logger.debug(`[telephonyService] telCapabilitiesEquals transferOrConferenceDestinations length1: ${capabilities1[capability].length} length2: ${capabilities2[capability].length} => isEqual: ${isEqual}`);
                    return isEqual;
                }
                return capabilities1[capability] === capabilities2[capability];
            }));
    }

    private onActiveConversationChange(__event: Event, activeConversation: any): void {
        if (this.contactService.userContact && (this.contactService.userContact.attendantConsoleEnabled || this.contactService.userContact.isSupervisor() || this.supervisionService.isMyselfManagerOrAssistant())) {
            this.logger.info("[telephonyService] TelCapabilities onActiveConversationChange conv: " + (activeConversation ? activeConversation.id : "null"));

            this.activeConversation = activeConversation;
            this.computeTelCapabilities();
        }
    }

    private onCallUpdated(__event: Event, call: CallRB): void {
        //RQRAINB-9057 now capabilities no more reserved to attendant/supervision
        if (this.contactService.userContact && call?.callType === CallType.PHONE) {
            //this.logger.debug("[telephonyService] TelCapabilities onCallUpdated call: " + call);
            this.computeTelCapabilities();
        }
    }

    /***************************************************************/
    /*                SUPERVISION CAPABILITIES                     */
    /***************************************************************/

    public computeAndNotifySupervisionCapabilities(contact: Contact, createSupervisionCapabilities: boolean = true): void {
        // Compute supervision capabilities
        if (!contact.supervisionCapabilities && !createSupervisionCapabilities) {
            //this.logger.debug("[telephonyService] SupervisionCapabilities don't create supervision capabilities for conatct id: " + contact.id);
            return;
        }
        const newSupervisionCapabilities = this.getSupervisionCapabilitiesForContact(contact);
        const supervisionCapabilitiesChange = !this.telCapabilitiesEquals(newSupervisionCapabilities, contact.supervisionCapabilities);
        if (supervisionCapabilitiesChange) {
            // save new supervision capabilities and notify change
            this.logger.info("[telephonyService] SupervisionCapabilities Notify for jid: " + contact.jid + " => " + JSON.stringify(newSupervisionCapabilities));
            contact.supervisionCapabilities = newSupervisionCapabilities;
        } else {
            //this.logger.debug("[telephonyService] SupervisionCapabilities nothing to notify for jid: " + contact.jid);
        }
    }

    public getSupervisionCapabilitiesForContact(contact: Contact): any {
        // Pickup allowed only for supervisor and supervisee on same pbx
        // This prevents pickup :
        // -between 2 hybrid systems (this is temporary, waiting for server inter-pcg mechanism implementation for homogeneous hybrid network)
        // -between hybrid and hub systems

        const supervisionCapabilities = { pickup: false };

        if ((contact.supervisionState === "ringingIn" || contact.supervisionState === "waiting")
            && contact.pbxId === this.contactService.userContact.pbxId) {
            // Do not propose pickup if the supervised contact is ringing for an outgoing call coming from me
            // Do not propose pickup if I'm engaged in an active conference
            supervisionCapabilities.pickup = !this.getOngoingOutgoingCallForContact(contact)
                && !this.getActiveCall()?.isConference
                && !this.isImmediateForwardActivated()
                && !this.existHgOrCcqOrSfuIncomingCall(contact);
        }

        return supervisionCapabilities;
    }

    private existHgOrCcqOrSfuIncomingCall(contact: Contact): boolean {
        return (contact?.supervisionCallInfos || []).some((callInfo: CallInfo) => {
            return callInfo.callState === "ringingIn" && (callInfo.callType === "hg" || callInfo.callType === "ccq" || callInfo.callType === "sfu");
        });
    }

    //we can release the phone call related to the bubble in case of PBX call in // with the bubble
    private onWebConferenceReleased(roomDbId: string) {
        const call = this.getActiveCall();

        //if web conf exist and the call is related the same web conf, then do not put on hold
        if (call?.webConferenceRoom && roomDbId && call.webConferenceRoom.dbId === roomDbId) {
            this.logger.info(`[telephonyService] onWebConferenceReleased for conf ${roomDbId} -- release related call ${call.id}`);
            this.releaseCall(call);
        }
    }

    /***************************************************************************************/
    // Method  that puts the active telephony call on hold only if the we're in MP context
    // it's also used when we Retrieve or answer WEBRTC P2P call
    // related to the MIX PSTN & WEBRTC call management
    //if the put on hold comes from a web conference session, then we send the conf session as parameter to check if the current call is not related to the bubble
    public async putActiveTelephonyCallOnHold(conferenceSession?): Promise<void> {
        //ignore if no feature flag or user is not in MP
        if (!this.isMixPstnWebRtcAllowed) { return; }

        if (!(this.isMediaPillarAvailable() && this.isMediaPillarUserSelected())) { return; }

        try {
            const call = this.getActiveCall();

            //if web conf exist and the call is related the same web conf, then do not put on hold
            if (call?.webConferenceRoom && conferenceSession && call?.webConferenceRoom?.id === conferenceSession?.id) {
                this.logger.info("[telephonyService] putActiveTelephonyCallOnHold ignored " + call);
                return;
            }

            if (call) {
                this.logger.info("[telephonyService] putActiveTelephonyCallOnHold " + call);
                await this.holdCall(call);
            }
        }
        catch (error) {
            this.logger.info("[telephonyService] putActiveTelephonyCallOnHold error -- " + error);
        }

    }

    public waitCallState(call: CallRB, expectedStatus: any[], timeout: number = 10000, retryPeriod: number = 300): Promise<void> {
        const start = Date.now();
        this.logger.debug("[telephonyService] waitCallState start -- " + start);
        return new Promise(waitForCallState.bind(this));

        // waitForCallState makes the decision whether the call state has the expected value
        // or not or the timeout has been exceeded which means
        // this promise will be rejected

        // eslint-disable-next-line jsdoc/require-jsdoc
        function waitForCallState(resolve, reject) {
            if (call && expectedStatus && expectedStatus.includes(call.callStatus)) {
                this.logger.debug("[telephonyService] waitCallState call " + call + " is now in expected state: " + expectedStatus.map((status) => { return status; }).join(','));
                resolve();
            } else if (timeout && (Date.now() - start) >= timeout) {
                this.logger.debug("[telephonyService] waitCallState timeout reached " + timeout);
                reject(new Error("timeout"));
            } else {
                this.logger.debug("[telephonyService] waitCallState call " + call + " not yet in expected state: " + expectedStatus.map((status) => { return status; }).join(','));
                setTimeout(waitForCallState.bind(this, resolve, reject), retryPeriod);
            }
        }
    }

    private setAutoOffHookConference(number: string, room: BubbleRB) {
        this.logger.info("[telephonyService] setAutoOffHookConference number " + number + " room " + (room ? room.name : "null"));
        this.autoOffHookConference = { number, room };

        // Check if the incoming call coming from the conference is already here
        // If it is already present in webclient, we must autoanswer it
        const existCall: CallRB = this.getCalls([CallStatus.RINGING_INCOMMING, CallStatus.QUEUED_INCOMMING]).find((call: CallRB) => {
            return !call.isSecondary && call.callerNumber
            && (call.callerNumber.indexOf(this.autoOffHookConference.number) !== -1 || this.autoOffHookConference.number.indexOf(call.callerNumber) !== -1);
        });

        if (existCall) {
            // Proceed to the auto answer of this incoming call coming from webconference
            this.logger.info(`[telephonyService] setAutoOffHookConference call id '${existCall.id}' already exists with autoOffHookConference number '${this.autoOffHookConference.number}' caller '${existCall.callerNumber}' => auto answerCall`);
            existCall.webConferenceRoom = this.autoOffHookConference.room;
            this.answerCall(existCall);
            this.autoOffHookConference = null;
        } else {
            this.logger.info(`[telephonyService] setAutoOffHookConference no call exists with callernumber '${this.autoOffHookConference.number}' => wait this call during 10s`);
            setTimeout(() => {
                if (this.autoOffHookConference)
                    this.logger.warn("[telephonyService] setAutoOffHookConference: cancel phone call waiting from conference after join by phone in room Jid: " + room.jid + " name: " + room.name);
                // remove pending wait of incoming phone conference call
                this.autoOffHookConference = null;
            }, 10000);
        }
    }

    private setWebConferenceInvitDeskphone(roomJid: string, roomName: string) {
        this.logger.info("[telephonyService] setWebConferenceInvitDeskphone room Jid: " + roomJid + " name: " + roomName);
        this.webConferenceInvitDeskphoneRoom = { jid: roomJid, name: roomName };

        setTimeout(() => {
            if (this.webConferenceInvitDeskphoneRoom)
                this.logger.warn("[telephonyService] setWebConferenceInvitDeskphone: cancel phone call waiting from conference room Jid: " + roomJid + " name: " + roomName);
            // remove pending wait of incoming phone conference call
            this.webConferenceInvitDeskphoneRoom = null;
        }, 10000);
    }

    /************************* Voicemail language *************************/
    /**
     * get list of supported voicemail audio languages: de, en, fr, nl, es, it, ro, ar, he...
     * @public
     * @function
     * @async
     * @returns {Promise<any[]>} Status promise resolved with the list of supported languages for voicemail
     */
    public async getVoicemailSupportedLanguages(): Promise<any[]> {
        if (!this.isSipWiseRoot()) {
            this.logger.info("[telephonyService] getVoicemailSupportedLanguages Anomaly not a sipWise situation");
            throw new Error("Anomaly not a sipWise situation");
        }
        return this.telephonyServiceSipWise.getVoicemailSupportedLanguages();
    }

    /**
     * get current voicemail language of logged user, among same_as_company, de, en, fr, nl, es, it, ro, ar, he...
     * @public
     * @function
     * @async
     * @returns {Promise<string>} Status promise resolved with the current voicemail language
     */
    public async getVoicemailLanguage(): Promise<string> {
        if (!this.isSipWiseRoot()) {
            this.logger.info("[telephonyService] getVoicemailLanguage Anomaly not a sipWise situation");
            throw new Error("Anomaly not a sipWise situation");
        }
        return this.telephonyServiceSipWise.getVoicemailLanguage();
    }

    /**
     * modify logged user's voicemail language
     * New language possible values: same_as_company, de, en, fr, nl, es, it, ro, ar, he...
     * @public
     * @function
     * @param {string} voicemailLanguage new voicemail language to set for the logged user
     * @async
     * @returns {Promise<string>} Status promise resolved with the new voicemail language
     */
    public async updateVoicemailLanguage(voicemailLanguage: string): Promise<string> {
        if (!this.isSipWiseRoot()) {
            this.logger.info("[telephonyService] getVoicemailLanguage Anomaly not a sipWise situation");
            throw new Error("Anomaly not a sipWise situation");
        }
        return this.telephonyServiceSipWise.updateVoicemailLanguage(voicemailLanguage);
    }

    public isRemoteControlledPhoneCallId(phoneCallId: string): boolean {
        if (!phoneCallId) return false;
        const remoteControlledCallIdsArray: string[] = Array.from(this.remoteControlledPhoneCallIds.values());
        return remoteControlledCallIdsArray.some((callId) => { return callId.includes(phoneCallId); });
    }

    public removeRemoteControlledPhoneCallId(phoneCallId: string): boolean {
        if (!phoneCallId) return false;
        const remoteControlledCallIdsArray: string[] = Array.from(this.remoteControlledPhoneCallIds.values());
        const foundCallId: string = remoteControlledCallIdsArray.find((callId) => { return callId.includes(phoneCallId); });
        if (foundCallId) {
            this.remoteControlledPhoneCallIds.delete(foundCallId);
        }
        return Boolean(foundCallId);
    }

    //overwritten by customManifestService
    public getCorrelatorDataFromManifest(phoneNumber: string) {
        return {
            correlatorData: "",
            correlatorDataHex: ""
        };
    }

    public setAutoAnswerCorrelatorIdResourceMap(correlatorId: string, resource?: string) {
        // for Oxe (used for supervision pickup): the correlatorId for autoanswered pickup is an endpoint jid or an endpoint phoneNumber
        // for Oxo (used for supervision pickup): the correlatorId for autoanswered pickup is a real CSTA call id
        // for Hub (used for hunt group pickup): the correlatorId for group autoanswered pickup is the endpoint phoneNumber

        this.logger.debug(`[telephonyService] setAutoAnswerCorrelatorIdResourceMap correlatorId ${correlatorId} resource ${resource}`);
        this.autoAnswerCorrelatorIdResourceMap[correlatorId] = resource ? resource : this.xmppService.getResourceFromJid(this.contactService.userContact.fullJid);

        // auto clear of the entry if not used in the next 10 seconds
        this.autoAnswerTimeout[correlatorId] = setTimeout(() => {
            this.logger.debug(`[telephonyService] setAutoAnswerCorrelatorIdResourceMap auto clear after 10s timeout correlatorId ${correlatorId}`);
            this.removeAutoAnswerCorrelatorId(correlatorId);
        }, 10000);
    }

    public getAutoAnswerCorrelatorIdResourceMap(correlatorId: string) {
        this.logger.debug(`[telephonyService] getAutoAnswerCorrelatorIdResourceMap correlatorId ${correlatorId} => ${this.autoAnswerCorrelatorIdResourceMap[correlatorId]}`);
        return this.autoAnswerCorrelatorIdResourceMap[correlatorId];
    }

    public removeAutoAnswerCorrelatorId(correlatorId: string) {
        this.logger.debug(`[telephonyService] removeAutoAnswerCorrelatorId correlatorId ${correlatorId} current ${this.autoAnswerCorrelatorIdResourceMap[correlatorId]}`);
        delete this.autoAnswerCorrelatorIdResourceMap[correlatorId];

        // stop and delete running timeout
        if (this.autoAnswerTimeout[correlatorId]) {
            clearTimeout(this.autoAnswerTimeout[correlatorId]);
            delete this.autoAnswerTimeout[correlatorId];
        }
    }
}
